import { useState, createContext, useContext, useMemo } from "react";
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { LocationPicker } from "@/components/LocationPicker";
import { Country } from "country-state-city";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useRoute, Link } from "wouter";
import { 
  useGetMe,
  useGetProject, useUpdateProject, getGetProjectQueryKey, getProjectSummaryQueryKey, useListCompanies, useProjectSummary,
  useListClientInvoices, useListClientReceipts, 
  useCreateClientInvoice, useUpdateClientInvoice, useDeleteClientInvoice, getListClientInvoicesQueryKey,
  useCreateClientReceipt, useUpdateClientReceipt, useDeleteClientReceipt, getListClientReceiptsQueryKey,
  useListScInvoices, useListScPayments,
  useCreateScInvoice, useUpdateScInvoice, useDeleteScInvoice, getListScInvoicesQueryKey,
  useCreateScPayment, useUpdateScPayment, useDeleteScPayment, getListScPaymentsQueryKey,
  useListSupplierInvoices, useListSupplierPayments,
  useCreateSupplierInvoice, useUpdateSupplierInvoice, useDeleteSupplierInvoice, getListSupplierInvoicesQueryKey,
  useCreateSupplierPayment, useUpdateSupplierPayment, useDeleteSupplierPayment, getListSupplierPaymentsQueryKey,
  useListCashFlow, useCreateCashFlowEntry, useUpdateCashFlowEntry, getListCashFlowQueryKey, useDeleteCashFlowEntry,
  useListPayroll, useCreatePayrollEntry, useUpdatePayrollEntry, useDeletePayrollEntry, getListPayrollQueryKey,
  useListBudget, useCreateBudgetLine, useUpdateBudgetLine, useDeleteBudgetLine, getListBudgetQueryKey,
  useListBoq, useCreateBoqItem, useUpdateBoqItem, useDeleteBoqItem, getListBoqQueryKey,
  useListPromissoryNotes, useCreatePromissoryNote, useUpdatePromissoryNote, useDeletePromissoryNote, getListPromissoryNotesQueryKey,
  useListSubcontractors, useCreateSubcontractor, useListSuppliers, useCreateSupplier, useListMasterStaff, useListCustomers,
  useListClientDocuments, useCreateClientDocument, useUpdateClientDocument, useDeleteClientDocument, getListClientDocumentsQueryKey, useConvertClientDocumentToInvoice,
  useListVendorOrders, useCreateVendorOrder, useUpdateVendorOrder, useDeleteVendorOrder, getListVendorOrdersQueryKey,
  useListExpenseCategories,
  useListTaxTypes,
  useListPayrollTaxConfigs,
  useListProjectMembers, useAddProjectMember, useUpdateProjectMemberRole, useRemoveProjectMember, getListProjectMembersQueryKey,
  useListMembers,
  type Project, type PayrollEntry, type BoqItem, type PromissoryNote, type CashFlowEntry,
  type ClientInvoice, type ClientReceipt, type ScInvoice, type ScPayment, type SupplierInvoice, type SupplierPayment, type BudgetLine,
  type Subcontractor, type Supplier, type Customer, type ClientDocument, type VendorOrder,
  type CreateVendorOrderBodyDocType, type CreateVendorOrderBodyStatus,
  type CreateClientDocumentBodyDocType, type CreateClientDocumentBodyStatus,
  type MasterStaffMember, type TaxTypeWithRates, type PayrollTaxConfig,
  type ProjectMember, type MemberItem,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { 
  LayoutDashboard, Wallet, Users, Briefcase, DollarSign, 
  Settings, ArrowLeft, Plus, Trash2, FileText, Pickaxe, Package,
  BookOpen, ListChecks, Landmark, Shield, Pencil, Save, BarChart2, Printer, Lock, Check, ChevronsUpDown, Menu, X, ChevronLeft, ChevronRight,
  Target, Activity, TrendingDown, HardHat, PiggyBank, AlertTriangle, CheckCircle
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { KpiCard } from "@/components/ui/kpi-card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { fc as fcBase, fp, stBadge, calcCI, calcSCI, calcSI, N, uid, separateAdvances, calcAdvanceRecovery, calcClientAdvanceRecovery, sumCnItems, sumRetItems, type Numeric, type CnItem, type RetItem } from "@/lib/finance";
import { hasFeature, getPlan, type PlanFeatureKey } from "@/lib/plans";
import { apiGet } from "@/lib/api";
import { RefNumberInput } from "@/components/RefNumberInput";
import { AttachmentPanel } from "@/components/AttachmentPanel";
import { LineItemsEditor, lineItemsNetTotal, lineItemsTaxTotal, type LineItem } from "@/components/LineItemsEditor";
import { DemoBanner } from "@/components/DemoBanner";
import { useDemoProfile } from "@/context/DemoProfileContext";
import { useEffect } from "react";
import { DocPrintView, type PrintDocType, type PrintCompany } from "@/components/print/DocPrintView";

const CurContext = createContext('SAR');
const CompanyTaxContext = createContext<{ taxRate: number; taxLabel: string; taxTypes: TaxTypeWithRates[] }>({ taxRate: 0, taxLabel: 'VAT', taxTypes: [] });
const CompanyPlanContext = createContext<string>("pro");
const CompanyContext = createContext<PrintCompany | null>(null);

function getDefaultTaxPct(taxTypes: TaxTypeWithRates[], fallbackRate: number): string {
  for (const t of taxTypes) {
    const def = t.rates.find(r => r.isDefault);
    if (def) return String((N(def.rate) * 100).toFixed(4));
  }
  if (taxTypes.length > 0 && taxTypes[0].rates.length > 0) {
    return String((N(taxTypes[0].rates[0].rate) * 100).toFixed(4));
  }
  return String(Math.round(fallbackRate * 100));
}

function TaxRatePicker({ value, onChange, className }: { value: string; onChange: (pct: string) => void; className?: string }) {
  const { taxTypes, taxRate: defaultRate, taxLabel } = useContext(CompanyTaxContext);
  if (!taxTypes.length) {
    return (
      <Input
        type="number" min="0" max="100" step="0.01"
        className={className ?? "rounded-xl"}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="0"
      />
    );
  }
  const allRates = taxTypes.flatMap(t => t.rates.map(r => ({ typeId: t.id, typeName: t.name, ...r })));
  const selectedRate = allRates.find(r => Math.abs(N(r.rate) * 100 - N(value)) < 0.001);
  return (
    <Select
      value={selectedRate ? selectedRate.id : "__custom__"}
      onValueChange={v => {
        if (v === "__custom__") { onChange(getDefaultTaxPct(taxTypes, defaultRate)); return; }
        const rate = allRates.find(r => r.id === v);
        if (rate) onChange(String((N(rate.rate) * 100).toFixed(4)));
      }}
    >
      <SelectTrigger className={className ?? "rounded-xl"}>
        <SelectValue placeholder={`Select ${taxLabel} rate…`} />
      </SelectTrigger>
      <SelectContent>
        {taxTypes.map(t => (
          t.rates.length > 0 ? (
            <div key={t.id}>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border/30">{t.name}</div>
              {t.rates.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.label} — {(N(r.rate) * 100).toFixed(2).replace(/\.00$/, '')}%{r.isDefault ? " ★" : ""}
                </SelectItem>
              ))}
            </div>
          ) : null
        ))}
        <SelectItem value="__custom__">— Enter manually —</SelectItem>
      </SelectContent>
    </Select>
  );
}

function MultiPicker({
  options, selected, onChange, placeholder = "Select…",
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const displayText = selected.length === 0
    ? placeholder
    : selected.length <= 2
      ? selected.map(id => options.find(o => o.id === id)?.label ?? id).join(", ")
      : `${selected.length} selected`;
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between rounded-xl font-normal text-sm h-9 px-3">
          <span className="truncate text-left max-w-[calc(100%-20px)]">{displayText}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList className="max-h-52">
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {options.map(opt => (
                <CommandItem key={opt.id} value={opt.label} onSelect={() => toggle(opt.id)}>
                  <Checkbox checked={selected.includes(opt.id)} className="mr-2 h-3.5 w-3.5" onCheckedChange={() => toggle(opt.id)} />
                  <span className={cn("text-sm", selected.includes(opt.id) && "font-medium")}>{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const TABS: { id: string; label: string; icon: React.ElementType; feature?: PlanFeatureKey }[] = [
  { id: 'dashboard',  label: 'Dashboard',       icon: LayoutDashboard },
  { id: 'client',     label: 'Client / Revenue', icon: Wallet    },
  { id: 'sc',         label: 'Subcontractors',   icon: Pickaxe   },
  { id: 'suppliers',  label: 'Suppliers',        icon: Briefcase },
  { id: 'payroll',    label: 'Payroll',          icon: Users,     feature: 'payroll'        },
  { id: 'expenses',   label: 'Expenses',         icon: Package,   feature: 'expenses'       },
  { id: 'budget',     label: 'Budget',           icon: BookOpen,  feature: 'budget'         },
  { id: 'boq',        label: 'BOQ',              icon: ListChecks,feature: 'boq'            },
  { id: 'retention',  label: 'Retention',        icon: Shield,    feature: 'retention'      },
  { id: 'promissory', label: 'Promissory Notes', icon: Landmark,  feature: 'promissory'     },
  { id: 'reports',    label: 'Reports',          icon: BarChart2, feature: 'reports'        },
  { id: 'settings',   label: 'Settings',         icon: Settings },
];

function NavItems({
  onSelect,
  collapsed,
  companyPlan,
  activeTab,
  setActiveTab,
}: {
  onSelect?: () => void;
  collapsed?: boolean;
  companyPlan: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  return (
    <>
      {TABS.map(t => {
        const locked = t.feature ? !hasFeature(companyPlan, t.feature) : false;
        const active = activeTab === t.id;
        return (
          <button
            key={t.id}
            title={collapsed ? t.label : undefined}
            onClick={() => { setActiveTab(t.id); onSelect?.(); }}
            className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200 ${
              collapsed ? 'px-0 py-3 justify-center' : 'px-4 py-3'
            } ${
              active
                ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                : locked
                ? 'text-muted-foreground/40 cursor-pointer hover:bg-muted/50'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <t.icon className="w-4 h-4 shrink-0" />
            {!collapsed && <span className="flex-1 text-left">{t.label}</span>}
            {!collapsed && locked && <Lock className="w-3 h-3 shrink-0 opacity-50" />}
          </button>
        );
      })}
    </>
  );
}

function LockedTab({ tabLabel, planRequired }: { tabLabel: string; planRequired: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-12">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-5">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold mb-2">{tabLabel} Locked</h2>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          This module is not available on your current plan. Upgrade to <strong>{planRequired}</strong> or higher to access {tabLabel}.
        </p>
        <div className="bg-muted rounded-xl p-4 text-xs text-muted-foreground">
          Contact your administrator or visit <strong>Settings → Subscription</strong> to upgrade your plan.
        </div>
      </div>
    </div>
  );
}

export default function ProjectApp() {
  const [match1, params1] = useRoute("/project/:id");
  const [match2, params2] = useRoute("/app/:slug/project/:id");
  const [match, params] = match2 ? [match2, params2] : [match1, params1];
  const projectId = params?.id || "";
  const companySlug = (params as { slug?: string })?.slug || "";
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { data: _summary, isLoading } = useProjectSummary(projectId);
  const project = _summary?.project as Project | undefined;
  const { data: meUser } = useGetMe();
  const { profile: demoProfile, clearProfile: clearDemoProfile, getEffectiveFeatures } = useDemoProfile();
  const isDemo = !!(meUser?.company?.isDemo);
  const { data: companies = [] } = useListCompanies();
  const company = companies.find(c => c.id === project?.companyId);
  const cur = company?.currency ?? 'SAR';

  /* Reset localStorage plan override when switching to a different company */
  useEffect(() => {
    if (demoProfile?.companyId && project?.companyId && demoProfile.companyId !== project.companyId) {
      clearDemoProfile();
    }
  }, [demoProfile?.companyId, project?.companyId]);

  const companyPlan = (isDemo && demoProfile?.planId) ? demoProfile.planId : (company?.plan ?? "pro");
  const effectiveFeatures = getEffectiveFeatures(companyPlan);
  const { data: companyTaxTypes = [] } = useListTaxTypes({ companyId: project?.companyId ?? "" });

  const canAccess = (feature?: PlanFeatureKey) => !feature || (effectiveFeatures[feature] ?? false);

  if (isLoading) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-background"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (!project) return <div className="p-10">Project not found</div>;

  const renderTab = () => {
    const tab = TABS.find(t => t.id === activeTab);
    if (tab?.feature && !hasFeature(companyPlan, tab.feature)) {
      const requiredPlan = tab.feature === 'retention' || tab.feature === 'promissory' || tab.feature === 'reports' || tab.feature === 'cfoDash'
        ? "Lite Pro"
        : tab.feature === 'vat' || tab.feature === 'multiCompany' || tab.feature === 'api'
        ? "Pro"
        : "Lite";
      return <LockedTab tabLabel={tab.label} planRequired={requiredPlan} />;
    }
    switch(activeTab) {
      case 'dashboard': return <ProjectDashboard project={project} />;
      case 'client': return <ClientRevenueTab project={project} />;
      case 'sc': return <SubcontractorsTab project={project} />;
      case 'suppliers': return <SuppliersTab project={project} />;
      case 'payroll': return <PayrollTab project={project} />;
      case 'expenses': return <ExpensesTab project={project} />;
      case 'budget': return <BudgetTab project={project} />;
      case 'boq': return <BOQTab project={project} />;
      case 'retention': return <RetentionTab project={project} />;
      case 'promissory': return <PromissoryTab project={project} />;
      case 'reports': return <ReportsTab project={project} />;
      case 'settings': return <ProjectSettingsTab project={project} />;
      default: return <ProjectDashboard project={project} />;
    }
  };

  const companyTaxRate = N(company?.taxRate ?? "0");
  const companyTaxLabel = company?.taxLabel ?? "VAT";

  return (
    <CurContext.Provider value={cur}>
    <CompanyPlanContext.Provider value={companyPlan}>
    <CompanyTaxContext.Provider value={{ taxRate: companyTaxRate, taxLabel: companyTaxLabel, taxTypes: companyTaxTypes }}>
    <CompanyContext.Provider value={company as PrintCompany ?? null}>
    <div className="flex flex-col h-screen bg-background">
      <DemoBanner
        isDemo={isDemo}
        companyId={company?.id ?? meUser?.companyId ?? ""}
        companyName={company?.name}
        companyPlan={company?.plan}
        userEmail={meUser?.email}
      />
      <div className="flex flex-1 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className="hidden md:flex bg-card border-r border-border/50 flex-col shrink-0 overflow-hidden"
        style={{ width: sidebarCollapsed ? 64 : 256, transition: 'width 200ms linear' }}
      >
        <div className={`flex items-center border-b border-border/50 shrink-0 ${sidebarCollapsed ? 'p-3 justify-center' : 'p-6 gap-3'}`}>
          <img src="/images/finproject-logo.png" alt="FinProject" className="h-9 w-auto shrink-0 rounded-lg" style={{ border: "2px solid #ffffff", padding: "2px", background: "#ffffff" }} />
          {!sidebarCollapsed && <span className="font-display font-bold text-lg whitespace-nowrap">FinProject</span>}
        </div>
        <nav className={`flex-1 py-6 space-y-1.5 overflow-y-auto no-scrollbar ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <NavItems collapsed={sidebarCollapsed} companyPlan={companyPlan} activeTab={activeTab} setActiveTab={setActiveTab} />
        </nav>
        {/* Plan badge in sidebar */}
        {!sidebarCollapsed && (
          <div className="px-4 pb-2">
            <div className="rounded-xl bg-muted px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Plan</span>
              <span className="text-xs font-semibold">{getPlan(companyPlan).label}</span>
            </div>
          </div>
        )}
        {/* Collapse toggle button */}
        <div className={`pb-4 flex ${sidebarCollapsed ? 'justify-center' : 'px-4 justify-end'}`}>
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="h-16 bg-card border-b border-border/50 flex items-center justify-between px-4 sm:px-8 shrink-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* Mobile hamburger */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors shrink-0">
                  <Menu className="w-4 h-4 text-muted-foreground" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="p-5 border-b border-border">
                  <SheetTitle className="text-left flex items-center gap-2">
                    <img src="/images/finproject-logo.png" alt="FinProject" className="h-7 w-auto rounded-md" style={{ border: "2px solid #ffffff", padding: "1px", background: "#ffffff" }} />
                    FinProject
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
                  <NavItems onSelect={() => setMobileNavOpen(false)} companyPlan={companyPlan} activeTab={activeTab} setActiveTab={setActiveTab} />
                </nav>
                <div className="px-4 pb-4 border-t border-border pt-4">
                  <div className="rounded-xl bg-muted px-3 py-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Plan</span>
                    <span className="text-xs font-semibold">{getPlan(companyPlan).label}</span>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <h1 className="text-base sm:text-xl font-display font-bold text-foreground truncate">{project.name}</h1>
            {project.code && <span className="hidden sm:inline font-mono text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{project.code}</span>}
            <Badge variant="secondary" className="hidden sm:flex bg-muted text-muted-foreground border-none px-3 py-1">
              {[(project.clientName || project.client), project.owner].filter(Boolean).join(' · ') || 'Internal'}
            </Badge>
          </div>
          <Link href={companySlug ? `/app/${companySlug}` : `/app/${company?.slug || ""}`} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 sm:gap-2 shrink-0">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to Portal</span>
          </Link>
        </header>
        
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto">
            {renderTab()}
          </div>
        </div>
      </main>
      </div>
    </div>
    </CompanyContext.Provider>
    </CompanyTaxContext.Provider>
    </CompanyPlanContext.Provider>
    </CurContext.Provider>
  );
}

// --- TAB COMPONENTS ---

function LockedCard({ title }: { title: string }) {
  return (
    <div className="bg-card rounded-2xl shadow-sm border border-border/50 relative overflow-hidden">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px] flex flex-col items-center justify-center z-10 rounded-2xl">
        <Lock className="w-6 h-6 text-muted-foreground mb-2" />
        <p className="text-sm font-semibold text-muted-foreground">{title} — Locked</p>
        <p className="text-xs text-muted-foreground mt-1">Switch to a higher plan to unlock</p>
      </div>
      <div className="p-6 opacity-20 pointer-events-none">
        <h3 className="font-display font-bold text-lg mb-4">{title}</h3>
        <div className="space-y-2 text-sm">
          {[1,2,3].map(i => <div key={i} className="flex justify-between py-1.5 border-b border-border/30"><span className="bg-muted rounded w-24 h-4" /><span className="bg-muted rounded w-16 h-4" /></div>)}
        </div>
      </div>
    </div>
  );
}

function ProjectDashboard({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const { data: invoices = [] } = useListClientInvoices({ projectId: project.id });
  const { data: receipts = [] } = useListClientReceipts({ projectId: project.id });
  const { data: cashFlow = [] } = useListCashFlow({ projectId: project.id });
  const { data: payroll = [] } = useListPayroll({ projectId: project.id });
  const { data: scPayments = [] } = useListScPayments({ projectId: project.id });
  const { data: supplierPayments = [] } = useListSupplierPayments({ projectId: project.id });
  const { data: clientDocs = [] } = useListClientDocuments({ projectId: project.id, companyId: project.companyId });
  const { data: vendorOrders = [] } = useListVendorOrders({ projectId: project.id, companyId: project.companyId });
  const { data: scInvoices = [] } = useListScInvoices({ projectId: project.id });
  const { data: supplierInvoices = [] } = useListSupplierInvoices({ projectId: project.id });

  const isReceiptOnlyMode = project.advanceMode === 'receipt_only';
  const totalInvoiced = invoices.reduce((s, i) => s + N(i.gross), 0);
  const ADVANCE_INV_TYPES = ['Progress Advance', 'Recovery Advance'];
  const advanceInvoiced = invoices.filter(i => ADVANCE_INV_TYPES.includes(i.invoiceType || '')).reduce((s, i) => s + N(i.gross), 0);
  const progressInvoiced = invoices.filter(i => i.invoiceType === 'Progress Invoice').reduce((s, i) => s + N(i.gross), 0);
  const finalInvoiced = invoices.filter(i => i.invoiceType === 'Final Invoice').reduce((s, i) => s + N(i.gross), 0);
  const retentionReleaseInvoiced = invoices.filter(i => i.invoiceType === 'Retention Release').reduce((s, i) => s + N(i.gross), 0);
  const { progressTotal: progressCollected, advanceTotal: advanceCollected, retentionReleaseTotal: retentionReleaseCollected } = separateAdvances(receipts);
  const advanceReceived = isReceiptOnlyMode ? advanceCollected : advanceInvoiced;
  const retentionHeld = invoices.reduce((s, i) => s + sumRetItems(i.retItems as RetItem[] | undefined, i.gross, i.retPct), 0);
  const totalCN = invoices.reduce((s, i) => s + sumCnItems(i.cnItems as CnItem[], i.cnAmt), 0);
  const retentionReceivable = retentionHeld - retentionReleaseInvoiced;
  const earnedRevenue = totalInvoiced - retentionHeld - totalCN;
  const arBal = invoices.reduce((s, i) => s + Math.max(0, calcCI(i, receipts, N(i.vatRate ?? 0)).bal), 0);
  const advanceRecovered = calcClientAdvanceRecovery(advanceReceived, project.ctrVal, progressInvoiced + finalInvoiced);
  const advanceOutstanding = Math.max(0, advanceReceived - advanceRecovered);

  const unified = buildUnifiedCashFlow(receipts, scPayments, supplierPayments, payroll, cashFlow);
  const cfIn = unified.filter(r => r.type === 'Inflow').reduce((s, r) => s + r.amount, 0);
  const cfOut = unified.filter(r => r.type === 'Outflow').reduce((s, r) => s + r.amount, 0);
  const netPos = cfIn - cfOut;
  const totalPayroll = unified.filter(r => r.source === 'Payroll').reduce((s, r) => s + r.amount, 0);
  const cfExpenseOut = unified.filter(r => r.source === 'Expense').reduce((s, r) => s + r.amount, 0);
  const cfManualOut = unified.filter(r => r.source === 'Manual' && r.type === 'Outflow').reduce((s, r) => s + r.amount, 0);

  // Construction KPIs
  const billingRate = N(project.ctrVal) > 0 ? (totalInvoiced / N(project.ctrVal)) * 100 : 0;
  const collectionRate = totalInvoiced > 0 ? (progressCollected / totalInvoiced) * 100 : 0;
  const scCostCommitted = scInvoices.reduce((s, i) => s + N(i.gross), 0);
  const supplierCostCommitted = supplierInvoices.reduce((s, i) => s + N(i.gross), 0);
  const totalCostCommitted = scCostCommitted + supplierCostCommitted + totalPayroll;
  const totalCostPaid = scPayments.reduce((s, p) => s + N(p.amount), 0) + supplierPayments.reduce((s, p) => s + N(p.amount), 0) + totalPayroll;
  const grossProfit = earnedRevenue - totalCostCommitted;
  const grossMarginPct = earnedRevenue > 0 ? (grossProfit / earnedRevenue) * 100 : 0;
  const costToContractPct = N(project.ctrVal) > 0 ? (totalCostCommitted / N(project.ctrVal)) * 100 : 0;

  const quotations = clientDocs.filter(d => d.docType === 'quotation' || d.docType === 'descope');
  const quoTotal = quotations.reduce((s, d) => s + N(d.grossAmount), 0);
  const quoByStatus: Record<string, { count: number; value: number }> = {};
  for (const d of quotations) {
    const st = d.status || 'Draft';
    if (!quoByStatus[st]) quoByStatus[st] = { count: 0, value: 0 };
    quoByStatus[st].count++;
    quoByStatus[st].value += N(d.grossAmount);
  }

  const purchaseOrders = vendorOrders.filter(o => o.docType === 'po' || o.docType === 'descope');
  const poTotal = purchaseOrders.reduce((s, o) => s + N(o.contractedValue), 0);
  const PO_STATUS_MAP: Record<string, string> = { PartialDelivery: 'Approved', Fulfilled: 'Completed' };
  const poByStatus: Record<string, { count: number; value: number }> = {};
  for (const o of purchaseOrders) {
    const rawSt = o.status || 'Draft';
    const st = PO_STATUS_MAP[rawSt] ?? rawSt;
    if (!poByStatus[st]) poByStatus[st] = { count: 0, value: 0 };
    poByStatus[st].count++;
    poByStatus[st].value += N(o.contractedValue);
  }

  const QUO_STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected'];
  const PO_STATUSES  = ['Draft', 'Issued', 'Approved', 'Completed', 'Cancelled'];

  const quoStatusColor: Record<string, string> = {
    Draft: 'text-muted-foreground', Sent: 'text-blue-600', Accepted: 'text-emerald-600', Rejected: 'text-rose-600',
  };
  const poStatusColor: Record<string, string> = {
    Draft: 'text-muted-foreground', Issued: 'text-blue-600', Approved: 'text-emerald-600', Completed: 'text-teal-600', Cancelled: 'text-rose-600',
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard title="Contract Value" value={fc(project.ctrVal)} icon={Briefcase} />
        <KpiCard title="Total Invoiced" value={fc(totalInvoiced)} icon={FileText} />
        <KpiCard title="Earned Revenue" value={fc(earnedRevenue)} icon={Wallet} trend="up" />
        <KpiCard title="Net Cash Position" value={fc(netPos)} icon={DollarSign} trend={netPos >= 0 ? "up" : "down"} />
      </div>

      {/* Construction Project Performance KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Billing Rate */}
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center"><BarChart2 className="w-4 h-4 text-blue-600" /></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Billing Rate</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{billingRate.toFixed(1)}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, billingRate)}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1.5">{fc(totalInvoiced)} billed of {fc(project.ctrVal)}</div>
        </div>
        {/* Collection Rate */}
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center"><Target className="w-4 h-4 text-emerald-600" /></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Collection Rate</span>
          </div>
          <div className={`text-2xl font-bold ${collectionRate >= 80 ? 'text-emerald-600' : collectionRate >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{collectionRate.toFixed(1)}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className={`h-full rounded-full transition-all ${collectionRate >= 80 ? 'bg-emerald-500' : collectionRate >= 50 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, collectionRate)}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1.5">{fc(progressCollected)} collected of {fc(totalInvoiced)}</div>
        </div>
        {/* Total Cost Committed */}
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center"><HardHat className="w-4 h-4 text-orange-600" /></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cost Committed</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{fc(totalCostCommitted)}</div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className={`h-full rounded-full transition-all ${costToContractPct > 80 ? 'bg-rose-500' : 'bg-orange-400'}`} style={{ width: `${Math.min(100, costToContractPct)}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1.5">
            SC {fc(scCostCommitted)} · Supp {fc(supplierCostCommitted)} · Payroll {fc(totalPayroll)}
          </div>
        </div>
        {/* Gross Profit / Margin */}
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-950 flex items-center justify-center"><PiggyBank className="w-4 h-4 text-violet-600" /></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Gross Profit</span>
          </div>
          <div className={`text-2xl font-bold ${grossProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fc(grossProfit)}</div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className={`h-full rounded-full transition-all ${grossMarginPct >= 20 ? 'bg-violet-500' : grossMarginPct >= 0 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, Math.max(0, grossMarginPct))}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1.5">
            {grossMarginPct.toFixed(1)}% margin · {fc(earnedRevenue)} earned rev.
          </div>
        </div>
      </div>

      {/* Secondary project indicators */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card px-4 py-3 rounded-xl border border-border/50 shadow-sm flex items-center gap-3">
          <Activity className="w-4 h-4 text-blue-500 shrink-0" />
          <div>
            <div className="text-sm font-semibold">{fc(arBal)}</div>
            <div className="text-xs text-muted-foreground">AR Balance</div>
          </div>
        </div>
        <div className="bg-card px-4 py-3 rounded-xl border border-border/50 shadow-sm flex items-center gap-3">
          <AlertTriangle className={`w-4 h-4 shrink-0 ${retentionHeld > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />
          <div>
            <div className="text-sm font-semibold">{fc(retentionHeld)}</div>
            <div className="text-xs text-muted-foreground">Retention Held</div>
          </div>
        </div>
        <div className="bg-card px-4 py-3 rounded-xl border border-border/50 shadow-sm flex items-center gap-3">
          <TrendingDown className="w-4 h-4 text-rose-500 shrink-0" />
          <div>
            <div className="text-sm font-semibold">{fc(totalCostPaid)}</div>
            <div className="text-xs text-muted-foreground">Total Costs Paid</div>
          </div>
        </div>
        <div className="bg-card px-4 py-3 rounded-xl border border-border/50 shadow-sm flex items-center gap-3">
          <CheckCircle className={`w-4 h-4 shrink-0 ${netPos >= 0 ? 'text-emerald-500' : 'text-rose-500'}`} />
          <div>
            <div className={`text-sm font-semibold ${netPos >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fc(netPos)}</div>
            <div className="text-xs text-muted-foreground">Net Cash Flow</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
           <h3 className="font-display font-bold text-lg mb-4">Revenue Recognition</h3>
           <div className="space-y-2 text-sm">
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground">Gross Invoiced</span>
               <span className="font-semibold">{fc(totalInvoiced)}</span>
             </div>
             {!isReceiptOnlyMode && (
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Advance Invoiced</span>
               <span className="font-semibold text-orange-600">{fc(advanceInvoiced)}</span>
             </div>
             )}
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Progress Billing Invoiced</span>
               <span className="font-semibold text-blue-600">{fc(progressInvoiced + finalInvoiced)}</span>
             </div>
             {finalInvoiced > 0 && (
               <div className="flex justify-between py-1.5 border-b border-border/30">
                 <span className="text-muted-foreground pl-8">of which: Final Invoice</span>
                 <span className="font-semibold text-blue-600">{fc(finalInvoiced)}</span>
               </div>
             )}
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Retention Release Invoiced</span>
               <span className="font-semibold text-teal-600">{fc(retentionReleaseInvoiced)}</span>
             </div>
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Less: Retention Held</span>
               <span className="font-semibold text-amber-600">−{fc(retentionHeld)}</span>
             </div>
             {totalCN > 0 && (
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Less: Credit Notes</span>
               <span className="font-semibold text-rose-600">−{fc(totalCN)}</span>
             </div>
             )}
             <div className="flex justify-between py-1.5 border-b border-border/30 font-semibold">
               <span>Earned Revenue (Net)</span>
               <span className="text-emerald-700">{fc(earnedRevenue)}</span>
             </div>
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground">{isReceiptOnlyMode ? 'Advance Received (Receipt)' : 'Advance Received (Liability)'}</span>
               <span className="font-semibold text-orange-600">{fc(advanceReceived)}</span>
             </div>
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Less: Advance Recovered (proportional to billing)</span>
               <span className="font-semibold text-emerald-600">−{fc(advanceRecovered)}</span>
             </div>
             <div className="flex justify-between py-1.5 border-b border-border/30">
               <span className="text-muted-foreground pl-4">Advance Outstanding (Liability)</span>
               <span className="font-semibold text-orange-600">{fc(advanceOutstanding)}</span>
             </div>
             <div className="flex justify-between py-1.5 font-bold">
               <span>Outstanding AR Balance</span>
               <span className={arBal > 0 ? "text-rose-600" : "text-emerald-600"}>{fc(Math.max(0, arBal))}</span>
             </div>
           </div>
        </div>
        <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
          <h3 className="font-display font-bold text-lg mb-4">Balance Sheet Items</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">Retention Receivable</span>
              <span className="font-semibold text-amber-700">{fc(retentionReceivable)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">Advance Outstanding (Deferred Liability)</span>
              <span className="font-semibold text-orange-600">{fc(advanceOutstanding)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30 pt-3">
              <span className="text-muted-foreground">Cash Flow — Total Inflows</span>
              <span className="font-bold text-emerald-600">{fc(cfIn)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">Cash Flow — Total Outflows</span>
              <span className="font-bold text-rose-600">{fc(cfOut)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground pl-4">Manual Outflows</span>
              <span className="font-semibold text-rose-500">{fc(cfManualOut)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground pl-4">Expenses</span>
              <span className="font-semibold text-rose-500">{fc(cfExpenseOut)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground pl-4">Payroll</span>
              <span className="font-semibold text-rose-500">{fc(totalPayroll)}</span>
            </div>
            <div className="flex justify-between py-1.5 font-bold">
              <span>Net Cash Position</span>
              <span className={netPos >= 0 ? "text-emerald-600" : "text-rose-600"}>{fc(netPos)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payroll & Expenses Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!canAccess("payroll") ? <LockedCard title="Payroll Summary" /> : <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
          <h3 className="font-display font-bold text-lg mb-4">Payroll Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">Payroll Entries</span>
              <span className="font-semibold">{payroll.length}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30 font-bold">
              <span>Total Net Payroll</span>
              <span className="text-rose-600">{fc(totalPayroll)}</span>
            </div>
          </div>
        </div>}
        {!canAccess("expenses") ? <LockedCard title="Expenses Summary" /> : <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
          <h3 className="font-display font-bold text-lg mb-4">Expenses Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-border/30">
              <span className="text-muted-foreground">Expense Entries</span>
              <span className="font-semibold">{cashFlow.filter(c => c.type === 'Expense').length}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30 font-bold">
              <span>Total Expenses</span>
              <span className="text-rose-600">{fc(cfExpenseOut)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/30 pt-2">
              <span className="text-muted-foreground">Total Outflows (incl. payroll)</span>
              <span className="font-semibold text-rose-600">{fc(cfOut)}</span>
            </div>
            <div className="flex justify-between py-1.5 font-bold">
              <span>Net Cash Position</span>
              <span className={netPos >= 0 ? "text-emerald-600" : "text-rose-600"}>{fc(netPos)}</span>
            </div>
          </div>
        </div>}
      </div>

      {/* Quotations & POs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!canAccess("commercialDocs") ? <LockedCard title="Quotations" /> : <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Quotations</h3>
            <span className="text-sm text-muted-foreground">{quotations.length} total · {fc(quoTotal)}</span>
          </div>
          <div className="space-y-2 text-sm">
            {QUO_STATUSES.map(st => {
              const entry = quoByStatus[st];
              if (!entry) return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className="text-muted-foreground">{st}</span>
                  <span className="text-muted-foreground">—</span>
                </div>
              );
              return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className={quoStatusColor[st] ?? 'text-muted-foreground'}>{st} <span className="text-muted-foreground">({entry.count})</span></span>
                  <span className={`font-semibold ${quoStatusColor[st] ?? ''}`}>{fc(entry.value)}</span>
                </div>
              );
            })}
            <div className="flex justify-between py-1.5 font-bold">
              <span>Total Pipeline</span>
              <span>{fc(quoTotal)}</span>
            </div>
          </div>
        </div>}

        {!canAccess("purchaseOrders") ? <LockedCard title="Purchase Orders" /> : <div className="bg-card p-6 rounded-2xl shadow-sm border border-border/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Purchase Orders</h3>
            <span className="text-sm text-muted-foreground">{purchaseOrders.length} total · {fc(poTotal)}</span>
          </div>
          <div className="space-y-2 text-sm">
            {PO_STATUSES.map(st => {
              const entry = poByStatus[st];
              if (!entry) return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className="text-muted-foreground">{st}</span>
                  <span className="text-muted-foreground">—</span>
                </div>
              );
              return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className={poStatusColor[st] ?? 'text-muted-foreground'}>{st} <span className="text-muted-foreground">({entry.count})</span></span>
                  <span className={`font-semibold ${poStatusColor[st] ?? ''}`}>{fc(entry.value)}</span>
                </div>
              );
            })}
            <div className="flex justify-between py-1.5 font-bold">
              <span>Total Contracted</span>
              <span>{fc(poTotal)}</span>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}

function ClientRevenueTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const { taxLabel, taxTypes, taxRate: companyTaxRate } = useContext(CompanyTaxContext);
  const company = useContext(CompanyContext);
  const [printState, setPrintState] = useState<{ doc: Record<string, unknown>; type: PrintDocType } | null>(null);
  const projVatRate = N(project.vatRate ?? "0");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data: customers = [] } = useListCustomers({ companyId: project.companyId });
  const { data: invoices = [] } = useListClientInvoices({ projectId: project.id });
  const { data: receipts = [] } = useListClientReceipts({ projectId: project.id });
  const createInvoice = useCreateClientInvoice();
  const updateInvoice = useUpdateClientInvoice();
  const delInvoice = useDeleteClientInvoice();
  const createReceipt = useCreateClientReceipt();
  const updateReceipt = useUpdateClientReceipt();
  const delReceipt = useDeleteClientReceipt();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<ClientInvoice | null>(null);
  const [ciDeds, setCiDeds] = useState<{desc: string; val: string; position: 'before_vat'|'after_vat'}[]>([]);
  const [ciCnItems, setCiCnItems] = useState<{ref: string; amt: string; tax: string}[]>([]);
  const [ciRetItems, setCiRetItems] = useState<{type: string; pct: string}[]>([]);
  const [ciInvoiceType, setCiInvoiceType] = useState<string>("");
  const [ciVatRatePct, setCiVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [receiptInv, setReceiptInv] = useState<ClientInvoice | null>(null);
  const [editingReceipt, setEditingReceipt] = useState<ClientReceipt | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [subTab, setSubTab] = useState<'quo'|'inv'|'rec'>('quo');
  const [freeReceiptMode, setFreeReceiptMode] = useState(false);
  const [freeReceiptTp, setFreeReceiptTp] = useState<string>("Standard");
  const [freeReceiptInvRefs, setFreeReceiptInvRefs] = useState<string[]>([]);
  const receiptDialogOpen = !!receiptInv || !!editingReceipt || freeReceiptMode;

  const [ciQuotationIds, setCiQuotationIds] = useState<string[]>([]);
  const [ciBoqItems, setCiBoqItems] = useState<string[]>([]);
  const { data: clientDocs = [] } = useListClientDocuments({ projectId: project.id });
  const { data: boqLines = [] } = useListBoq({ projectId: project.id });
  const createClientDoc = useCreateClientDocument();
  const updateClientDoc = useUpdateClientDocument();
  const delClientDoc = useDeleteClientDocument();
  const convertToInvoice = useConvertClientDocumentToInvoice();
  const [quoOpen, setQuoOpen] = useState(false);
  const [editingQuo, setEditingQuo] = useState<ClientDocument | null>(null);
  const [quoDocType, setQuoDocType] = useState<'quotation'|'vo'|'descope'>('quotation');
  const [linkAdvanceDoc, setLinkAdvanceDoc] = useState<ClientDocument | null>(null);
  const projVatRatePct = Math.round(N(project.vatRate ?? "0") * 100);
  const [quoVatRatePct, setQuoVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [quoNetAmount, setQuoNetAmount] = useState<string>("");
  const [quoLineItems, setQuoLineItems] = useState<LineItem[]>([]);
  const [quoLiMode,    setQuoLiMode]    = useState<"free" | "boq">("free");
  const [ciLineItems,  setCiLineItems]  = useState<LineItem[]>([]);
  const [ciLiMode,     setCiLiMode]     = useState<"free" | "boq">("free");
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const lineItemDocs = (project.lineItemDocs as Record<string, boolean> | null) ?? {};
  const itemSourceMode = ((project.itemSourceMode as string | null) ?? 'free') as 'free' | 'boq' | 'budget';
  const quoLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.quotations;
  const ciLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.clientInvoices;
  const openEditReceipt = (r: ClientReceipt) => { setEditingReceipt(r); setReceiptInv(null); setFreeReceiptMode(false); setReceiptError(null); setFreeReceiptTp(r.tp ?? "Standard"); };
  const openFreeReceipt = () => { setReceiptInv(null); setEditingReceipt(null); setFreeReceiptMode(true); setReceiptError(null); setFreeReceiptTp("Standard"); setFreeReceiptInvRefs([]); };

  const grossInvoiced = invoices.reduce((s, i) => s + N(i.gross), 0);
  const retentionHeld = invoices.reduce((s, i) => s + sumRetItems(i.retItems as RetItem[] | undefined, i.gross, i.retPct), 0);
  const totalCN = invoices.reduce((s, i) => s + sumCnItems(i.cnItems as CnItem[], i.cnAmt), 0);
  const { progressTotal, advanceTotal, retentionReleaseTotal } = separateAdvances(receipts);
  const earnedRevenue = grossInvoiced - retentionHeld - totalCN;
  const retentionReceivable = retentionHeld - retentionReleaseTotal;
  const advanceRecovered = calcClientAdvanceRecovery(advanceTotal, project.ctrVal, grossInvoiced);
  const advanceOutstanding = Math.max(0, advanceTotal - advanceRecovered);

  const openNew  = () => {
    setEditing(null); setCiDeds([]); setCiCnItems([]); setCiRetItems([]); setCiInvoiceType("");
    setCiVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setCiQuotationIds([]); setCiBoqItems([]);
    setCiLineItems([]);
    setCiLiMode("free");
    setIsOpen(true);
  };
  const openEdit = (inv: ClientInvoice) => {
    setEditing(inv);
    setCiInvoiceType(inv.invoiceType || "");
    setCiVatRatePct(inv.vatRate ? String((N(inv.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setCiQuotationIds((inv.quotationIds as string[] | null | undefined) ?? []);
    setCiBoqItems((inv.boqItems as string[] | null | undefined) ?? []);
    const ciItems = (inv.lineItems as LineItem[] | null) ?? [];
    setCiLineItems(ciItems);
    setCiLiMode(ciItems.some(i => i.sourceType === "boq") ? "boq" : "free");
    setCiDeds((inv.deds as {desc:string;val:string;position?:string}[] | undefined || []).map(d => ({desc: d.desc, val: String(d.val), position: (d.position ?? 'after_vat') as 'before_vat'|'after_vat'})));
    const existingCnItems = (inv.cnItems as {ref:string;amt:number;taxPct?:number}[] | undefined);
    if (existingCnItems && existingCnItems.length > 0) {
      setCiCnItems(existingCnItems.map(c => ({ ref: c.ref, amt: String(c.amt), tax: String(N(c.taxPct ?? 0) * 100) })));
    } else if (inv.cnAmt && inv.cnAmt !== "0") {
      setCiCnItems([{ ref: inv.cnRef || '', amt: inv.cnAmt, tax: "0" }]);
    } else {
      setCiCnItems([]);
    }
    const existingRetItems = (inv.retItems as {type:string;pct:number}[] | undefined);
    if (existingRetItems && existingRetItems.length > 0) {
      setCiRetItems(existingRetItems.map(r => ({ type: r.type, pct: String((N(r.pct) * 100).toFixed(4)) })));
    } else if (N(inv.retPct) > 0) {
      setCiRetItems([{ type: 'Performance Retention', pct: String((N(inv.retPct) * 100).toFixed(4)) }]);
    } else {
      setCiRetItems([]);
    }
    setIsOpen(true);
  };

  const openNewQuo = (type: 'quotation'|'vo'|'descope' = 'quotation') => {
    setEditingQuo(null);
    setQuoDocType(type);
    setQuoVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setQuoNetAmount("");
    setQuoLineItems([]);
    setQuoLiMode("free");
    setQuoOpen(true);
  };
  const openEditQuo = (doc: ClientDocument) => {
    setEditingQuo(doc);
    setQuoDocType(doc.docType as 'quotation'|'vo'|'descope');
    setQuoVatRatePct(doc.vatRate ? String((N(doc.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setQuoNetAmount(doc.grossAmount ?? "");
    const qItems = (doc.lineItems as LineItem[] | null) ?? [];
    setQuoLineItems(qItems);
    setQuoLiMode(qItems.some(i => i.sourceType === "boq") ? "boq" : "free");
    setQuoOpen(true);
  };

  const handleQuoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const dt = (fd.get("docType") as 'quotation'|'vo'|'descope') || quoDocType;
    const liEnabled = quoLineItemsEnabled && quoLineItems.length > 0;
    const netAmt = liEnabled ? lineItemsNetTotal(quoLineItems) : N(quoNetAmount);
    if (dt === 'descope' && netAmt >= 0) {
      toast({ title: "Invalid amount", description: "Descope documents must have a negative net amount.", variant: "destructive" });
      return;
    }
    if (dt !== 'descope' && netAmt < 0) {
      toast({ title: "Invalid amount", description: "Only Descope documents can have a negative amount.", variant: "destructive" });
      return;
    }
    const vatPct = liEnabled ? 0 : N(quoVatRatePct) / 100;
    const computedVatAmount = liEnabled ? quoLineItems.reduce((s, i) => s + i.taxAmount, 0) : netAmt * vatPct;
    const data = {
      docType: dt as CreateClientDocumentBodyDocType,
      refNumber: (fd.get("refNumber") as string) || "",
      customerId: project.clientId ?? null,
      customerName: project.clientName || project.client || "",
      description: fd.get("description") as string,
      date: fd.get("date") as string,
      validUntil: (fd.get("validUntil") as string) || null,
      grossAmount: String(netAmt),
      vatRate: String(vatPct),
      vatAmount: String(computedVatAmount),
      customerPoNumber: (fd.get("customerPoNumber") as string) || null,
      notes: (fd.get("notes") as string) || null,
      status: ((fd.get("status") as string) || "Draft") as CreateClientDocumentBodyStatus,
      ...(quoLineItemsEnabled ? { lineItems: quoLineItems } : {}),
    };
    try {
      if (editingQuo) {
        await updateClientDoc.mutateAsync({ id: editingQuo.id, data });
      } else {
        await createClientDoc.mutateAsync({ data: { id: `QUO-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
      }
      queryClient.invalidateQueries({ queryKey: getListClientDocumentsQueryKey({ projectId: project.id }) });
      setQuoOpen(false);
      setEditingQuo(null);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { error?: string }; message?: string };
      if (apiErr?.status === 409 || apiErr?.data?.error?.includes("already in use") || apiErr?.message?.includes("already in use")) {
        const msg = apiErr?.data?.error || "This reference number is already in use. Please choose a different one.";
        toast({ title: "Duplicate ref number", description: msg, variant: "destructive" });
        return;
      }
      throw err;
    }
  };

  const handleConvertToInvoice = (doc: ClientDocument) => {
    if (doc.status !== "Accepted") {
      toast({ title: "Cannot convert", description: "Only accepted quotations can be converted to invoices.", variant: "destructive" });
      return;
    }
    const alreadyConverted = invoices.some(inv => inv.cnRef === doc.refNumber);
    if (alreadyConverted) {
      toast({ title: "Already converted", description: `An invoice already exists for ${doc.refNumber}.`, variant: "destructive" });
      return;
    }
    setEditing({
      id: undefined as any,
      projectId: project.id,
      companyId: project.companyId,
      customerId: doc.customerId || "",
      customerName: doc.customerName || project.client || "",
      desc: `Invoice from ${doc.refNumber}${doc.description ? " — " + doc.description : ""}`,
      date: new Date().toISOString().slice(0, 10),
      gross: String(N(doc.grossAmount ?? "0") + N(doc.vatAmount ?? "0")),
      retPct: "",
      cnRef: doc.refNumber,
    } as any);
    setCiDeds([]);
    setCiCnItems([]);
    setCiRetItems([]);
    setCiInvoiceType("");
    setCiQuotationIds([]);
    setCiBoqItems([]);
    setIsOpen(true);
  };

  const handleReceiptSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setReceiptError(null);
    const fd = new FormData(e.currentTarget);
    const tp = fd.get("tp") as string || "Standard";
    const isAdvanceTp = tp === 'Advance' || tp === 'Advance mobilisation';
    const fields = {
      amount: fd.get("amount") as string,
      date: fd.get("date") as string,
      tp,
      method: fd.get("method") as string,
      desc: fd.get("desc") as string,
      refNumber: (fd.get("refNumber") as string) || "",
    };
    try {
      if (editingReceipt) {
        await updateReceipt.mutateAsync({ id: editingReceipt.id, data: fields });
      } else {
        const isReceiptOnly = project.advanceMode === 'receipt_only';
        const allowNoRef = isReceiptOnly && isAdvanceTp;
        const invRef = receiptInv ? receiptInv.id : freeReceiptInvRefs[0] || "";
        const invRefs = receiptInv ? [receiptInv.id] : freeReceiptInvRefs;
        if (!invRef && !allowNoRef) return;
        await createReceipt.mutateAsync({ data: {
          id: `CR-${uid()}`, projectId: project.id, companyId: project.companyId,
          invRef: invRef || "", invRefs,
          ...fields,
        }});
      }
    } catch (err: unknown) {
      const msg = (err as { data?: { error?: string } })?.data?.error ?? (err as Error)?.message ?? "Failed to save receipt.";
      setReceiptError(msg);
      toast({ title: "Overpayment not allowed", description: msg, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: getListClientReceiptsQueryKey({ projectId: project.id }) });
    queryClient.invalidateQueries({ queryKey: getListClientReceiptsQueryKey({ companyId: project.companyId }) });
    setReceiptInv(null);
    setEditingReceipt(null);
    setFreeReceiptMode(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ciInvoiceType) {
      toast({ title: "Invoice Type required", description: "Please select an invoice type before saving.", variant: "destructive" });
      return;
    }
    const fd = new FormData(e.currentTarget);
    const ciLiEnabled = ciLineItemsEnabled && ciLineItems.length > 0;
    const vatPct = ciLiEnabled ? 0 : N(ciVatRatePct) / 100;
    const grossVal = ciLiEnabled ? String(lineItemsNetTotal(ciLineItems)) : (fd.get("gross") as string) || editing?.gross || "0";
    const data = {
      customerName: (fd.get("customerName") as string) || null,
      desc: fd.get("desc") as string,
      gross: grossVal,
      date: fd.get("date") as string,
      dueDate: (fd.get("dueDate") as string) || null,
      refNumber: (fd.get("refNumber") as string) || "",
      retPct: "0",
      vatRate: String(vatPct),
      advRecPct: String(N(String(fd.get("advRecPct") ?? "0")) / 100),
      invoiceType: ciInvoiceType,
      quotationIds: ciQuotationIds,
      boqItems: ciBoqItems,
      cnItems: ciCnItems.filter(c => c.amt).map(c => ({ ref: c.ref, amt: Number(c.amt), taxPct: N(c.tax) / 100 })) as CnItem[],
      retItems: ciRetItems.filter(r => r.pct).map(r => ({ type: r.type, pct: N(r.pct) / 100 })),
      deds: ciDeds.filter(d => d.desc && d.val).map((d, i) => ({ id: `d${i}`, desc: d.desc, tp: "Additional", val: Number(d.val), position: d.position })),
      ...(ciLineItemsEnabled ? { lineItems: ciLineItems } : {}),
    };
    if (editing?.id) {
      await updateInvoice.mutateAsync({ id: editing.id, data });
    } else {
      await createInvoice.mutateAsync({ data: { id: `CI-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    queryClient.invalidateQueries({ queryKey: getListClientInvoicesQueryKey() });
    setIsOpen(false);
    setEditing(null);
    setCiDeds([]);
    setCiCnItems([]);
    setCiRetItems([]);
    setCiInvoiceType("");
    setCiQuotationIds([]);
    setCiBoqItems([]);
    setCiLineItems([]);
  };

  return (
    <>
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Client Revenue</h2>
          <p className="text-muted-foreground text-sm mt-1">Quotations, invoices, receipts and collections</p>
        </div>
        {subTab === 'quo' ? (
          <Button className="rounded-xl font-medium" onClick={() => openNewQuo('quotation')}>
            <Plus className="w-4 h-4 mr-2"/> {canAccess('voDescope') ? 'New Quotation/VO' : 'New Quotation'}
          </Button>
        ) : subTab === 'inv' ? (
          <Button className="rounded-xl font-medium" onClick={openNew}>
            <Plus className="w-4 h-4 mr-2"/> New Invoice
          </Button>
        ) : (
          <Button className="rounded-xl font-medium" onClick={openFreeReceipt}>
            <Plus className="w-4 h-4 mr-2"/> New Receipt
          </Button>
        )}

        {/* Quotation / VO form dialog */}
        <Dialog open={quoOpen} onOpenChange={v => { setQuoOpen(v); if (!v) setEditingQuo(null); }}>
          <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${quoLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[700px]'}`}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{editingQuo ? "Edit Document" : "New Quotation / VO"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleQuoSubmit} className="space-y-4 pt-2">
              {/* Row 1: Type + Ref Number */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select name="docType" value={quoDocType} onValueChange={v => { setQuoDocType(v as 'quotation'|'vo'|'descope'); }}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quotation">Quotation (Commercial Proposal)</SelectItem>
                      {canAccess('voDescope') && <SelectItem value="vo">Variation Order (VO)</SelectItem>}
                      {canAccess('voDescope') && <SelectItem value="descope">Descope (Scope Reduction)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <RefNumberInput
                  enabled={canAccess('docSequencing')}
                  key={quoDocType}
                  docType={quoDocType}
                  companyId={project.companyId}
                  projectId={project.id}
                  editingValue={editingQuo?.refNumber ?? undefined}
                />
              </div>
              {/* Row 2: Description (full width) */}
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input key={(editingQuo?.id ?? 'new') + 'desc'} name="description" required placeholder="Brief scope description" className="rounded-xl" defaultValue={editingQuo?.description ?? ""} />
              </div>
              {/* Row 3: Client + Customer PO Number */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <div className="flex items-center h-10 px-3 rounded-xl border border-border/50 bg-muted/30 gap-2">
                    <span className="text-sm flex-1 truncate">{project.clientName || project.client || <span className="text-muted-foreground">No client set on project</span>}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 bg-muted rounded px-1.5 py-0.5">locked</span>
                  </div>
                  <input type="hidden" name="customerId" value={project.clientId ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Customer PO Number</Label>
                  <Input key={(editingQuo?.id ?? 'new') + 'cpo'} name="customerPoNumber" placeholder="Client's PO / reference" className="rounded-xl font-mono" defaultValue={editingQuo?.customerPoNumber ?? ""} />
                </div>
              </div>
              {/* Row 4: Date + Validity + Status */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input key={(editingQuo?.id ?? 'new') + 'dt'} type="date" name="date" required className="rounded-xl" defaultValue={editingQuo?.date ?? new Date().toISOString().slice(0,10)} />
                </div>
                <div className="space-y-2">
                  <Label>Validity Date</Label>
                  <Input key={(editingQuo?.id ?? 'new') + 'vd'} type="date" name="validUntil" className="rounded-xl" defaultValue={editingQuo?.validUntil ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select key={(editingQuo?.id ?? 'new') + 'sts'} name="status" defaultValue={editingQuo?.status ?? "Draft"}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Sent">Sent</SelectItem>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Row 5: Amount — Line Items or single Net+VAT */}
              {quoLineItemsEnabled ? (
                <LineItemsEditor
                  projectId={project.id}
                  value={quoLineItems}
                  onChange={setQuoLineItems}
                  sourceMode={quoLiMode}
                  onSourceModeChange={v => setQuoLiMode(v as "free" | "boq")}
                  availableModes={["free", "boq"]}
                  taxTypes={taxTypes}
                  taxLabel={taxLabel}
                />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Net Amount (excl. {taxLabel}) *{quoDocType === 'descope' ? ' — negative' : ''}</Label>
                      <Input key={(editingQuo?.id ?? 'new') + 'amt'} type="number" required step="0.01" className="rounded-xl" value={quoNetAmount} onChange={e => setQuoNetAmount(e.target.value)} placeholder={quoDocType === 'descope' ? '-0.00' : '0.00'} />
                    </div>
                    <div className="space-y-2">
                      <Label>{taxLabel} Rate</Label>
                      <TaxRatePicker value={quoVatRatePct} onChange={setQuoVatRatePct} />
                    </div>
                  </div>
                  {(N(quoNetAmount) !== 0 || N(quoVatRatePct) > 0) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm space-y-1">
                      <div className="flex justify-between text-muted-foreground"><span>Net (excl. {taxLabel})</span><span>{fc(N(quoNetAmount))}</span></div>
                      <div className="flex justify-between text-blue-700"><span>{taxLabel} ({quoVatRatePct}%)</span><span>{fc(N(quoNetAmount) * N(quoVatRatePct) / 100)}</span></div>
                      <div className="flex justify-between font-bold border-t border-blue-200 pt-1"><span>Gross Total (incl. {taxLabel})</span><span>{fc(N(quoNetAmount) * (1 + N(quoVatRatePct) / 100))}</span></div>
                    </div>
                  )}
                </>
              )}
              {/* Row 6: Notes (full width) */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input key={(editingQuo?.id ?? 'new') + 'notes'} name="notes" placeholder="Optional notes or terms" className="rounded-xl" defaultValue={editingQuo?.notes ?? ""} />
              </div>
              {editingQuo?.id && (
                <AttachmentPanel
                  entityType="client_document"
                  entityId={editingQuo.id}
                  companyId={project.companyId}
                  enabled={canAccess('attachments')}
                />
              )}
              <Button type="submit" className="w-full h-11 rounded-xl" disabled={createClientDoc.isPending || updateClientDoc.isPending}>
                {createClientDoc.isPending || updateClientDoc.isPending ? "Saving..." : editingQuo ? "Save Changes" : "Save Document"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Link to Advance dialog (reuses receipt dialog pre-filled in advance mode) */}
        <Dialog open={!!linkAdvanceDoc} onOpenChange={v => { if (!v) setLinkAdvanceDoc(null); }}>
          <DialogContent className="max-w-full sm:max-w-[440px] rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-lg">Link to Advance — {linkAdvanceDoc?.refNumber}</DialogTitle></DialogHeader>
            <form onSubmit={async e => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              await createReceipt.mutateAsync({ data: {
                id: `CR-${uid()}`, projectId: project.id, companyId: project.companyId,
                invRef: (fd.get("invRef") as string) || "",
                amount: fd.get("amount") as string,
                date: fd.get("date") as string,
                tp: "Advance mobilisation",
                method: fd.get("method") as string,
                desc: `Advance linked to ${linkAdvanceDoc?.refNumber}`,
              }});
              queryClient.invalidateQueries({ queryKey: getListClientReceiptsQueryKey({ projectId: project.id }) });
              setLinkAdvanceDoc(null);
            }} className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label>Link to Invoice (for advance recovery tracking)</Label>
                <select name="invRef" style={{width:'100%',padding:'6px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,background:'#fff'}}>
                  <option value="">No invoice yet (standalone advance)</option>
                  {invoices.map(i => <option key={i.id} value={i.id}>{i.id} — {(i.desc||'').slice(0,40)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Advance Amount *</Label>
                  <Input type="number" name="amount" required min="0" step="0.01" placeholder="0.00" className="rounded-xl" defaultValue={linkAdvanceDoc?.grossAmount ?? ""} />
                </div>
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Input type="date" name="date" className="rounded-xl" defaultValue={new Date().toISOString().slice(0,10)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Payment Method</Label>
                <Select name="method" defaultValue="Bank Transfer">
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full h-10 rounded-xl" disabled={createReceipt.isPending}>
                {createReceipt.isPending ? "Saving..." : "Record Advance"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${ciLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[800px]'}`}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{editing?.id ? "Edit Client Invoice" : "Create Client Invoice"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <RefNumberInput
                  enabled={canAccess('docSequencing')}
                key={(editing?.id ?? "new") + "ref-ci"}
                docType="client-invoice"
                companyId={project.companyId}
                projectId={project.id}
                editingValue={editing?.refNumber ?? ""}
              />
              {/* Row 1: Client | Invoice Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <div className="flex items-center h-10 px-3 rounded-xl border border-border/50 bg-muted/30 gap-2">
                    <span className="text-sm flex-1 truncate">{project.clientName || project.client || <span className="text-muted-foreground">No client set on project</span>}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 bg-muted rounded px-1.5 py-0.5">locked</span>
                  </div>
                  <input type="hidden" name="customerName" value={project.clientName || project.client || ""} />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Type *</Label>
                  <Select value={ciInvoiceType || undefined} onValueChange={setCiInvoiceType}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Progress Invoice">Progress Invoice</SelectItem>
                      <SelectItem value="Final Invoice">Final Invoice</SelectItem>
                      {project.advanceMode !== 'receipt_only' && <SelectItem value="Progress Advance">Progress Advance</SelectItem>}
                      {project.advanceMode !== 'receipt_only' && <SelectItem value="Recovery Advance">Recovery Advance</SelectItem>}
                      <SelectItem value="Retention Release">Retention Release</SelectItem>
                    </SelectContent>
                  </Select>
                  {ciInvoiceType && ({
                    'Progress Invoice':  'Billing for completed work to date. Retention withheld; advance recovery applied.',
                    'Final Invoice':     'Final billing on project completion. Settles remaining contract value.',
                    'Progress Advance':  'Advance for work not yet done. Not deducted or recovered from future invoices.',
                    'Recovery Advance':  'Advance invoice. Deducted from future progress and final invoices.',
                    'Retention Release': 'Releases withheld retention on milestone or project completion.',
                  } as Record<string,string>)[ciInvoiceType] && (
                    <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-2.5 py-1.5 leading-snug">
                      {({
                        'Progress Invoice':  'Billing for completed work to date. Retention withheld; advance recovery applied.',
                        'Final Invoice':     'Final billing on project completion. Settles remaining contract value.',
                        'Progress Advance':  'Advance for work not yet done. Not deducted or recovered from future invoices.',
                        'Recovery Advance':  'Advance invoice. Deducted from future progress and final invoices.',
                        'Retention Release': 'Releases withheld retention on milestone or project completion.',
                      } as Record<string,string>)[ciInvoiceType]}
                    </p>
                  )}
                </div>
              </div>
              {/* Row 1b: Linked Quotations | Linked BOQ Items */}
              <div className={`grid gap-4 ${clientDocs.length > 0 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                {clientDocs.length > 0 && (
                  <div className="space-y-2">
                    <Label>Linked Quotations / VOs</Label>
                    <MultiPicker
                      placeholder="Link quotation(s)…"
                      options={clientDocs.map(d => ({ id: d.refNumber, label: `${d.refNumber}${d.description ? " — " + d.description.slice(0, 30) : ""}` }))}
                      selected={ciQuotationIds}
                      onChange={setCiQuotationIds}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Linked BOQ Items</Label>
                  <MultiPicker
                    placeholder={boqLines.length === 0 ? "No BOQ items — add in BOQ tab first" : "Link BOQ item(s)…"}
                    options={boqLines.map((b: BoqItem) => ({ id: b.id, label: `${b.sec ? b.sec + ' / ' : ''}${b.itm ?? b.id}`.slice(0, 50) }))}
                    selected={ciBoqItems}
                    onChange={setCiBoqItems}
                  />
                </div>
              </div>
              {/* Row 2: Description */}
              <div className="space-y-2">
                <Label>Description / Invoice Title *</Label>
                <Input key={editing?.id + "d"} name="desc" required placeholder="e.g., Valuation No. 1" className="rounded-xl" defaultValue={editing?.desc ?? ""} />
              </div>
              {/* Row 3: Issue Date | Due Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issue Date</Label>
                  <Input key={editing?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editing?.date ?? new Date().toISOString().slice(0,10)} />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input key={editing?.id + "dd"} type="date" name="dueDate" className="rounded-xl" defaultValue={editing?.dueDate ?? ""} />
                </div>
              </div>
              {/* Row 4: Gross Amount | Tax Rate — or Line Items */}
              {ciLineItemsEnabled ? (
                <LineItemsEditor
                  projectId={project.id}
                  value={ciLineItems}
                  onChange={setCiLineItems}
                  sourceMode={ciLiMode}
                  onSourceModeChange={v => setCiLiMode(v as "free" | "boq")}
                  availableModes={["free", "boq"]}
                  taxTypes={taxTypes}
                  taxLabel={taxLabel}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gross Amount (excl. {taxLabel}) *</Label>
                    <Input key={editing?.id + "g"} type="number" name="gross" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.gross ?? ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>{taxLabel} Rate</Label>
                    <TaxRatePicker value={ciVatRatePct} onChange={setCiVatRatePct} />
                  </div>
                </div>
              )}
              {/* Row 5: Retention Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Retention</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setCiRetItems(r => [...r, { type: 'Performance Retention', pct: String((N(project.cltRet || "0.10") * 100).toFixed(4)) }])}>+ Add Retention</Button>
                </div>
                {ciRetItems.length > 0 && (
                  <div className="grid gap-1 text-[10px] text-muted-foreground font-medium px-1" style={{gridTemplateColumns:'1fr 110px 32px'}}>
                    <span>Type</span><span>%</span><span/>
                  </div>
                )}
                {ciRetItems.map((r, i) => (
                  <div key={i} className="grid gap-2 items-center" style={{gridTemplateColumns:'1fr 110px 32px'}}>
                    <select value={r.type} onChange={e => setCiRetItems(rs => rs.map((x,j) => j===i ? {...x,type:e.target.value} : x))} style={{fontSize:13,padding:'7px 10px',borderRadius:12,border:'1px solid #d1d5db',background:'#fff'}}>
                      <option>Performance Retention</option>
                      <option>Defects Liability</option>
                      <option>Contractual Retention</option>
                      <option>Other</option>
                    </select>
                    <Input type="number" placeholder="%" className="rounded-xl" min="0" max="100" step="0.01" value={r.pct} onChange={e => setCiRetItems(rs => rs.map((x,j) => j===i ? {...x,pct:e.target.value} : x))} />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setCiRetItems(rs => rs.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {ciRetItems.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No retention — click "Add Retention" to add items</div>}
              </div>
              {/* Row 5b: Advance Recovery % (conditional) */}
              {(ciInvoiceType === 'Recovery Advance' || (project.advanceMode !== 'receipt_only' && ['Progress Invoice', 'Final Invoice'].includes(ciInvoiceType))) && (
                <div className="space-y-2">
                  <Label>Advance Recovery %</Label>
                  <Input key={editing?.id + "ar"} type="number" name="advRecPct" min="0" max="100" step="0.1" className="rounded-xl" defaultValue={editing ? String(N(editing.advRecPct) * 100) : String(N(project.advRec || "0") * 100)} />
                </div>
              )}
              {/* Row 6: Additional Deductions (before Credit Notes) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Additional Deductions</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setCiDeds(d => [...d, {desc:"",val:"",position:"after_vat"}])}>+ Add Deduction</Button>
                </div>
                {ciDeds.map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Description" className="rounded-xl flex-1" value={d.desc} onChange={e => setCiDeds(ds => ds.map((x,j) => j===i ? {...x,desc:e.target.value} : x))} />
                    <Input type="number" placeholder="Amount" className="rounded-xl w-28" min="0" step="0.01" value={d.val} onChange={e => setCiDeds(ds => ds.map((x,j) => j===i ? {...x,val:e.target.value} : x))} />
                    <select value={d.position} onChange={e => setCiDeds(ds => ds.map((x,j) => j===i ? {...x,position:e.target.value as 'before_vat'|'after_vat'} : x))} style={{fontSize:11,padding:'4px 6px',borderRadius:6,border:'1px solid #d1d5db',background:'#fff',flexShrink:0}}>
                      <option value="after_vat">After {taxLabel}</option>
                      <option value="before_vat">Before {taxLabel}</option>
                    </select>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setCiDeds(ds => ds.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {ciDeds.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No additional deductions</div>}
              </div>
              {/* Row 7: Credit Notes (after Deductions) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Credit Notes</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setCiCnItems(d => [...d, {ref:"",amt:"",tax:ciVatRatePct}])}>+ Add Credit Note</Button>
                </div>
                {ciCnItems.length > 0 && (
                  <div className="grid grid-cols-[1fr_7rem_5rem_2rem] gap-2 pb-1">
                    <span className="text-xs text-muted-foreground">CN Reference</span>
                    <span className="text-xs text-muted-foreground">Amount (excl. tax)</span>
                    <span className="text-xs text-muted-foreground">{taxLabel} Rate</span>
                    <span />
                  </div>
                )}
                {ciCnItems.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_7rem_5rem_2rem] gap-2 items-center">
                    <Input placeholder="CN Reference" className="rounded-xl" value={c.ref} onChange={e => setCiCnItems(cs => cs.map((x,j) => j===i ? {...x,ref:e.target.value} : x))} />
                    <Input type="number" placeholder="0.00" className="rounded-xl" min="0" step="0.01" value={c.amt} onChange={e => setCiCnItems(cs => cs.map((x,j) => j===i ? {...x,amt:e.target.value} : x))} />
                    <TaxRatePicker value={c.tax} onChange={v => setCiCnItems(cs => cs.map((x,j) => j===i ? {...x,tax:v} : x))} className="rounded-xl" />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => setCiCnItems(cs => cs.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {ciCnItems.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No credit notes</div>}
              </div>
              {editing?.id && (
                <AttachmentPanel
                  entityType="client_invoice"
                  entityId={editing.id}
                  companyId={project.companyId}
                  enabled={canAccess('attachments')}
                />
              )}
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createInvoice.isPending || updateInvoice.isPending}>
                {createInvoice.isPending || updateInvoice.isPending ? "Saving..." : editing ? "Save Changes" : "Save Invoice"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sub-tab bar */}
      <div style={{display:"flex",gap:2,background:"#f3f4f6",borderRadius:7,padding:3,overflowX:"auto",maxWidth:"100%"}}>
        {([['quo','Quotations'],['inv','Client Invoices'],['rec','Receipts & Payments']] as const).map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{padding:'5px 16px',borderRadius:5,border:'none',fontSize:12,fontWeight:500,background:subTab===id?'#1a2332':'transparent',color:subTab===id?'#fff':'#6b7280',cursor:'pointer',transition:'all .15s'}}>
            {label}
          </button>
        ))}
      </div>

      {/* Quotations sub-tab */}
      {subTab === 'quo' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Total Quoted Value</div>
              <div className="text-2xl font-bold">{fc(clientDocs.reduce((s,d) => s + N(d.grossAmount), 0))}</div>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Accepted</div>
              <div className="text-2xl font-bold text-emerald-600">{fc(clientDocs.filter(d=>d.status==='Accepted').reduce((s,d) => s + N(d.grossAmount), 0))}</div>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Pending (Draft/Sent)</div>
              <div className="text-2xl font-bold text-amber-600">{fc(clientDocs.filter(d=>d.status==='Draft'||d.status==='Sent').reduce((s,d) => s + N(d.grossAmount), 0))}</div>
            </div>
          </div>
          <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">Ref Number</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Client</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Customer PO No</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Valid Until</TableHead>
                  <TableHead className="text-right font-semibold">Net Amount</TableHead>
                  <TableHead className="text-right font-semibold text-blue-700">{taxLabel}</TableHead>
                  <TableHead className="text-right font-semibold">Gross (incl. {taxLabel})</TableHead>
                  <TableHead className="text-center font-semibold">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientDocs.length === 0 && (
                  <TableRow><TableCell colSpan={12} className="text-center py-12 text-muted-foreground">No quotations or VOs yet. Click "New Quotation/VO" above.</TableCell></TableRow>
                )}
                {clientDocs.map((doc: ClientDocument) => {
                  const stBg = doc.status === 'Accepted' ? 'bg-emerald-100 text-emerald-700' : doc.status === 'Rejected' ? 'bg-rose-100 text-rose-700' : doc.status === 'Sent' ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground';
                  const docVatAmt = N(doc.vatAmount ?? "0");
                  const docGrossInclVat = N(doc.grossAmount) + docVatAmt;
                  return (
                    <TableRow key={doc.id} className="group hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium font-mono text-sm">{doc.refNumber}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{doc.docType === 'quotation' ? 'Quotation' : doc.docType === 'descope' ? 'Descope' : 'VO'}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{doc.customerName || '—'}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-sm" title={doc.description}>{doc.description || '—'}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{doc.customerPoNumber || '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{doc.date || '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{doc.validUntil || '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(doc.grossAmount)}</TableCell>
                      <TableCell className="text-right text-blue-600">{docVatAmt > 0 ? fc(docVatAmt) : '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(docGrossInclVat)}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${stBg} border-none text-xs`}>{doc.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {doc.status === 'Accepted' && !doc.convertedInvoiceId && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" onClick={() => handleConvertToInvoice(doc)}>
                              Convert to Invoice
                            </Button>
                          )}
                          {doc.status === 'Accepted' && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-orange-700 hover:text-orange-800 hover:bg-orange-50" onClick={() => setLinkAdvanceDoc(doc)}>
                              Link to Advance
                            </Button>
                          )}
                          {doc.convertedInvoiceId && (
                            <span className="text-xs text-muted-foreground italic px-2">→ {doc.convertedInvoiceId}</span>
                          )}
                          {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _c = customers.find((x: Customer) => x.id === doc.customerId); setPrintState({ doc: { ...doc as unknown as Record<string, unknown>, partyPhone: _c?.phone ?? "", partyEmail: _c?.email ?? "", partyCountry: _c?.country ?? "", partyVatNo: _c?.vatNo ?? "", partyContact: _c?.contact ?? "" }, type: doc.docType === 'quotation' ? 'client-quotation' : doc.docType === 'vo' ? 'client-vo' : 'client-descope' }); }}>
                            <Printer className="w-3.5 h-3.5" />
                          </Button>}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditQuo(doc)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600" onClick={async () => {
                            if(await confirm({ title: 'Delete Document', message: 'Delete this document? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) {
                              try {
                                await delClientDoc.mutateAsync({ id: doc.id });
                                queryClient.invalidateQueries({ queryKey: getListClientDocumentsQueryKey({ projectId: project.id }) });
                              } catch (err: unknown) {
                                const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed. This document may have been converted to an invoice.";
                                toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
                              }
                            }
                          }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {subTab === 'inv' && <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        {projVatRate > 0 && (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700 flex items-center gap-2">
            <span className="font-semibold">VAT {Math.round(projVatRate * 100)}% applied</span>
            <span className="text-blue-500">— from project settings. Net Payable = Gross + VAT − Retention − CN.</span>
          </div>
        )}
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-semibold">Invoice #</TableHead>
              <TableHead className="font-semibold">Client</TableHead>
              <TableHead className="font-semibold">Description</TableHead>
              <TableHead className="font-semibold">Type</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="text-right font-semibold">Gross</TableHead>
              {projVatRate > 0 && <TableHead className="text-right font-semibold text-blue-700">VAT ({Math.round(projVatRate * 100)}%)</TableHead>}
              {projVatRate > 0 && <TableHead className="text-right font-semibold">Total+VAT</TableHead>}
              <TableHead className="text-right font-semibold">Retention</TableHead>
              {project.advanceMode !== 'receipt_only' && <TableHead className="text-right font-semibold text-orange-700">Adv. Rec.</TableHead>}
              <TableHead className="text-right font-semibold">CN Amt</TableHead>
              <TableHead className="text-right font-semibold">Net Payable</TableHead>
              <TableHead className="text-right font-semibold">Collected</TableHead>
              <TableHead className="text-right font-semibold">Balance</TableHead>
              <TableHead className="font-semibold text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={20} className="text-center py-12 text-muted-foreground">No invoices found. Create one above.</TableCell></TableRow>
            )}
            {invoices.map(inv => {
              const invVatRate = N(inv.vatRate ?? 0) || projVatRate;
              const { va, tot, ret, adv, net, coll, bal, cn } = calcCI(inv, receipts, invVatRate);
              const st = stBadge(bal, coll);
              return (
                <TableRow key={inv.id} className="hover:bg-muted/30 transition-colors group">
                  <TableCell className="font-medium">{inv.id}</TableCell>
                  <TableCell className="max-w-[120px] truncate text-sm text-muted-foreground">{inv.customerName || "—"}</TableCell>
                  <TableCell className="max-w-[160px] truncate" title={inv.desc}>{inv.desc}</TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{inv.invoiceType || "—"}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">{inv.date || '—'}</TableCell>
                  <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                  {projVatRate > 0 && <TableCell className="text-right text-blue-600 font-medium">{fc(va)}</TableCell>}
                  {projVatRate > 0 && <TableCell className="text-right">{fc(tot)}</TableCell>}
                  <TableCell className="text-right text-amber-700">{ret > 0 ? fc(ret) : '—'}</TableCell>
                  {project.advanceMode !== 'receipt_only' && <TableCell className="text-right text-orange-600">{adv > 0 ? fc(adv) : '—'}</TableCell>}
                  <TableCell className="text-right text-muted-foreground">{cn > 0 ? fc(cn) : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{fc(net)}</TableCell>
                  <TableCell className="text-right text-emerald-600 font-medium">{fc(coll)}</TableCell>
                  <TableCell className="text-right font-bold">{fc(bal)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${st.bg} ${st.cl} border-none`}>{st.lb}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Record Receipt" onClick={() => { setReceiptInv(inv); setReceiptError(null); setFreeReceiptTp("Standard"); }}>
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _c = customers.find((x: Customer) => x.id === inv.customerId); setPrintState({ doc: { ...inv as unknown as Record<string, unknown>, partyPhone: _c?.phone ?? "", partyEmail: _c?.email ?? "", partyCountry: _c?.country ?? "", partyVatNo: _c?.vatNo ?? "", partyContact: _c?.contact ?? "" }, type: 'client-invoice' }); }}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(inv)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" onClick={async () => {
                        if(await confirm({ title: 'Delete Invoice', message: 'Delete this invoice? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) {
                          try {
                            await delInvoice.mutateAsync({ id: inv.id });
                            queryClient.invalidateQueries({ queryKey: getListClientInvoicesQueryKey() });
                          } catch (err: unknown) {
                            const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed. This invoice may have receipts linked to it.";
                            toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
                          }
                        }
                      }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>}

      {/* Record / Edit Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={open => { if (!open) { setReceiptInv(null); setEditingReceipt(null); setFreeReceiptMode(false); } }}>
        <DialogContent className="max-w-full sm:max-w-[440px] rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-lg">{editingReceipt ? "Edit Receipt" : receiptInv ? `Record Receipt — ${receiptInv.id}` : "New Receipt"}</DialogTitle></DialogHeader>
          <form onSubmit={handleReceiptSubmit} className="space-y-3 pt-2">
            {receiptError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{receiptError}</div>
            )}
            <RefNumberInput
                  enabled={canAccess('docSequencing')}
              key={(editingReceipt?.id ?? "new") + "ref-cr"}
              docType="client-receipt"
              companyId={project.companyId}
              projectId={project.id}
              editingValue={editingReceipt?.refNumber ?? ""}
            />
            {freeReceiptMode && !editingReceipt && !(project.advanceMode === 'receipt_only' && (freeReceiptTp === 'Advance' || freeReceiptTp === 'Advance mobilisation')) && (
              <div className="space-y-1.5">
                <Label>Invoice(s) *</Label>
                <MultiPicker
                  placeholder="Select invoice(s)…"
                  options={invoices.map(i => ({ id: i.id, label: `${i.id} — ${(i.desc || '').slice(0, 40)}` }))}
                  selected={freeReceiptInvRefs}
                  onChange={setFreeReceiptInvRefs}
                />
                {freeReceiptInvRefs.length === 0 && <p className="text-xs text-muted-foreground">Select at least one invoice</p>}
              </div>
            )}
            {freeReceiptMode && !editingReceipt && project.advanceMode === 'receipt_only' && (freeReceiptTp === 'Advance' || freeReceiptTp === 'Advance mobilisation') && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
                Advance receipt — no invoice reference required in Receipt Only mode.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount *</Label>
                <Input key={editingReceipt?.id + "amt"} type="number" name="amount" required min="0" step="0.01" placeholder="0.00" className="rounded-xl" defaultValue={editingReceipt?.amount ?? ""} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input key={editingReceipt?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editingReceipt?.date ?? new Date().toISOString().slice(0,10)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select key={(editingReceipt?.id ?? "new") + "-tp"} name="tp" value={freeReceiptTp} onValueChange={setFreeReceiptTp}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="Full Payment">Full Payment</SelectItem>
                    <SelectItem value="Partial Payment">Partial Payment</SelectItem>
                    <SelectItem value="Advance mobilisation">Advance mobilisation</SelectItem>
                    <SelectItem value="Advance">Advance</SelectItem>
                    <SelectItem value="Retention Release">Retention Release</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select key={(editingReceipt?.id ?? "new") + "-mth"} name="method" defaultValue={editingReceipt?.method ?? "Bank Transfer"}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select method…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                    <SelectItem value="Letter of Credit">Letter of Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes / Bank Ref</Label>
              <Input key={editingReceipt?.id + "desc"} name="desc" placeholder="Bank ref, cheque no., etc." className="rounded-xl" defaultValue={editingReceipt?.desc ?? ""} />
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl" disabled={createReceipt.isPending || updateReceipt.isPending}>
              {(createReceipt.isPending || updateReceipt.isPending) ? "Saving..." : editingReceipt ? "Save Changes" : "Record Receipt"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipts tab */}
      {subTab === 'rec' && (
        <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow><TableHead>Receipt #</TableHead><TableHead>Invoice Ref</TableHead><TableHead>Description</TableHead><TableHead>Type</TableHead><TableHead>Method</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {receipts.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No receipts recorded yet. Click "New Receipt" to add one.</TableCell></TableRow>}
              {receipts.map(r => (
                <TableRow key={r.id} className="group">
                  <TableCell className="font-medium">{r.id}</TableCell>
                  <TableCell className="font-mono text-xs">{r.invRef}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-muted-foreground">{r.desc || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{r.tp || 'Standard'}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{r.method || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{r.date || '—'}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">{fc(r.amount)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100">
                      {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _c = customers.find((x: Customer) => x.id === r.customerId); setPrintState({ doc: { ...r as unknown as Record<string, unknown>, partyPhone: _c?.phone ?? "", partyEmail: _c?.email ?? "", partyCountry: _c?.country ?? "", partyVatNo: _c?.vatNo ?? "", partyContact: _c?.contact ?? "" }, type: 'client-receipt' }); }}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditReceipt(r)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete Receipt', message: 'Delete this receipt? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { try { await delReceipt.mutateAsync({ id: r.id }); queryClient.invalidateQueries({ queryKey: getListClientReceiptsQueryKey({ projectId: project.id }) }); queryClient.invalidateQueries({ queryKey: getListClientReceiptsQueryKey({ companyId: project.companyId }) }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
    {printState && (
      <DocPrintView
        open
        onClose={() => setPrintState(null)}
        printDocType={printState.type}
        doc={printState.doc}
        company={company}
        project={project}
        currency={cur}
      />
    )}
    </>
  );
}

function SuppliersTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const { taxLabel, taxRate: companyTaxRate, taxTypes } = useContext(CompanyTaxContext);
  const company = useContext(CompanyContext);
  const [printState, setPrintState] = useState<{ doc: Record<string, unknown>; type: PrintDocType } | null>(null);
  const projVatRate = N(project.vatRate ?? "0");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data: invoices = [] } = useListSupplierInvoices({ projectId: project.id });
  const { data: payments = [] } = useListSupplierPayments({ projectId: project.id });
  const createInv = useCreateSupplierInvoice();
  const updateInv = useUpdateSupplierInvoice();
  const delInv = useDeleteSupplierInvoice();
  const createPmt = useCreateSupplierPayment();
  const updatePmt = useUpdateSupplierPayment();
  const delPmt = useDeleteSupplierPayment();
  const { data: suppList = [] } = useListSuppliers({ projectId: project.id });
  const createSupp = useCreateSupplier();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierInvoice | null>(null);
  const [selectedSupp, setSelectedSupp] = useState<string>("");
  const [suppPoIds, setSuppPoIds] = useState<string[]>([]);
  const [suppBudgetItems, setSuppBudgetItems] = useState<string[]>([]);
  const [suppInvoiceType, setSuppInvoiceType] = useState<string>("");
  const [suppVatRatePct, setSuppVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [suppDeds, setSuppDeds] = useState<{desc: string; val: string; position: 'before_vat'|'after_vat'}[]>([]);
  const [suppCnItems, setSuppCnItems] = useState<{ref: string; amt: string; tax: string}[]>([]);
  const [pmtInv, setPmtInv] = useState<SupplierInvoice | null>(null);
  const [editingPmt, setEditingPmt] = useState<SupplierPayment | null>(null);
  const [pmtError, setPmtError] = useState<string | null>(null);
  const [subTab, setSubTab] = useState<'inv'|'pmt'|'po'>('inv');
  const [freePmtMode, setFreePmtMode] = useState(false);
  const [freeSuppPmtInvRefs, setFreeSuppPmtInvRefs] = useState<string[]>([]);
  const pmtDialogOpen = !!pmtInv || !!editingPmt || freePmtMode;
  const openEditPmt = (p: SupplierPayment) => { setEditingPmt(p); setPmtInv(null); setFreePmtMode(false); setPmtError(null); };
  const openFreePmt = () => { setPmtInv(null); setEditingPmt(null); setFreePmtMode(true); setPmtError(null); setFreeSuppPmtInvRefs([]); };
  const { data: vendorOrders = [] } = useListVendorOrders({ projectId: project.id });
  const supplierOrders: VendorOrder[] = vendorOrders.filter((o: VendorOrder) => o.vendorType === 'supplier');
  const { data: budgetLines = [] } = useListBudget({ projectId: project.id });
  const createVO = useCreateVendorOrder();
  const updateVO = useUpdateVendorOrder();
  const delVO = useDeleteVendorOrder();
  const [poOpen, setPoOpen] = useState(false);
  const [editingPo, setEditingPo] = useState<VendorOrder | null>(null);
  const [poVatRatePct, setPoVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [poContractedValue, setPoContractedValue] = useState<string>("");
  const [poIsDescope, setPoIsDescope] = useState(false);
  const [poSuppVendorId, setPoSuppVendorId] = useState<string>("");
  const [poSuppDocType, setPoSuppDocType] = useState<string>("po");
  const [poLineItems,    setPoLineItems]    = useState<LineItem[]>([]);
  const [poLiMode,       setPoLiMode]       = useState<"free" | "budget">("free");
  const [suppInvLineItems, setSuppInvLineItems] = useState<LineItem[]>([]);
  const [suppInvLiMode,  setSuppInvLiMode]  = useState<"free" | "budget">("free");
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const lineItemDocs = (project.lineItemDocs as Record<string, boolean> | null) ?? {};
  const itemSourceMode = ((project.itemSourceMode as string | null) ?? 'free') as 'free' | 'boq' | 'budget';
  const suppPoLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.supplierPO;
  const suppInvLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.supplierInvoice;
  const openNewPo = () => { setEditingPo(null); setPoVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate)); setPoContractedValue(""); setPoIsDescope(false); setPoSuppVendorId(""); setPoSuppDocType("po"); setPoLineItems([]); setPoLiMode("free"); setPoOpen(true); };
  const openEditPo = (o: VendorOrder) => {
    setEditingPo(o);
    setPoVatRatePct(o.vatRate ? String((N(o.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setPoContractedValue(o.contractedValue ?? "");
    setPoIsDescope(o.docType === 'descope');
    setPoSuppVendorId(o.vendorId ?? "");
    const poItems = (o.lineItems as LineItem[] | null) ?? [];
    setPoLineItems(poItems);
    setPoLiMode(poItems.some(i => i.sourceType === "budget") ? "budget" : "free");
    setPoOpen(true);
  };
  const ensureProjectSupplier = async (virtualId: string): Promise<string> => {
    if (!virtualId.startsWith("master:")) return virtualId;
    const masterId = virtualId.replace("master:", "");
    const masterSupp = suppList.find((s: Supplier) => s.id === virtualId);
    if (!masterSupp) return virtualId;
    const existing = suppList.find((s: Supplier) => s.masterId === masterId && !s.id.startsWith("master:"));
    if (existing) return existing.id;
    const newId = `SUP-${uid()}`;
    await createSupp.mutateAsync({ data: {
      id: newId, projectId: project.id, companyId: project.companyId,
      name: masterSupp.name, cat: masterSupp.cat ?? "", masterId,
    }});
    queryClient.invalidateQueries({ queryKey: ["/api/suppliers"] });
    return newId;
  };
  const handlePoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const dt = (fd.get("docType") as string) || "po";
    const rawVendorId = poSuppVendorId;
    const selectedVendorId = rawVendorId.startsWith("master:") ? await ensureProjectSupplier(rawVendorId) : rawVendorId;
    const selectedVendor = suppList.find((s: Supplier) => s.id === rawVendorId) ?? suppList.find((s: Supplier) => s.id === selectedVendorId);
    const vatPct = N(poVatRatePct) / 100;
    const netVal = N(poContractedValue);
    if (dt === 'descope' && netVal >= 0) {
      toast({ title: "Invalid amount", description: "Descope documents must have a negative contracted value.", variant: "destructive" });
      return;
    }
    if (dt !== 'descope' && netVal < 0) {
      toast({ title: "Invalid amount", description: "Only Descope documents can have a negative contracted value.", variant: "destructive" });
      return;
    }
    const poLiEnabled = suppPoLineItemsEnabled && poLineItems.length > 0;
    const effectiveNet = poLiEnabled ? lineItemsNetTotal(poLineItems) : netVal;
    const computedVatAmt = poLiEnabled ? lineItemsTaxTotal(poLineItems) : effectiveNet * vatPct;
    const effectiveVatRate = poLiEnabled ? 0 : vatPct;
    const data = {
      vendorId: selectedVendorId || null,
      vendorName: selectedVendor?.name ?? "",
      docType: dt as CreateVendorOrderBodyDocType,
      refNumber: (fd.get("refNumber") as string) || "",
      description: (fd.get("description") as string) || "",
      date: (fd.get("date") as string) || "",
      contractedValue: String(effectiveNet),
      vatRate: String(effectiveVatRate),
      vatAmount: String(computedVatAmt),
      supplierQtNumber: (fd.get("supplierQtNumber") as string) || null,
      budgetLineId: (() => { const v = fd.get("budgetLineId") as string; return v && v !== "none" ? v : null; })(),
      status: ((fd.get("status") as string) || "Draft") as CreateVendorOrderBodyStatus,
      ...(suppPoLineItemsEnabled ? { lineItems: poLineItems } : {}),
    };
    try {
      if (editingPo) {
        await updateVO.mutateAsync({ id: editingPo.id, data });
      } else {
        await createVO.mutateAsync({ data: { id: `VO-${uid()}`, projectId: project.id, companyId: project.companyId, vendorType: 'supplier' as const, ...data } });
      }
      queryClient.invalidateQueries({ queryKey: getListVendorOrdersQueryKey({ projectId: project.id }) });
      setPoOpen(false);
      setEditingPo(null);
      setPoLineItems([]);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { error?: string }; message?: string };
      if (apiErr?.status === 409 || apiErr?.data?.error?.includes("already in use") || apiErr?.message?.includes("already in use")) {
        const msg = apiErr?.data?.error || "This reference number is already in use. Please choose a different one.";
        toast({ title: "Duplicate ref number", description: msg, variant: "destructive" });
        return;
      }
      throw err;
    }
  };
  const getSuppVatRate = (inv: SupplierInvoice) => N(inv.vatRate ?? 0);
  const totalAmt = invoices.reduce((s, i) => s + calcSI(i, payments, getSuppVatRate(i)).net, 0);
  const totalPaid = invoices.reduce((s, i) => s + calcSI(i, payments, getSuppVatRate(i)).paid, 0);

  const openNew  = () => { setEditing(null); setSelectedSupp(""); setSuppPoIds([]); setSuppBudgetItems([]); setSuppInvoiceType(""); setSuppVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate)); setSuppDeds([]); setSuppCnItems([]); setSuppInvLineItems([]); setSuppInvLiMode("free"); setIsOpen(true); };
  const defaultSuppCnTax = getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate);
  const openEdit = (inv: SupplierInvoice) => {
    setEditing(inv);
    setSelectedSupp(inv.suppId || "");
    setSuppPoIds((inv.poIds as string[] | null | undefined) ?? (inv.po ? [inv.po] : []));
    setSuppBudgetItems((inv.budgetItems as string[] | null | undefined) ?? []);
    setSuppInvoiceType(inv.invoiceType || "");
    setSuppVatRatePct(inv.vatRate ? String((N(inv.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    const sItems = (inv.lineItems as LineItem[] | null) ?? [];
    setSuppInvLineItems(sItems);
    setSuppInvLiMode(sItems.some(i => i.sourceType === "budget") ? "budget" : "free");
    setSuppDeds((inv.deds as {desc:string;val:string;position?:string}[] | undefined || []).map(d => ({desc: d.desc, val: String(d.val), position: (d.position ?? 'after_vat') as 'before_vat'|'after_vat'})));
    const existingCnItems = (inv.cnItems as {ref:string;amt:number;taxPct?:number}[] | undefined);
    if (existingCnItems && existingCnItems.length > 0) {
      setSuppCnItems(existingCnItems.map(c => ({ ref: c.ref, amt: String(c.amt), tax: c.taxPct != null ? String((c.taxPct * 100).toFixed(4)) : '' })));
    } else if (inv.cnAmt && inv.cnAmt !== "0") {
      setSuppCnItems([{ ref: inv.cnRef || '', amt: inv.cnAmt, tax: '' }]);
    } else {
      setSuppCnItems([]);
    }
    setIsOpen(true);
  };

  const handlePmtSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPmtError(null);
    const fd = new FormData(e.currentTarget);
    const fields = {
      amount: fd.get("amount") as string,
      date: fd.get("date") as string,
      tp: fd.get("tp") as string || "Standard",
      method: fd.get("method") as string,
      refNumber: (fd.get("refNumber") as string) || "",
    };
    try {
      if (editingPmt) {
        await updatePmt.mutateAsync({ id: editingPmt.id, data: fields });
      } else {
        const invRef = pmtInv ? pmtInv.id : freeSuppPmtInvRefs[0] || "";
        const invRefs = pmtInv ? [pmtInv.id] : freeSuppPmtInvRefs;
        const suppNameVal = pmtInv ? (pmtInv.suppName || "") : (invoices.find((i: SupplierInvoice) => i.id === invRef)?.suppName || "");
        if (!invRef) return;
        await createPmt.mutateAsync({ data: {
          id: `SP-${uid()}`, projectId: project.id, companyId: project.companyId,
          invRef, invRefs, suppName: suppNameVal, ...fields,
        }});
      }
    } catch (err: unknown) {
      const msg = (err as { data?: { error?: string } })?.data?.error ?? (err as Error)?.message ?? "Failed to save payment.";
      setPmtError(msg);
      toast({ title: "Overpayment not allowed", description: msg, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: getListSupplierPaymentsQueryKey({ projectId: project.id }) });
    queryClient.invalidateQueries({ queryKey: getListSupplierPaymentsQueryKey({ companyId: project.companyId }) });
    setPmtInv(null);
    setEditingPmt(null);
    setFreePmtMode(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const rawSelectedSupp = selectedSupp && selectedSupp !== "__custom__"
      ? (selectedSupp.startsWith("master:") ? await ensureProjectSupplier(selectedSupp) : selectedSupp)
      : null;
    const chosenSupp = suppList.find((s: Supplier) => s.id === selectedSupp) ?? suppList.find((s: Supplier) => s.id === rawSelectedSupp);
    const suppId = rawSelectedSupp || `SP-${uid()}`;
    const suppName = chosenSupp?.name || (fd.get("suppNameFree") as string) || "";
    const suppLiEnabled = suppInvLineItemsEnabled && suppInvLineItems.length > 0;
    const grossVal = suppLiEnabled ? String(lineItemsNetTotal(suppInvLineItems)) : (fd.get("gross") as string) || editing?.gross || "0";
    const vatRateVal = suppLiEnabled ? "0" : String(N(suppVatRatePct) / 100);
    const data = {
      suppId,
      suppName,
      cat: (fd.get("cat") ?? "") as string,
      po: suppPoIds[0] || "",
      poIds: suppPoIds,
      budgetItems: suppBudgetItems,
      date: (fd.get("date") ?? "") as string,
      gross: grossVal,
      refNumber: (fd.get("refNumber") as string) || "",
      vatRate: vatRateVal,
      invoiceType: suppInvoiceType || null,
      cnItems: suppCnItems.filter(c => c.amt).map(c => ({ ref: c.ref, amt: Number(c.amt), ...(c.tax ? { taxPct: N(c.tax) / 100 } : {}) })) as CnItem[],
      deds: suppDeds.filter(d => d.desc && d.val).map((d, i) => ({ id: `d${i}`, desc: d.desc, tp: "Additional", val: Number(d.val), position: d.position })),
      ...(suppInvLineItemsEnabled ? { lineItems: suppInvLineItems } : {}),
    };
    if (editing) {
      await updateInv.mutateAsync({ id: editing.id, data });
    } else {
      const id = `SI-${uid()}`;
      await createInv.mutateAsync({ data: { id, projectId: project.id, companyId: project.companyId, ...data, suppId: data.suppId || id } });
    }
    queryClient.invalidateQueries({ queryKey: getListSupplierInvoicesQueryKey() });
    setIsOpen(false);
    setEditing(null);
    setSuppDeds([]);
    setSuppCnItems([]);
    setSuppInvoiceType("");
    setSuppPoIds([]);
    setSuppBudgetItems([]);
    setSuppInvLineItems([]);
  };

  return (
    <>
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Suppliers</h2>
          <p className="text-muted-foreground text-sm mt-1">Vendor invoices, VAT & payments</p>
        </div>
        {subTab === 'inv' ? (
          <Button className="rounded-xl font-medium" onClick={openNew}><Plus className="w-4 h-4 mr-2"/>Log Supplier Invoice</Button>
        ) : subTab === 'pmt' ? (
          <Button className="rounded-xl font-medium" onClick={openFreePmt}><Plus className="w-4 h-4 mr-2"/>New Payment</Button>
        ) : subTab === 'po' ? (
          <Button onClick={openNewPo} className="rounded-xl font-medium"><Plus className="w-4 h-4 mr-2" /> New PO / VO</Button>
        ) : null}
        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${suppInvLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[560px]'}`}>
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Supplier Invoice" : "Log Supplier Invoice"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <RefNumberInput
                  enabled={canAccess('docSequencing')}
                key={(editing?.id ?? "new") + "ref-si"}
                docType="supplier-invoice"
                companyId={project.companyId}
                projectId={project.id}
                editingValue={editing?.refNumber ?? ""}
              />
              <div className="space-y-2">
                <Label>Supplier *</Label>
                {suppList.length > 0 ? (
                  <Select value={selectedSupp || undefined} onValueChange={setSelectedSupp}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select supplier…" /></SelectTrigger>
                    <SelectContent>
                      {suppList.map((s: Supplier) => <SelectItem key={s.id} value={s.id}>{s.name}{s.cat ? ` — ${s.cat}` : ""}</SelectItem>)}
                      <SelectItem value="__custom__">— Enter manually —</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input key={editing?.id + "sn"} name="suppNameFree" required placeholder="e.g., ABC Supplies Ltd" className="rounded-xl" defaultValue={editing?.suppName ?? ""} />
                )}
                {selectedSupp === "__custom__" && (
                  <Input name="suppNameFree" required placeholder="Enter supplier name" className="rounded-xl mt-2" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PO / VO Reference(s)</Label>
                  {supplierOrders.length > 0 ? (
                    <MultiPicker
                      placeholder="Link PO(s)…"
                      options={supplierOrders.map((o: VendorOrder) => ({ id: o.refNumber, label: `${o.refNumber} — ${o.vendorName || o.description || 'N/A'}` }))}
                      selected={suppPoIds}
                      onChange={setSuppPoIds}
                    />
                  ) : (
                    <Input key={editing?.id + "p"} placeholder="PO-001" className="rounded-xl" value={suppPoIds[0] ?? ""} onChange={e => setSuppPoIds(e.target.value ? [e.target.value] : [])} />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input key={editing?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editing?.date ?? new Date().toISOString().slice(0,10)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Linked Budget Items</Label>
                <MultiPicker
                  placeholder={budgetLines.length === 0 ? "No budget items — add in Budget tab first" : "Link budget item(s)…"}
                  options={budgetLines.map((b: BudgetLine) => ({ id: b.id, label: `${b.wbs ? b.wbs + ' ' : ''}${b.desc ?? b.id}`.slice(0, 50) }))}
                  selected={suppBudgetItems}
                  onChange={setSuppBudgetItems}
                />
              </div>
              {suppInvLineItemsEnabled ? (
                <LineItemsEditor
                  projectId={project.id}
                  value={suppInvLineItems}
                  onChange={setSuppInvLineItems}
                  sourceMode={suppInvLiMode}
                  onSourceModeChange={v => setSuppInvLiMode(v as "free" | "budget")}
                  availableModes={["free", "budget"]}
                  taxTypes={taxTypes}
                  taxLabel={taxLabel}
                />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gross Amount *</Label>
                    <Input key={editing?.id + "g"} type="number" name="gross" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.gross ?? ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>{taxLabel} Rate</Label>
                    <TaxRatePicker value={suppVatRatePct} onChange={setSuppVatRatePct} />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Invoice Type</Label>
                <Select value={suppInvoiceType || undefined} onValueChange={setSuppInvoiceType}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supplying">Supplying</SelectItem>
                    <SelectItem value="Installation">Installation</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                  </SelectContent>
                </Select>
                {suppInvoiceType && ({
                  'Supplying':    'Materials, equipment, or goods only — no installation labor.',
                  'Installation': 'Labor and installation services only — no materials.',
                  'Both':         'Covers both supplied materials and installation services.',
                } as Record<string,string>)[suppInvoiceType] && (
                  <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-2.5 py-1.5 leading-snug">
                    {({
                      'Supplying':    'Materials, equipment, or goods only — no installation labor.',
                      'Installation': 'Labor and installation services only — no materials.',
                      'Both':         'Covers both supplied materials and installation services.',
                    } as Record<string,string>)[suppInvoiceType]}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Credit Notes</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setSuppCnItems(d => [...d, {ref:"",amt:"",tax:defaultSuppCnTax}])}>+ Add Credit Note</Button>
                </div>
                {suppCnItems.length > 0 && (
                  <div className="grid gap-1 text-[10px] text-muted-foreground font-medium px-1" style={{gridTemplateColumns:'1fr 120px 130px 32px'}}>
                    <span>Reference</span><span>Amount</span><span>{taxLabel} Rate</span><span/>
                  </div>
                )}
                {suppCnItems.map((c, i) => (
                  <div key={i} className="grid gap-2 items-center" style={{gridTemplateColumns:'1fr 120px 130px 32px'}}>
                    <Input placeholder="CN Reference" className="rounded-xl" value={c.ref} onChange={e => setSuppCnItems(cs => cs.map((x,j) => j===i ? {...x,ref:e.target.value} : x))} />
                    <Input type="number" placeholder="Amount" className="rounded-xl" min="0" step="0.01" value={c.amt} onChange={e => setSuppCnItems(cs => cs.map((x,j) => j===i ? {...x,amt:e.target.value} : x))} />
                    <TaxRatePicker value={c.tax || ''} onChange={v => setSuppCnItems(cs => cs.map((x,j) => j===i ? {...x,tax:v} : x))} />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setSuppCnItems(cs => cs.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {suppCnItems.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No credit notes</div>}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Additional Deductions</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setSuppDeds(d => [...d, {desc:"",val:"",position:"after_vat"}])}>+ Add Deduction</Button>
                </div>
                {suppDeds.map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Description" className="rounded-xl flex-1" value={d.desc} onChange={e => setSuppDeds(ds => ds.map((x,j) => j===i ? {...x,desc:e.target.value} : x))} />
                    <Input type="number" placeholder="Amount" className="rounded-xl w-28" min="0" step="0.01" value={d.val} onChange={e => setSuppDeds(ds => ds.map((x,j) => j===i ? {...x,val:e.target.value} : x))} />
                    <select value={d.position} onChange={e => setSuppDeds(ds => ds.map((x,j) => j===i ? {...x,position:e.target.value as 'before_vat'|'after_vat'} : x))} style={{fontSize:11,padding:'4px 6px',borderRadius:6,border:'1px solid #d1d5db',background:'#fff',flexShrink:0}}>
                      <option value="after_vat">After VAT</option>
                      <option value="before_vat">Before VAT</option>
                    </select>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setSuppDeds(ds => ds.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {suppDeds.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No additional deductions</div>}
              </div>
              {editing?.id && (
                <AttachmentPanel
                  entityType="supplier_invoice"
                  entityId={editing.id}
                  companyId={project.companyId}
                  enabled={canAccess('attachments')}
                />
              )}
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createInv.isPending || updateInv.isPending}>
                {createInv.isPending || updateInv.isPending ? "Saving..." : editing ? "Save Changes" : "Save Invoice"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sub-tab bar */}
      <div style={{display:"flex",gap:2,background:"#f3f4f6",borderRadius:7,padding:3,overflowX:"auto",maxWidth:"100%"}}>
        {([['po','POs'],['inv','Supplier Invoices'],['pmt','Payments']] as const).map(([id,label])=>(
          <button key={id} onClick={()=>{ setSubTab(id); if(id!=='po'){setPoOpen(false);setEditingPo(null);} }} style={{padding:'5px 16px',borderRadius:5,border:'none',fontSize:12,fontWeight:500,background:subTab===id?'#1a2332':'transparent',color:subTab===id?'#fff':'#6b7280',cursor:'pointer',transition:'all .15s'}}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'po' && (
        <div className="space-y-4">
          <Dialog open={poOpen} onOpenChange={v => { setPoOpen(v); if (!v) setEditingPo(null); }}>
            <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${suppPoLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[700px]'}`}>
              <DialogHeader><DialogTitle className="font-display text-xl">{editingPo ? "Edit Supplier PO / VO" : "New Supplier PO / VO"}</DialogTitle></DialogHeader>
              <form onSubmit={handlePoSubmit} className="space-y-4 pt-2">
                {/* Row 1: Type + Ref Number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select name="docType" value={editingPo ? (editingPo.docType ?? "po") : poSuppDocType} onValueChange={v => { setPoIsDescope(v === 'descope'); setPoSuppDocType(v); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="po">Purchase Order</SelectItem>
                        <SelectItem value="vo">Variation Order</SelectItem>
                        <SelectItem value="descope">Descope (Scope Reduction)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <RefNumberInput
                  enabled={canAccess('docSequencing')}
                    key={(editingPo?.id ?? "new") + "-" + (editingPo ? (editingPo.docType ?? "po") : poSuppDocType)}
                    docType={(() => { const dt = editingPo ? (editingPo.docType ?? "po") : poSuppDocType; return dt === 'po' ? 'sp-po' : dt === 'vo' ? 'sp-vo' : dt === 'descope' ? 'sp-ds' : dt; })()}
                    companyId={project.companyId}
                    projectId={project.id}
                    editingValue={editingPo?.refNumber ?? undefined}
                  />
                </div>
                {/* Row 2: Supplier (full width) */}
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={poSuppVendorId || undefined} onValueChange={setPoSuppVendorId}>
                    <SelectTrigger><SelectValue placeholder="Select a supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppList.map((s: Supplier) => <SelectItem key={s.id} value={s.id}>{s.name}{s.id.startsWith("master:") ? " ★" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Row 3: Description + Supplier QT Number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input name="description" placeholder="Brief description" className="rounded-xl" defaultValue={editingPo?.description ?? ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier QT Number</Label>
                    <Input name="supplierQtNumber" placeholder="Supplier's reference" className="rounded-xl font-mono" defaultValue={editingPo?.supplierQtNumber ?? ""} />
                  </div>
                </div>
                {/* Row 4: Date */}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input name="date" type="date" className="rounded-xl" defaultValue={editingPo?.date ?? ""} />
                </div>
                {/* Row 4b: Net Value + VAT — or Line Items */}
                {suppPoLineItemsEnabled ? (
                  <LineItemsEditor
                    projectId={project.id}
                    value={poLineItems}
                    onChange={setPoLineItems}
                    sourceMode={poLiMode}
                    onSourceModeChange={v => setPoLiMode(v as "free" | "budget")}
                    availableModes={["free", "budget"]}
                    taxTypes={taxTypes}
                    taxLabel={taxLabel}
                  />
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Net Value (excl. {taxLabel}){poIsDescope ? ' — neg.' : ''}</Label>
                        <Input type="number" step="0.01" className="rounded-xl" value={poContractedValue} onChange={e => setPoContractedValue(e.target.value)} placeholder={poIsDescope ? '-0.00' : '0.00'} />
                      </div>
                      <div className="space-y-2">
                        <Label>{taxLabel} Rate</Label>
                        <TaxRatePicker value={poVatRatePct} onChange={setPoVatRatePct} />
                      </div>
                    </div>
                    {(N(poContractedValue) !== 0 || N(poVatRatePct) > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm space-y-1">
                        <div className="flex justify-between text-muted-foreground"><span>Net (excl. {taxLabel})</span><span>{fc(N(poContractedValue))}</span></div>
                        <div className="flex justify-between text-blue-700"><span>{taxLabel} ({poVatRatePct}%)</span><span>{fc(N(poContractedValue) * N(poVatRatePct) / 100)}</span></div>
                        <div className="flex justify-between font-bold border-t border-blue-200 pt-1"><span>Gross Total (incl. {taxLabel})</span><span>{fc(N(poContractedValue) * (1 + N(poVatRatePct) / 100))}</span></div>
                      </div>
                    )}
                  </>
                )}
                {/* Row 5: Budget Line + Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget Line (optional)</Label>
                    <Select name="budgetLineId" defaultValue={editingPo?.budgetLineId ?? "none"}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {budgetLines.map(bl => <SelectItem key={bl.id} value={bl.id}>{bl.cat} — {bl.desc}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingPo?.status ?? "Draft"}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Draft">Draft</SelectItem>
                        <SelectItem value="Issued">Issued</SelectItem>
                        <SelectItem value="PartialDelivery">Partial Delivery</SelectItem>
                        <SelectItem value="Fulfilled">Fulfilled</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 rounded-xl">{editingPo ? "Save Changes" : "Create PO / VO"}</Button>
              </form>
            </DialogContent>
          </Dialog>
          {(() => {
            const committed = supplierOrders.reduce((s: number, o: VendorOrder) => s + N(o.contractedValue), 0);
            const invoiced = invoices.reduce((s, i) => s + N(i.gross), 0);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Committed (PO Value)</div>
                  <div className="text-2xl font-bold">{fc(committed)}</div>
                </div>
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Invoiced</div>
                  <div className="text-2xl font-bold text-blue-600">{fc(invoiced)}</div>
                </div>
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Remaining Commitment</div>
                  <div className="text-2xl font-bold text-amber-600">{fc(committed - invoiced)}</div>
                </div>
              </div>
            );
          })()}
          <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">Ref Number</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Supplier</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Supplier QT No</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="text-right font-semibold">Net Value</TableHead>
                  <TableHead className="text-right font-semibold text-blue-700">{taxLabel}</TableHead>
                  <TableHead className="text-right font-semibold">Gross (incl. {taxLabel})</TableHead>
                  <TableHead className="text-right font-semibold">Invoiced</TableHead>
                  <TableHead className="text-right font-semibold">Balance</TableHead>
                  <TableHead className="text-center font-semibold">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {supplierOrders.length === 0 && (
                  <TableRow><TableCell colSpan={13} className="text-center py-12 text-muted-foreground">No purchase orders yet. Click "New PO / VO" above.</TableCell></TableRow>
                )}
                {supplierOrders.map((ord: VendorOrder) => {
                  const invTotal = invoices.filter(i => i.po === ord.refNumber).reduce((s, i) => s + N(i.gross), 0);
                  const ordVatAmt = N(ord.vatAmount ?? "0");
                  const ordGrossInclVat = N(ord.contractedValue) + ordVatAmt;
                  const balance = ordGrossInclVat - invTotal;
                  const stBg = ord.status === 'Issued' ? 'bg-blue-100 text-blue-700' : ord.status === 'Fulfilled' ? 'bg-emerald-100 text-emerald-700' : ord.status === 'PartialDelivery' ? 'bg-amber-100 text-amber-700' : ord.status === 'Cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-muted text-muted-foreground';
                  return (
                    <TableRow key={ord.id} className="group hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium font-mono text-sm">{ord.refNumber}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{ord.docType === 'po' ? 'PO' : ord.docType === 'descope' ? 'Descope' : 'VO'}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{ord.vendorName || '—'}</TableCell>
                      <TableCell className="max-w-[140px] truncate text-sm" title={ord.description}>{ord.description || '—'}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{ord.supplierQtNumber || '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{ord.date || '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(ord.contractedValue)}</TableCell>
                      <TableCell className="text-right text-blue-600">{ordVatAmt > 0 ? fc(ordVatAmt) : '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(ordGrossInclVat)}</TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">{fc(invTotal)}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-600">{fc(balance)}</TableCell>
                      <TableCell className="text-center"><Badge className={`${stBg} border-none text-xs`}>{ord.status === 'PartialDelivery' ? 'Partial Delivery' : ord.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _s = suppList.find((x: { id: string; cat?: string }) => x.id === ord.vendorId); setPrintState({ doc: { ...ord as unknown as Record<string, unknown>, partySubtitle: _s?.cat ?? "" }, type: 'sp-po' }); }}>
                            <Printer className="w-3.5 h-3.5" />
                          </Button>}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditPo(ord)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600" onClick={async () => {
                            if (await confirm({ title: 'Delete Order', message: 'Delete this order? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) {
                              try {
                                await delVO.mutateAsync({ id: ord.id });
                                queryClient.invalidateQueries({ queryKey: getListVendorOrdersQueryKey({ projectId: project.id }) });
                              } catch (err: unknown) {
                                const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
                                toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
                              }
                            }
                          }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {subTab === 'inv' && <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <KpiCard title="Total Net Payable (incl. VAT)" value={fc(totalAmt)} icon={Package} />
        <KpiCard title="Total Paid" value={fc(totalPaid)} icon={DollarSign} trend="up" />
        <KpiCard title="Outstanding Balance" value={fc(totalAmt - totalPaid)} icon={Briefcase} trend={totalAmt - totalPaid > 0 ? "down" : "up"} />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Inv #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>PO</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">{taxLabel}</TableHead>
              <TableHead className="text-right">Total+{taxLabel}</TableHead>
              <TableHead className="text-right">CN</TableHead>
              <TableHead className="text-right">Net Payable</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={15} className="text-center py-12 text-muted-foreground">No supplier invoices found. Log one above.</TableCell></TableRow>
            )}
            {invoices.map(inv => {
              const gr = N(inv.gross);
              const invVatRate = getSuppVatRate(inv);
              const { va, tot, net, paid, bal, cn } = calcSI(inv, payments, invVatRate);
              const st = stBadge(bal, paid);
              return (
                <TableRow key={inv.id} className="group">
                  <TableCell className="font-medium">{inv.id}</TableCell>
                  <TableCell>{inv.suppName}</TableCell>
                  <TableCell className="text-muted-foreground">{inv.cat || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{inv.invoiceType || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{inv.po || "—"}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">{inv.date || "—"}</TableCell>
                  <TableCell className="text-right">{fc(gr)}</TableCell>
                  <TableCell className="text-right text-blue-600">{invVatRate > 0 ? fc(va) : "—"}</TableCell>
                  <TableCell className="text-right">{invVatRate > 0 ? fc(tot) : fc(net)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{cn > 0 ? fc(cn) : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{fc(net)}</TableCell>
                  <TableCell className="text-right text-emerald-600 font-medium">{fc(paid)}</TableCell>
                  <TableCell className="text-right font-bold">{fc(bal)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${st.bg} ${st.cl} border-none`}>{st.lb}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Record Payment" onClick={() => { setPmtInv(inv); setFreePmtMode(false); setPmtError(null); }}>
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _s = suppList.find((x: { id: string; cat?: string }) => x.id === inv.vendorId); setPrintState({ doc: { ...inv as unknown as Record<string, unknown>, partySubtitle: _s?.cat ?? "" }, type: 'sp-invoice' }); }}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(inv)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete Invoice', message: 'Delete this invoice? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { try { await delInv.mutateAsync({ id: inv.id }); queryClient.invalidateQueries({ queryKey: getListSupplierInvoicesQueryKey() }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed. This invoice may have payments linked to it."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      </>}

      {/* Record / Edit Supplier Payment Dialog */}
      <Dialog open={pmtDialogOpen} onOpenChange={open => { if (!open) { setPmtInv(null); setEditingPmt(null); setFreePmtMode(false); } }}>
        <DialogContent className="max-w-full sm:max-w-[440px] rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-lg">{editingPmt ? "Edit Payment" : pmtInv ? `Record Payment — ${pmtInv.id}` : "New Supplier Payment"}</DialogTitle></DialogHeader>
          <form onSubmit={handlePmtSubmit} className="space-y-3 pt-2">
            {pmtError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{pmtError}</div>
            )}
            <RefNumberInput
                  enabled={canAccess('docSequencing')}
              key={(editingPmt?.id ?? "new") + "ref-sp"}
              docType="supplier-payment"
              companyId={project.companyId}
              projectId={project.id}
              editingValue={editingPmt?.refNumber ?? ""}
            />
            {freePmtMode && !editingPmt && (
              <div className="space-y-1.5">
                <Label>Supplier Invoice(s) *</Label>
                <MultiPicker
                  placeholder="Select invoice(s)…"
                  options={invoices.map((i: SupplierInvoice) => ({ id: i.id, label: `${i.id} — ${i.suppName} — ${(i.cat || '').slice(0, 25)}` }))}
                  selected={freeSuppPmtInvRefs}
                  onChange={setFreeSuppPmtInvRefs}
                />
                {freeSuppPmtInvRefs.length === 0 && <p className="text-xs text-muted-foreground">Select at least one invoice</p>}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount *</Label>
                <Input key={editingPmt?.id + "amt"} type="number" name="amount" required min="0" step="0.01" placeholder="0.00" className="rounded-xl" defaultValue={editingPmt?.amount ?? ""} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input key={editingPmt?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editingPmt?.date ?? new Date().toISOString().slice(0,10)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select key={(editingPmt?.id ?? "new") + "-tp"} name="tp" defaultValue={editingPmt?.tp ?? "Standard"}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="Partial Payment">Partial Payment</SelectItem>
                    <SelectItem value="Advance">Advance</SelectItem>
                    <SelectItem value="Final">Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select key={(editingPmt?.id ?? "new") + "-mth"} name="method" defaultValue={editingPmt?.method ?? "Bank Transfer"}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select method…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl" disabled={createPmt.isPending || updatePmt.isPending}>
              {(createPmt.isPending || updatePmt.isPending) ? "Saving..." : editingPmt ? "Save Changes" : "Record Payment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payments sub-tab */}
      {subTab === 'pmt' && (
        <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow><TableHead>Pmt #</TableHead><TableHead>Supplier Invoice</TableHead><TableHead>Supplier</TableHead><TableHead>Type</TableHead><TableHead>Method</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No payments recorded yet. Click "New Payment" above.</TableCell></TableRow>}
              {payments.map(p => (
                <TableRow key={p.id} className="group">
                  <TableCell className="font-medium">{p.id}</TableCell>
                  <TableCell className="font-mono text-xs">{p.invRef}</TableCell>
                  <TableCell>{p.suppName || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{p.tp || 'Standard'}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{p.method || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{p.date || '—'}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-600">{fc(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditPmt(p)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete Payment', message: 'Delete this payment? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { try { await delPmt.mutateAsync({ id: p.id }); queryClient.invalidateQueries({ queryKey: getListSupplierPaymentsQueryKey({ projectId: project.id }) }); queryClient.invalidateQueries({ queryKey: getListSupplierPaymentsQueryKey({ companyId: project.companyId }) }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
    {printState && (
      <DocPrintView
        open
        onClose={() => setPrintState(null)}
        printDocType={printState.type}
        doc={printState.doc}
        company={company}
        project={project}
        currency={cur}
      />
    )}
    </>
  );
}

const PAYROLL_TAX_SUGGESTIONS: Array<{ iso: string; names: string[]; taxes: string[] }> = [
  { iso: "gb", names: ["united kingdom", "uk", "great britain", "england", "scotland", "wales"], taxes: ["PAYE", "National Insurance (NI)"] },
  { iso: "us", names: ["united states", "usa", "america"], taxes: ["Federal Income Tax Withholding", "FICA (Social Security & Medicare)"] },
  { iso: "ca", names: ["canada"], taxes: ["Income Tax Withholding", "CPP", "EI"] },
  { iso: "au", names: ["australia"], taxes: ["PAYG Withholding"] },
  { iso: "eg", names: ["egypt"], taxes: ["Salary Tax"] },
  { iso: "sa", names: ["saudi arabia", "ksa"], taxes: ["GOSI (Employer + Employee)"] },
  { iso: "ae", names: ["united arab emirates", "uae", "dubai", "abu dhabi"], taxes: ["No Income Tax (VAT only)"] },
  { iso: "de", names: ["germany", "deutschland"], taxes: ["Lohnsteuer", "Sozialversicherung"] },
  { iso: "fr", names: ["france"], taxes: ["Prélèvement à la source"] },
  { iso: "in", names: ["india"], taxes: ["TDS (Salary)", "EPF", "ESI"] },
  { iso: "za", names: ["south africa"], taxes: ["PAYE", "UIF"] },
  { iso: "ng", names: ["nigeria"], taxes: ["PAYE", "Pension Contribution (PenCom)"] },
  { iso: "ke", names: ["kenya"], taxes: ["PAYE", "NHIF", "NSSF"] },
  { iso: "pk", names: ["pakistan"], taxes: ["Salary Tax Withholding", "EOBI"] },
  { iso: "jo", names: ["jordan"], taxes: ["Income Tax Withholding", "SSC"] },
  { iso: "qa", names: ["qatar"], taxes: ["No Income Tax (GRSIA for nationals)"] },
  { iso: "kw", names: ["kuwait"], taxes: ["No Income Tax (PIFSS for nationals)"] },
  { iso: "bh", names: ["bahrain"], taxes: ["No Income Tax (SIO contributions)"] },
  { iso: "om", names: ["oman"], taxes: ["No Income Tax (PASI contributions)"] },
  { iso: "tr", names: ["turkey", "türkiye"], taxes: ["Gelir Vergisi Stopajı", "SGK"] },
  { iso: "ph", names: ["philippines"], taxes: ["Income Tax Withholding", "SSS", "PhilHealth", "Pag-IBIG"] },
  { iso: "sg", names: ["singapore"], taxes: ["Income Tax (no withholding)", "CPF"] },
  { iso: "my", names: ["malaysia"], taxes: ["PCB (Monthly Tax Deduction)", "EPF", "SOCSO"] },
];

function getPayrollTaxSuggestions(country: string): { iso: string; taxes: string[] } | null {
  if (!country) return null;
  const normalized = country.toLowerCase().trim();
  for (const entry of PAYROLL_TAX_SUGGESTIONS) {
    if (entry.iso === normalized || entry.names.some(n => normalized === n || normalized.includes(n) || n.includes(normalized))) {
      return { iso: entry.iso.toUpperCase(), taxes: entry.taxes };
    }
  }
  return null;
}

function PayrollTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { taxTypes } = useContext(CompanyTaxContext);
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const { data: companies = [] } = useListCompanies();
  const company = companies.find(c => c.id === project.companyId);
  const { data: payroll = [] } = useListPayroll({ projectId: project.id });
  const { data: payrollTaxConfigs = [] } = useListPayrollTaxConfigs({ companyId: project.companyId });
  const activePayrollTaxConfigs: PayrollTaxConfig[] = payrollTaxConfigs.filter((c: PayrollTaxConfig) => c.isActive);
  const createPay = useCreatePayrollEntry();
  const updatePay = useUpdatePayrollEntry();
  const delPay = useDeletePayrollEntry();
  const { data: masterStaffList = [] } = useListMasterStaff({ companyId: project.companyId });
  const activeStaff: MasterStaffMember[] = masterStaffList.filter((s: MasterStaffMember) => s.status !== 'Inactive');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<PayrollEntry | null>(null);
  const [payStaffId, setPayStaffId] = useState<string>("__none__");
  const [payName, setPayName] = useState<string>("");
  const [payBasic, setPayBasic] = useState<string>("");
  const [payHouse, setPayHouse] = useState<string>("");
  const [payTrans, setPayTrans] = useState<string>("");
  const [payOthr, setPayOthr] = useState<string>("");
  const [payDeduc, setPayDeduc] = useState<string>("");
  const [payTaxEnabled, setPayTaxEnabled] = useState<Record<string, boolean>>({});
  const [deducManualOverride, setDeducManualOverride] = useState(false);
  const [payMonth, setPayMonth] = useState<string>(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [payYear, setPayYear] = useState<string>(String(new Date().getFullYear()));
  const [paySts, setPaySts] = useState<string>("Pending");
  const [payPaidOn, setPayPaidOn] = useState<string>("");

  type BulkRow = { _id: string; staffId: string | null; name: string; basic: string; house: string; trans: string; othr: string; deduc: string; include: boolean };
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState<string>(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [bulkYear, setBulkYear] = useState<string>(String(new Date().getFullYear()));
  const [bulkSts, setBulkSts] = useState("Pending");
  const [bulkPaidOn, setBulkPaidOn] = useState("");
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([]);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const YEARS = Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + 1 - i));
  const parseMon = (mon: string) => {
    const parts = mon?.split(" ");
    const mName = parts?.[0] ?? "";
    const mIdx = MONTHS.findIndex(m => m.startsWith(mName));
    return { month: mIdx >= 0 ? String(mIdx + 1).padStart(2, "0") : String(new Date().getMonth() + 1).padStart(2, "0"), year: parts?.[1] ?? String(new Date().getFullYear()) };
  };
  const totalBasic = payroll.reduce((s, p) => s + N(p.basic), 0);
  const totalAllowances = payroll.reduce((s, p) => s + N(p.house) + N(p.trans) + N(p.othr), 0);
  const totalDeductions = payroll.reduce((s, p) => s + N(p.deduc), 0);
  const totalNet = payroll.reduce((s, p) => s + N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc), 0);

  const onStaffChange = (staffId: string) => {
    setPayStaffId(staffId);
    if (staffId && staffId !== "__none__") {
      const s = activeStaff.find((m: MasterStaffMember) => m.id === staffId);
      if (s) { setPayName(s.name); setPayBasic(s.basic ?? ""); setPayHouse(s.house ?? ""); setPayTrans(s.trans ?? ""); }
    } else {
      setPayName(""); setPayBasic(""); setPayHouse(""); setPayTrans("");
    }
  };
  const openNew = () => {
    setEditing(null); setPayStaffId("__none__"); setPayName(""); setPayBasic(""); setPayHouse(""); setPayTrans("");
    setPayOthr(""); setPayDeduc(""); setPayTaxEnabled({}); setDeducManualOverride(false);
    setPayMonth(String(new Date().getMonth() + 1).padStart(2, "0")); setPayYear(String(new Date().getFullYear()));
    setPaySts("Pending"); setPayPaidOn("");
    setIsOpen(true);
  };
  const openEdit = (p: PayrollEntry) => {
    setEditing(p); setPayStaffId(p.staffId ?? "__none__"); setPayName(p.name); setPayBasic(String(p.basic ?? "")); setPayHouse(String(p.house ?? "")); setPayTrans(String(p.trans ?? ""));
    setPayOthr(String(p.othr ?? "")); setPayDeduc(String(p.deduc ?? "")); setPayTaxEnabled({}); setDeducManualOverride(true);
    const { month, year } = parseMon(p.mon || "");
    setPayMonth(month); setPayYear(year);
    setPaySts(p.sts || "Pending"); setPayPaidOn(p.date || "");
    setIsOpen(true);
  };

  const computeAutoDeducFromTax = () => {
    const gross = N(payBasic) + N(payHouse) + N(payTrans) + N(payOthr);
    return activePayrollTaxConfigs.reduce((sum, c) => {
      if (payTaxEnabled[c.id] === false) return sum;
      return sum + gross * N(c.rate);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const finalDeduc = deducManualOverride || activePayrollTaxConfigs.length === 0
      ? payDeduc || "0"
      : String(computeAutoDeducFromTax());
    const data = {
      staffId: payStaffId !== "__none__" ? payStaffId : null,
      name: payName,
      mon: `${MONTHS[parseInt(payMonth, 10) - 1]} ${payYear}`,
      basic: payBasic || "0",
      house: payHouse || "0",
      trans: payTrans || "0",
      othr: payOthr || "0",
      deduc: finalDeduc,
      sts: paySts || "Pending",
      date: paySts === "Paid" ? payPaidOn : "",
      refNumber: (fd.get("refNumber") as string) || "",
    };
    if (editing) {
      await updatePay.mutateAsync({ id: editing.id, data });
    } else {
      await createPay.mutateAsync({ data: { id: `PR-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    queryClient.invalidateQueries({ queryKey: getListPayrollQueryKey() });
    setIsOpen(false);
    setEditing(null);
  };

  const openBulkPayroll = () => {
    setBulkMonth(String(new Date().getMonth() + 1).padStart(2, "0"));
    setBulkYear(String(new Date().getFullYear()));
    setBulkSts("Pending");
    setBulkPaidOn("");
    setBulkRows([]);
    setBulkOpen(true);
  };

  const updateBulkRow = (idx: number, field: keyof BulkRow, value: string | boolean | null) => {
    setBulkRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeBulkRow = (idx: number) => {
    setBulkRows(prev => prev.filter((_, i) => i !== idx));
  };

  const addStaffToBulk = (staffId: string) => {
    const s = activeStaff.find((m: MasterStaffMember) => m.id === staffId);
    if (!s) return;
    setBulkRows(prev => [...prev, {
      _id: `staff-${staffId}-${uid()}`,
      staffId: s.id,
      name: s.name,
      basic: s.basic ?? "",
      house: s.house ?? "",
      trans: s.trans ?? "",
      othr: "",
      deduc: "",
      include: true,
    }]);
  };

  const addBlankBulkRow = () => {
    setBulkRows(prev => [...prev, { _id: `new-${uid()}`, staffId: null, name: "", basic: "", house: "", trans: "", othr: "", deduc: "", include: true }]);
  };

  const handleBulkSubmit = async () => {
    const mon = `${MONTHS[parseInt(bulkMonth, 10) - 1]} ${bulkYear}`;
    const included = bulkRows.filter(r => r.name.trim());
    if (included.length === 0) return;
    const results = await Promise.allSettled(included.map(r =>
      createPay.mutateAsync({
        data: {
          id: `PR-${uid()}`,
          projectId: project.id,
          companyId: project.companyId,
          staffId: r.staffId,
          name: r.name,
          mon,
          basic: r.basic || "0",
          house: r.house || "0",
          trans: r.trans || "0",
          othr: r.othr || "0",
          deduc: r.deduc || "0",
          sts: bulkSts,
          date: bulkSts === "Paid" ? bulkPaidOn : "",
          refNumber: "",
        },
      })
    ));
    const saved = results.filter(r => r.status === "fulfilled").length;
    const dupes = results
      .map((r, i) => ({ r, name: included[i].name }))
      .filter(x => x.r.status === "rejected" && (x.r as PromiseRejectedResult).reason?.response?.status === 409)
      .map(x => x.name);
    const otherFailed = results.filter(r => r.status === "rejected").length - dupes.length;
    if (dupes.length > 0) {
      toast({
        title: `${saved} record${saved !== 1 ? "s" : ""} saved — ${dupes.length} skipped`,
        description: `Already paid in ${mon}: ${dupes.join(", ")}`,
        variant: "default",
      });
    } else if (otherFailed > 0) {
      toast({ title: "Some records failed to save", description: `${saved} saved, ${otherFailed} failed.`, variant: "destructive" });
    }
    queryClient.invalidateQueries({ queryKey: getListPayrollQueryKey() });
    setBulkOpen(false);
  };

  const countryName = company?.country ?? "";
  const taxSuggestion = getPayrollTaxSuggestions(countryName);
  const configuredTaxNames = [
    ...taxTypes.map(t => t.name.toLowerCase()),
    ...activePayrollTaxConfigs.map(c => c.name.toLowerCase()),
  ];
  const unconfiguredSuggestions = (taxSuggestion && activePayrollTaxConfigs.length === 0)
    ? taxSuggestion.taxes.filter(s => !configuredTaxNames.some(n => n.includes(s.toLowerCase()) || s.toLowerCase().includes(n)))
    : [];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {countryName && taxSuggestion && unconfiguredSuggestions.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
          <span className="text-amber-600 mt-0.5 shrink-0">ℹ️</span>
          <div>
            <span className="font-semibold text-amber-700">Payroll Tax Tip ({taxSuggestion.iso} — {countryName}):</span>
            <span className="text-amber-600 ml-1">
              Consider configuring {unconfiguredSuggestions.join(", ")} under Company Settings → Payroll Taxes for automatic deduction tracking.
            </span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Payroll</h2>
          <p className="text-muted-foreground text-sm mt-1">Employee wages for this project</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-xl font-medium gap-1.5" onClick={openBulkPayroll}>
            <Users className="w-4 h-4" /> Run Payroll
          </Button>
          <Button className="rounded-xl font-medium shadow-lg shadow-primary/20" onClick={openNew}><Plus className="w-4 h-4 mr-2"/> Add Record</Button>
        </div>
        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if(!o) setEditing(null); }}>
          <DialogContent className="max-w-full sm:max-w-[540px] rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Payroll Record" : "Add Payroll Record"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <RefNumberInput
                  enabled={canAccess('docSequencing')}
                key={(editing?.id ?? "new") + "ref-pay"}
                docType="payroll"
                companyId={project.companyId}
                projectId={project.id}
                editingValue={editing?.refNumber ?? ""}
              />
              {activeStaff.length > 0 && (
                <div className="space-y-2">
                  <Label>Link to Staff Member <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Select value={payStaffId} onValueChange={onStaffChange}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select staff or enter manually" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No staff link (manual entry) —</SelectItem>
                      {activeStaff.map((s: MasterStaffMember) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}{s.jobTitle ? ` — ${s.jobTitle}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Name *</Label>
                  {payStaffId && payStaffId !== "__none__" ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-xl border border-border/50 min-h-10">
                      <span className="font-medium text-sm">{payName}</span>
                    </div>
                  ) : (
                    <Input name="name" required value={payName} onChange={e => setPayName(e.target.value)} placeholder="John Smith" className="rounded-xl" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Period (Month / Year) *</Label>
                  <div className="flex gap-2">
                    <Select value={payMonth} onValueChange={setPayMonth}>
                      <SelectTrigger className="rounded-xl flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={payYear} onValueChange={setPayYear}>
                      <SelectTrigger className="rounded-xl w-[100px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Basic Salary</Label>
                  <Input type="number" min="0" step="0.01" value={payBasic} onChange={e => setPayBasic(e.target.value)} className="rounded-xl" placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>Housing</Label>
                  <Input type="number" min="0" step="0.01" value={payHouse} onChange={e => setPayHouse(e.target.value)} className="rounded-xl" placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>Transport</Label>
                  <Input type="number" min="0" step="0.01" value={payTrans} onChange={e => setPayTrans(e.target.value)} className="rounded-xl" placeholder="0.00" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Other Allowances</Label>
                  <Input type="number" min="0" step="0.01" value={payOthr} onChange={e => setPayOthr(e.target.value)} className="rounded-xl" placeholder="0.00" />
                </div>
              </div>
              {/* ── Deductions (payroll tax configs) ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Deductions</Label>
                  {activePayrollTaxConfigs.length > 0 && (
                    <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                      onClick={() => setDeducManualOverride(v => !v)}>
                      {deducManualOverride ? "Use auto-calculated" : "Enter manually"}
                    </button>
                  )}
                </div>
                {activePayrollTaxConfigs.length > 0 && !deducManualOverride ? (
                  <div className="border border-border/50 rounded-xl overflow-hidden">
                    <div className="px-3 py-2 bg-muted/30 border-b border-border/30">
                      <span className="text-xs text-muted-foreground">Based on gross earnings (basic + allowances). Toggle taxes to include/exclude.</span>
                    </div>
                    {activePayrollTaxConfigs.map(c => {
                      const gross = N(payBasic) + N(payHouse) + N(payTrans) + N(payOthr);
                      const amount = gross * N(c.rate);
                      const enabled = payTaxEnabled[c.id] !== false;
                      return (
                        <div key={c.id} className={`flex items-center gap-3 px-3 py-2 border-b border-border/20 last:border-0 ${enabled ? "" : "opacity-40"}`}>
                          <button type="button" onClick={() => setPayTaxEnabled(prev => ({ ...prev, [c.id]: !enabled }))}
                            className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 transition-colors ${enabled ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground"}`}>
                            {enabled && <svg className="w-3 h-3" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </button>
                          <span className="flex-1 text-sm">{c.name}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">{fp(N(c.rate))}</span>
                          <span className="text-sm font-medium tabular-nums w-24 text-right">{fc(enabled ? amount : 0)}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/20 border-t border-border/40">
                      <span className="text-sm font-semibold">Total Deductions</span>
                      <span className="text-sm font-bold text-rose-600 tabular-nums">{fc(computeAutoDeducFromTax())}</span>
                    </div>
                  </div>
                ) : (
                  <Input type="number" min="0" step="0.01" value={payDeduc} onChange={e => setPayDeduc(e.target.value)} className="rounded-xl" placeholder="0.00" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={paySts} onValueChange={v => { setPaySts(v); if (v === "Paid" && !payPaidOn) setPayPaidOn(new Date().toISOString().slice(0, 10)); }}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Processing">Processing</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {paySts === "Paid" && (
                  <div className="space-y-2">
                    <Label>Paid On *</Label>
                    <Input type="date" className="rounded-xl" value={payPaidOn} onChange={e => setPayPaidOn(e.target.value)} required />
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createPay.isPending || updatePay.isPending}>
                {(createPay.isPending || updatePay.isPending) ? "Saving..." : editing ? "Update Record" : "Add Record"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ── Bulk / Multi-Member Payroll Dialog ── */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-full sm:max-w-5xl rounded-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Run Payroll — Multi-Member
              </DialogTitle>
            </DialogHeader>

            {/* Period + Status row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-4 border-b border-border/30">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Month</Label>
                <Select value={bulkMonth} onValueChange={setBulkMonth}>
                  <SelectTrigger className="rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Year</Label>
                <Select value={bulkYear} onValueChange={setBulkYear}>
                  <SelectTrigger className="rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                <Select value={bulkSts} onValueChange={v => { setBulkSts(v); if (v === "Paid" && !bulkPaidOn) setBulkPaidOn(new Date().toISOString().slice(0, 10)); }}>
                  <SelectTrigger className="rounded-xl h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Processing">Processing</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {bulkSts === "Paid" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Paid On</Label>
                  <Input type="date" className="rounded-xl h-9 text-sm" value={bulkPaidOn} onChange={e => setBulkPaidOn(e.target.value)} />
                </div>
              )}
            </div>

            {/* Staff picker — adds from master list */}
            {(() => {
              const periodLabel = `${MONTHS[parseInt(bulkMonth, 10) - 1]} ${bulkYear}`;
              const alreadyPaidIds = new Set(payroll.filter(p => p.mon === periodLabel && p.staffId).map(p => p.staffId));
              const alreadyAddedIds = new Set(bulkRows.map(r => r.staffId).filter(Boolean));
              const available = (activeStaff as MasterStaffMember[]).filter(s => !alreadyAddedIds.has(s.id) && !alreadyPaidIds.has(s.id));
              const alreadyPaidInPeriod = (activeStaff as MasterStaffMember[]).filter(s => alreadyPaidIds.has(s.id));
              return (
                <div className="space-y-2 pb-3 border-b border-border/30">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                    {available.length > 0 ? (
                      <Select onValueChange={addStaffToBulk} value="">
                        <SelectTrigger className="rounded-xl h-9 text-sm flex-1">
                          <SelectValue placeholder={`Add from staff list (${available.length} available)…`} />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {available.map((s: MasterStaffMember) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}{s.basic ? ` — Basic ${fc(Number(s.basic))}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">All registered staff already added or paid for this period.</span>
                    )}
                  </div>
                  {alreadyPaidInPeriod.length > 0 && (
                    <div className="flex items-start gap-1.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        Already paid in <strong>{periodLabel}</strong>: {alreadyPaidInPeriod.map(s => s.name).join(", ")}. They are hidden from the dropdown to prevent duplicates.
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Table of employees */}
            {bulkRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-sm gap-2">
                <Users className="w-8 h-8 opacity-30" />
                <p>No employees added yet.</p>
                <p className="text-xs">Use the dropdown above to pick from your staff list, or add an unregistered employee below.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/40 text-muted-foreground text-xs font-medium">
                      <th className="py-2 px-2 text-left min-w-[150px]">Name</th>
                      <th className="py-2 px-2 text-right w-28">Basic</th>
                      <th className="py-2 px-2 text-right w-28">Housing</th>
                      <th className="py-2 px-2 text-right w-28">Transport</th>
                      <th className="py-2 px-2 text-right w-28">Other</th>
                      <th className="py-2 px-2 text-right w-28">Deductions</th>
                      <th className="py-2 px-2 text-right w-28 text-emerald-600">Net Pay</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, idx) => {
                      const net = N(row.basic) + N(row.house) + N(row.trans) + N(row.othr) - N(row.deduc);
                      return (
                        <tr key={row._id} className="border-b border-border/20 hover:bg-muted/20 transition-colors">
                          <td className="py-1.5 px-2">
                            <Input value={row.name} onChange={e => updateBulkRow(idx, "name", e.target.value)}
                              placeholder="Employee name" className="h-8 rounded-lg text-sm min-w-[140px]"
                              readOnly={!!row.staffId} />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input type="number" min="0" step="0.01" value={row.basic} onChange={e => updateBulkRow(idx, "basic", e.target.value)}
                              placeholder="0" className="h-8 rounded-lg text-sm text-right" />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input type="number" min="0" step="0.01" value={row.house} onChange={e => updateBulkRow(idx, "house", e.target.value)}
                              placeholder="0" className="h-8 rounded-lg text-sm text-right" />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input type="number" min="0" step="0.01" value={row.trans} onChange={e => updateBulkRow(idx, "trans", e.target.value)}
                              placeholder="0" className="h-8 rounded-lg text-sm text-right" />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input type="number" min="0" step="0.01" value={row.othr} onChange={e => updateBulkRow(idx, "othr", e.target.value)}
                              placeholder="0" className="h-8 rounded-lg text-sm text-right" />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input type="number" min="0" step="0.01" value={row.deduc} onChange={e => updateBulkRow(idx, "deduc", e.target.value)}
                              placeholder="0" className="h-8 rounded-lg text-sm text-right" />
                          </td>
                          <td className="py-1.5 px-2 text-right font-bold text-emerald-600 tabular-nums whitespace-nowrap">
                            {fc(net)}
                          </td>
                          <td className="py-1.5 px-1">
                            <button type="button" onClick={() => removeBulkRow(idx)}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/20 border-t border-border/40 font-semibold text-sm">
                      <td className="py-2 px-2 text-muted-foreground">{bulkRows.length} employee{bulkRows.length !== 1 ? "s" : ""}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fc(bulkRows.reduce((s,r)=>s+N(r.basic),0))}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fc(bulkRows.reduce((s,r)=>s+N(r.house),0))}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fc(bulkRows.reduce((s,r)=>s+N(r.trans),0))}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fc(bulkRows.reduce((s,r)=>s+N(r.othr),0))}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-rose-600">{fc(bulkRows.reduce((s,r)=>s+N(r.deduc),0))}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-emerald-600">{fc(bulkRows.reduce((s,r)=>s+N(r.basic)+N(r.house)+N(r.trans)+N(r.othr)-N(r.deduc),0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-border/30">
              <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground" onClick={addBlankBulkRow}>
                <Plus className="w-3.5 h-3.5" /> Add Unregistered Employee
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button type="button" className="rounded-xl gap-1.5" disabled={createPay.isPending || bulkRows.filter(r => r.name.trim()).length === 0} onClick={handleBulkSubmit}>
                  {createPay.isPending ? "Saving…" : `Save ${bulkRows.filter(r => r.name.trim()).length} Record${bulkRows.filter(r => r.name.trim()).length !== 1 ? "s" : ""}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard title="Total Basic" value={fc(totalBasic)} icon={Users} />
        <KpiCard title="Allowances" value={fc(totalAllowances)} icon={Wallet} />
        <KpiCard title="Deductions" value={fc(totalDeductions)} icon={Trash2} />
        <KpiCard title="Net Payroll" value={fc(totalNet)} icon={DollarSign} />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Basic</TableHead>
              <TableHead className="text-right">Housing</TableHead>
              <TableHead className="text-right">Transport</TableHead>
              <TableHead className="text-right">Deductions</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payroll.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No payroll records. Add one above.</TableCell></TableRow>
            )}
            {payroll.map(p => {
              const net = N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc);
              return (
                <TableRow key={p.id} className="group">
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.mon}</TableCell>
                  <TableCell className="text-right">{fc(p.basic)}</TableCell>
                  <TableCell className="text-right">{fc(p.house)}</TableCell>
                  <TableCell className="text-right">{fc(p.trans)}</TableCell>
                  <TableCell className="text-right text-rose-600">{fc(p.deduc)}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-600">{fc(net)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <Badge className={p.sts === 'Paid' ? 'bg-emerald-100 text-emerald-700 border-none' : p.sts === 'Processing' ? 'bg-blue-100 text-blue-700 border-none' : 'bg-amber-100 text-amber-700 border-none'}>{p.sts || "Pending"}</Badge>
                      {p.sts === 'Paid' && p.date && <span className="text-[10px] text-muted-foreground">{p.date}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(p)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete Payroll Record', message: `Delete payroll record for ${p.name}? This action cannot be undone.`, confirmLabel: 'Delete', destructive: true })) { try { await delPay.mutateAsync({ id: p.id }); queryClient.invalidateQueries({ queryKey: getListPayrollQueryKey() }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ExpensesTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const confirm = useConfirm();
  const { taxLabel, taxRate: companyTaxRate, taxTypes } = useContext(CompanyTaxContext);
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const { data: allCashFlow = [] } = useListCashFlow({ projectId: project.id });
  const { data: masterExpenseCats = [] } = useListExpenseCategories({ companyId: project.companyId });
  const createEntry = useCreateCashFlowEntry();
  const deleteEntry = useDeleteCashFlowEntry();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<CashFlowEntry | null>(null);
  const projVatRate = N(project.vatRate ?? "0");
  const [vatRatePct, setVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));

  const expenses = allCashFlow.filter(e => e.type === "Expense");
  const invalidate = () => queryClient.invalidateQueries({ queryKey: getListCashFlowQueryKey({ projectId: project.id }) });

  const getEntryRate = (exp: CashFlowEntry) => exp.vatRate ? N(exp.vatRate) : projVatRate;
  const totalExclVat = expenses.reduce((s, e) => s + N(e.amount), 0);
  const totalVat = expenses.reduce((s, e) => s + N(e.amount) * getEntryRate(e), 0);
  const totalExpenses = totalExclVat + totalVat;
  const hasAnyTax = totalVat > 0 || N(vatRatePct) > 0;

  const EXP_CATS = masterExpenseCats.map(c => c.name);
  const curMonth = new Date().toISOString().slice(0, 7);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      type:      "Expense",
      cat:       fd.get("cat")       as string,
      desc:      fd.get("desc")      as string,
      amount:    fd.get("amount")    as string,
      vatRate:   String(N(vatRatePct) / 100),
      date:      fd.get("date")      as string,
      ref:       fd.get("ref")       as string,
      refNumber: (fd.get("refNumber") as string) || "",
    };
    if (editing) {
      await deleteEntry.mutateAsync({ id: editing.id });
      await createEntry.mutateAsync({ data: { id: `EXP-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    } else {
      await createEntry.mutateAsync({ data: { id: `EXP-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    if (!await confirm({ title: 'Delete Expense', message: 'Delete this expense? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteEntry.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (exp: CashFlowEntry) => {
    setEditing(exp);
    setVatRatePct(exp.vatRate ? String((N(exp.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setIsOpen(true);
  };
  const openNew = () => {
    setEditing(null);
    setVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setIsOpen(true);
  };

  const thisMonthTotal = expenses
    .filter(e => e.date?.slice(0, 7) === curMonth)
    .reduce((s, e) => s + N(e.amount) * (1 + getEntryRate(e)), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Expenses</h2>
          <p className="text-muted-foreground text-sm mt-1">Project-related operational expenses and disbursements</p>
        </div>
        <Button className="rounded-xl font-medium" onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[640px] max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <RefNumberInput
                  enabled={canAccess('docSequencing')}
              key={(editing?.id ?? "new") + "ref-exp"}
              docType="expense"
              companyId={project.companyId}
              projectId={project.id}
              editingValue={editing?.refNumber ?? ""}
            />
            {/* Row 1: Category (full width) */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select name="cat" defaultValue={editing?.cat || ""}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {EXP_CATS.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">No categories — add them in Masters</div>
                  ) : (
                    EXP_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>
            {/* Row 2: Description (full width) */}
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input name="desc" required defaultValue={editing?.desc} placeholder="Brief description of expense" className="rounded-xl" />
            </div>
            {/* Row 3: Amount | Tax Rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (excl. {taxLabel}) *</Label>
                <Input name="amount" type="number" min="0" step="0.01" defaultValue={editing?.amount} placeholder="0.00" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>{taxLabel} Rate</Label>
                <TaxRatePicker value={vatRatePct} onChange={setVatRatePct} />
              </div>
            </div>
            {N(vatRatePct) > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 text-xs text-blue-700">
                {taxLabel} ({N(vatRatePct).toFixed(2).replace(/\.00$/, '')}%) will be added. Total = Amount + {taxLabel}.
              </div>
            )}
            {/* Row 4: Date | External Receipt Ref */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input name="date" type="date" defaultValue={editing?.date || new Date().toISOString().slice(0, 10)} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>External Receipt Ref</Label>
                <Input name="ref" defaultValue={editing?.ref} placeholder="Supplier invoice / receipt no." className="rounded-xl" />
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Expense"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <KpiCard title={hasAnyTax ? `Total Expenses (incl. ${taxLabel})` : "Total Expenses"} value={fc(totalExpenses)} icon={DollarSign} />
        {hasAnyTax && <KpiCard title={`${taxLabel} Paid`} value={fc(totalVat)} icon={FileText} />}
        <KpiCard title={hasAnyTax ? `This Month (incl. ${taxLabel})` : "This Month"} value={fc(thisMonthTotal)} icon={Wallet} />
      </div>

      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Ref No.</TableHead>
              <TableHead className="text-right">Amount (excl.)</TableHead>
              <TableHead className="text-right text-blue-700">{taxLabel} Rate</TableHead>
              <TableHead className="text-right text-blue-700">{taxLabel}</TableHead>
              <TableHead className="text-right font-semibold">Total</TableHead>
              <TableHead className="text-right w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  No expenses recorded. Add your first expense above.
                </TableCell>
              </TableRow>
            )}
            {expenses.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")).map(exp => {
              const amt = N(exp.amount);
              const rate = getEntryRate(exp);
              const va = amt * rate;
              const total = amt + va;
              return (
                <TableRow key={exp.id}>
                  <TableCell className="text-sm text-muted-foreground">{exp.date || "—"}</TableCell>
                  <TableCell>
                    {exp.cat && <Badge variant="outline" className="text-xs">{exp.cat}</Badge>}
                  </TableCell>
                  <TableCell className="font-medium">{exp.desc || "—"}</TableCell>
                  <TableCell>
                    {exp.refNumber ? (
                      <div>
                        <span className="font-mono text-sm font-medium">{exp.refNumber}</span>
                        {exp.ref && <div className="text-xs text-muted-foreground mt-0.5">{exp.ref}</div>}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm font-mono">{exp.ref || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{fc(amt)}</TableCell>
                  <TableCell className="text-right text-blue-600 text-sm">{(rate * 100).toFixed(2).replace(/\.00$/, '')}%</TableCell>
                  <TableCell className="text-right text-blue-600">{fc(va)}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-600">{fc(total)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-primary" onClick={() => openEdit(exp)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:bg-destructive/10" onClick={() => handleDelete(exp.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {expenses.length > 0 && (
          <div className="px-4 py-3 border-t border-border/50 bg-muted/20 flex justify-end gap-6">
            <span className="text-sm text-muted-foreground">Excl. {taxLabel}: <span className="font-semibold ml-1">{fc(totalExclVat)}</span></span>
            <span className="text-sm text-muted-foreground">{taxLabel}: <span className="font-semibold text-blue-600 ml-1">{fc(totalVat)}</span></span>
            <span className="text-sm font-bold">Total (incl. {taxLabel}): <span className="text-rose-600 ml-1">{fc(totalExpenses)}</span></span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectTeamAccessPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: members = [] } = useListProjectMembers(project.id);
  const { data: companyMembers = [] } = useListMembers();
  const addMember = useAddProjectMember();
  const updateRole = useUpdateProjectMemberRole();
  const removeMember = useRemoveProjectMember();
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("Viewer");

  const memberUserIds = new Set(members.map((m: ProjectMember) => m.userId));
  const available = (companyMembers as MemberItem[]).filter(m => m.status === "Active" && !memberUserIds.has(m.id));

  const handleAdd = async () => {
    if (!addUserId) return;
    try {
      await addMember.mutateAsync({ projectId: project.id, data: { userId: addUserId, role: addRole } });
      queryClient.invalidateQueries({ queryKey: getListProjectMembersQueryKey(project.id) });
      setAddUserId(""); setAddRole("Viewer"); setAddOpen(false);
      toast({ title: "Member added to project" });
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await updateRole.mutateAsync({ projectId: project.id, userId, role });
      queryClient.invalidateQueries({ queryKey: getListProjectMembersQueryKey(project.id) });
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handleRemove = async (userId: string, name: string) => {
    try {
      await removeMember.mutateAsync({ projectId: project.id, userId });
      queryClient.invalidateQueries({ queryKey: getListProjectMembersQueryKey(project.id) });
      toast({ title: `${name} removed from project` });
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <div className="bg-card rounded-2xl shadow-sm border border-border/50 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />Team Access
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">Control which team members can access this project</p>
        </div>
        <Button type="button" size="sm" className="rounded-xl" onClick={() => setAddOpen(o => !o)} disabled={available.length === 0 && !addOpen}>
          <Plus className="w-4 h-4 mr-2" />{addOpen ? "Cancel" : "Add Member"}
        </Button>
      </div>

      {addOpen && (
        <div className="bg-muted/30 rounded-xl p-4 space-y-3 border border-border/50 animate-in fade-in duration-200">
          <p className="text-sm font-medium">Add a company member to this project</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Member</Label>
              <Select value={addUserId} onValueChange={setAddUserId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a member…" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((m: MemberItem) => (
                    <SelectItem key={m.id} value={m.id}>{m.name} — {m.email}</SelectItem>
                  ))}
                  {available.length === 0 && <SelectItem value="__none" disabled>All members already added</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Select value={addRole} onValueChange={setAddRole}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin — Full access</SelectItem>
                  <SelectItem value="Editor">Editor — Edit financial data</SelectItem>
                  <SelectItem value="Viewer">Viewer — Read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => { setAddOpen(false); setAddUserId(""); }}>Cancel</Button>
            <Button type="button" size="sm" className="rounded-xl" disabled={!addUserId || addMember.isPending} onClick={handleAdd}>
              {addMember.isPending ? "Adding…" : "Add to Project"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground border-t border-border/30 pt-3">
        <span><span className="font-semibold text-foreground">Admin</span> — Full access including settings &amp; team management</span>
        <span><span className="font-semibold text-foreground">Editor</span> — Edit all financial data</span>
        <span><span className="font-semibold text-foreground">Viewer</span> — Read-only access</span>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-20" />
          <p className="text-sm">No team members added yet</p>
          <p className="text-xs mt-1">Add company members above to control their project access.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((m: ProjectMember) => (
            <div key={m.userId} className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30">
              <div>
                <p className="font-medium text-sm">{m.userName}</p>
                <p className="text-xs text-muted-foreground">{m.userEmail}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={m.role} onValueChange={role => handleRoleChange(m.userId, role)}>
                  <SelectTrigger className="rounded-lg h-8 text-xs w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Editor">Editor</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleRemove(m.userId, m.userName)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectSettingsTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const updateProject = useUpdateProject();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const { taxLabel: companyTaxLabel, taxTypes: companyTaxTypes } = useContext(CompanyTaxContext);
  const { data: allStaff = [] } = useListMasterStaff({ companyId: project.companyId });
  const { data: customers = [] } = useListCustomers({ companyId: project.companyId });
  const [vatRatePct, setVatRatePct] = useState<string>(String(N(project.vatRate) * 100));
  const activeStaff = allStaff.filter(s => s.status === "Active");
  const activeCustomers = customers.filter(c => c.status === "Active");
  const [selectedClientId, setSelectedClientId] = useState<string>(project.clientId ?? "");
  const [clientIdModified, setClientIdModified] = useState(false);
  const [clientComboOpen, setClientComboOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    const clientIdVal = clientIdModified ? (selectedClientId || null) : (project.clientId ?? null);
    const selectedCustomer = activeCustomers.find(c => c.id === selectedClientId);
    const clientText = clientIdModified
      ? (selectedCustomer?.name ?? "")
      : (project.client || "");
    try {
      await updateProject.mutateAsync({ id: project.id, data: {
        name: fd.get("name") as string,
        code: (fd.get("code") as string) || null,
        client: clientText,
        clientId: clientIdVal,
        owner: fd.get("owner") as string,
        manager: fd.get("manager") as string,
        status: fd.get("status") as string,
        ctrVal: fd.get("ctrVal") as string,
        budget: fd.get("budget") as string,
        vatRate: String(N(vatRatePct) / 100),
        cltRet: String(N(String(fd.get("cltRet") ?? "0")) / 100),
        scRet: String(N(String(fd.get("scRet") ?? "0")) / 100),
        advRec: String(N(String(fd.get("advRec") ?? "0")) / 100),
        stDt: fd.get("stDt") as string,
        endDt: fd.get("endDt") as string,
        advanceMode: (fd.get("advanceMode") as string) || "invoice",
        division: (fd.get("division") as string) || null,
        locationCountry: (fd.get("locationCountry") as string) || null,
        locationCity: (fd.get("locationCity") as string) || null,
      }});
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey(project.id) });
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300 max-w-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-display font-bold">Edit Project Settings</h2>
            <p className="text-muted-foreground text-sm mt-1">Update project configuration and financials</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => setIsEditing(false)}>Cancel</Button>
        </div>
        <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border/50 p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input name="name" required defaultValue={project.name} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Project Code</Label>
              <Input name="code" defaultValue={project.code || ""} placeholder="e.g. NTD-001" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              <Popover open={clientComboOpen} onOpenChange={setClientComboOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className={(selectedClientId || (!clientIdModified && (project.clientName || project.client))) ? "text-foreground" : "text-muted-foreground"}>
                      {selectedClientId
                        ? (activeCustomers.find(c => c.id === selectedClientId)?.name ?? "Select client")
                        : clientIdModified
                          ? "No client"
                          : (project.clientName || project.client || "Select a client")}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => {
                            setSelectedClientId("");
                            setClientIdModified(true);
                            setClientComboOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${(clientIdModified && !selectedClientId) ? "opacity-100" : "opacity-0"}`} />
                          — No client —
                        </CommandItem>
                        {activeCustomers.map(c => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setSelectedClientId(prev => prev === c.id ? "" : c.id);
                              setClientIdModified(true);
                              setClientComboOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${selectedClientId === c.id ? "opacity-100" : "opacity-0"}`} />
                            {c.name}{c.type ? ` — ${c.type}` : ""}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Owner</Label>
              <Input name="owner" defaultValue={project.owner || ""} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Project Manager</Label>
              <Select name="manager" defaultValue={project.manager || ""}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={activeStaff.length === 0 ? "No active staff found" : "Select a staff member"} />
                </SelectTrigger>
                <SelectContent>
                  {activeStaff.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">No active staff members found</div>
                  ) : (
                    activeStaff.map(s => (
                      <SelectItem key={s.id} value={s.name}>
                        {s.name}{s.jobTitle ? ` — ${s.jobTitle}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select name="status" defaultValue={project.status || "Active"}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Division</Label>
              <Input name="division" placeholder="e.g. Infrastructure, Civil" defaultValue={project.division || ""} className="rounded-xl" />
            </div>
            <div className="col-span-full -mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Location</p>
            </div>
            <LocationPicker
              defaultCountry={project.locationCountry}
              defaultCity={project.locationCity}
            />
            <div className="space-y-2">
              <Label>Contract Value</Label>
              <Input type="number" name="ctrVal" min="0" step="0.01" defaultValue={project.ctrVal || ""} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Budget</Label>
              <Input type="number" name="budget" min="0" step="0.01" defaultValue={project.budget || ""} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" name="stDt" defaultValue={project.stDt || ""} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" name="endDt" defaultValue={project.endDt || ""} className="rounded-xl" />
            </div>
          </div>
          <div className="border-t border-border/50 pt-4">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Financial Settings</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{companyTaxLabel} Rate</Label>
                <TaxRatePicker value={vatRatePct} onChange={setVatRatePct} className="rounded-xl" />
                {companyTaxTypes.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">Configure tax types in company settings first.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Client Retention %</Label>
                <Input type="number" name="cltRet" min="0" max="100" step="0.1" defaultValue={N(project.cltRet) * 100} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>SC Retention %</Label>
                <Input type="number" name="scRet" min="0" max="100" step="0.1" defaultValue={N(project.scRet) * 100} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Advance Recovery %</Label>
                <Input type="number" name="advRec" min="0" max="100" step="0.1" defaultValue={N(project.advRec) * 100} className="rounded-xl" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Advance Handling</Label>
                <Select name="advanceMode" defaultValue={project.advanceMode || "invoice"}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Invoice (default)</SelectItem>
                    <SelectItem value="receipt_only">Receipt Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Receipt Only hides the Advance invoice type; advance is recorded as a direct receipt.</p>
              </div>
            </div>
          </div>
          <Button type="submit" className="w-full h-12 rounded-xl" disabled={saving}>
            {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2"/>Save Changes</>}
          </Button>
        </form>
        <ProjectTeamAccessPanel project={project} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Project Settings</h2>
          <p className="text-muted-foreground text-sm mt-1">Configuration and financial details for this project</p>
        </div>
        <Button className="rounded-xl" onClick={() => setIsEditing(true)}>
          <Pencil className="w-4 h-4 mr-2"/>Edit Settings
        </Button>
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Project Name</Label>
            <p className="font-semibold mt-1">{project.name}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Project Code</Label>
            <p className="font-semibold mt-1 font-mono">{project.code || "—"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Client</Label>
            <p className="font-semibold mt-1">{project.clientName || project.client || "—"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Owner</Label>
            <p className="font-semibold mt-1">{project.owner || "—"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Project Manager</Label>
            <p className="font-semibold mt-1">{project.manager || "—"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Status</Label>
            <div className="mt-1"><Badge>{project.status || "Active"}</Badge></div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Division</Label>
            <p className="font-semibold mt-1">{project.division || "—"}</p>
          </div>
          {(project.locationCountry || project.locationCity) && (
            <div className="sm:col-span-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Location</Label>
              <p className="font-semibold mt-1">
                {[project.locationCity, project.locationCountry ? (Country.getCountryByCode(project.locationCountry)?.name ?? project.locationCountry) : null].filter(Boolean).join(", ") || "—"}
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Contract Value</Label>
            <p className="font-semibold mt-1">{fc(project.ctrVal)}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Budget</Label>
            <p className="font-semibold mt-1">{fc(project.budget)}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Start Date</Label>
            <p className="font-semibold mt-1">{project.stDt || "—"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">End Date</Label>
            <p className="font-semibold mt-1">{project.endDt || "—"}</p>
          </div>
        </div>
        <div className="border-t border-border/50 pt-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold mb-4">Financial Settings</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">{companyTaxLabel} Rate</p>
              <p className="font-bold mt-1">{N(project.vatRate) > 0 ? `${(N(project.vatRate) * 100).toFixed(1)}%` : "—"}</p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">Client Retention</p>
              <p className="font-bold mt-1">{(N(project.cltRet) * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">SC Retention</p>
              <p className="font-bold mt-1">{(N(project.scRet) * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground">Advance Recovery</p>
              <p className="font-bold mt-1">{(N(project.advRec) * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center sm:col-span-2">
              <p className="text-xs text-muted-foreground">Advance Handling</p>
              <p className="font-bold mt-1">{project.advanceMode === 'receipt_only' ? 'Receipt Only' : 'Invoice'}</p>
            </div>
          </div>
        </div>
      </div>
      <ProjectTeamAccessPanel project={project} />
      <ProjectLineItemsSettings project={project} />
    </div>
  );
}

function ProjectLineItemsSettings({ project }: { project: Project }) {
  const companyPlan = useContext(CompanyPlanContext);
  const queryClient = useQueryClient();
  const updateProject = useUpdateProject();
  const [toggling, setToggling] = useState<string | null>(null);
  const liFeature = hasFeature(companyPlan, 'lineItems');
  if (!liFeature) return null;

  const lineItemDocs = (project.lineItemDocs as Record<string, boolean> | null) ?? {};

  const DOC_KEYS: { key: string; label: string; source: string; group: 'client' | 'sc' | 'supplier' }[] = [
    { key: 'quotations',      label: 'Client Quotations', source: 'BOQ',    group: 'client' },
    { key: 'clientInvoices',  label: 'Client Invoices',   source: 'BOQ',    group: 'client' },
    { key: 'supplierPO',      label: 'Supplier POs',      source: 'Budget', group: 'supplier' },
    { key: 'supplierInvoice', label: 'Supplier Invoices', source: 'Budget', group: 'supplier' },
    { key: 'scPO',            label: 'SC POs',            source: 'Budget', group: 'sc' },
    { key: 'scInvoice',       label: 'SC Invoices',       source: 'Budget', group: 'sc' },
  ];

  const handleToggle = async (key: string, val: boolean) => {
    setToggling(key);
    try {
      const newDocs = { ...lineItemDocs, [key]: val };
      await updateProject.mutateAsync({ id: project.id, data: { lineItemDocs: newDocs } });
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey(project.id) });
    } finally {
      setToggling(null);
    }
  };

  const groupColors: Record<string, string> = {
    client: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800',
    supplier: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800',
    sc: 'text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800',
  };

  return (
    <div className="bg-card rounded-2xl shadow-sm border border-border/50 p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold text-sm">Line Items</p>
          <p className="text-xs text-muted-foreground mt-0.5">Enable itemised breakdowns per document type. When on, documents show a line-item table instead of a single amount.</p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${Object.values(lineItemDocs).some(Boolean) ? 'bg-primary/10 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'}`}>
          {Object.values(lineItemDocs).filter(Boolean).length} / {DOC_KEYS.length} enabled
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {DOC_KEYS.map(({ key, label, source, group }) => {
          const on = !!lineItemDocs[key];
          const busy = toggling === key;
          return (
            <button
              key={key}
              type="button"
              disabled={busy}
              onClick={() => handleToggle(key, !on)}
              className={`group relative flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
                on
                  ? 'border-primary/40 bg-primary/5 shadow-sm'
                  : 'border-border bg-card hover:border-border/80 hover:bg-muted/40'
              }`}
            >
              <div className={`shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-bold transition-colors ${on ? groupColors[group] : 'text-muted-foreground bg-muted border-border'}`}>
                {source === 'BOQ' ? 'B' : '$'}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium leading-tight transition-colors ${on ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">From {source}</p>
              </div>
              <div className={`shrink-0 w-10 h-6 rounded-full border-2 border-transparent flex items-center transition-colors duration-200 ${on ? 'bg-primary justify-end' : 'bg-input justify-start'}`}>
                <span className={`w-4 h-4 mx-0.5 rounded-full shadow transition-all duration-200 ${on ? 'bg-white scale-110' : 'bg-white/80'}`} />
              </div>
              {busy && (
                <span className="absolute inset-0 rounded-xl bg-background/40 flex items-center justify-center">
                  <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SubcontractorsTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const { taxLabel, taxRate: companyTaxRate, taxTypes } = useContext(CompanyTaxContext);
  const company = useContext(CompanyContext);
  const [printState, setPrintState] = useState<{ doc: Record<string, unknown>; type: PrintDocType } | null>(null);
  const projVatRate = N(project.vatRate ?? "0");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data: invoices = [] } = useListScInvoices({ projectId: project.id });
  const { data: payments = [] } = useListScPayments({ projectId: project.id });
  const createInv = useCreateScInvoice();
  const updateInv = useUpdateScInvoice();
  const delInv = useDeleteScInvoice();
  const createPmt = useCreateScPayment();
  const updatePmt = useUpdateScPayment();
  const delPmt = useDeleteScPayment();
  const { data: scList = [] } = useListSubcontractors({ projectId: project.id });
  const createSc = useCreateSubcontractor();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<ScInvoice | null>(null);
  const [selectedSc, setSelectedSc] = useState<string>("");
  const [selectedPoRefs, setSelectedPoRefs] = useState<string[]>([]);
  const [scBudgetItems, setScBudgetItems] = useState<string[]>([]);
  const [scInvoiceType, setScInvoiceType] = useState<string>("");
  const [invDeds, setInvDeds] = useState<{desc: string; val: string; position: 'before_vat'|'after_vat'}[]>([]);
  const [invCnItems, setInvCnItems] = useState<{ref: string; amt: string; tax: string}[]>([]);
  const [invRetItems, setInvRetItems] = useState<{type: string; pct: string}[]>([]);
  const [scInvVatRatePct, setScInvVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [pmtInv, setPmtInv] = useState<ScInvoice | null>(null);
  const [editingPmt, setEditingPmt] = useState<ScPayment | null>(null);
  const [pmtError, setPmtError] = useState<string | null>(null);
  const [subTab, setSubTab] = useState<'inv'|'pmt'|'po'>('inv');
  const [freePmtMode, setFreePmtMode] = useState(false);
  const [freePmtTp, setFreePmtTp] = useState<string>("Standard");
  const [scFreePmtInvRefs, setScFreePmtInvRefs] = useState<string[]>([]);
  const pmtDialogOpen = !!pmtInv || !!editingPmt || freePmtMode;
  const openEditPmt = (p: ScPayment) => { setEditingPmt(p); setPmtInv(null); setFreePmtMode(false); setPmtError(null); setFreePmtTp(p.tp ?? "Standard"); };
  const openFreePmt = () => { setPmtInv(null); setEditingPmt(null); setFreePmtMode(true); setPmtError(null); setFreePmtTp("Standard"); setScFreePmtInvRefs([]); };
  const { data: vendorOrders = [] } = useListVendorOrders({ projectId: project.id });
  const scOrders: VendorOrder[] = vendorOrders.filter((o: VendorOrder) => o.vendorType === 'sc');
  const { data: budgetLines = [] } = useListBudget({ projectId: project.id });
  const createVO = useCreateVendorOrder();
  const updateVO = useUpdateVendorOrder();
  const delVO = useDeleteVendorOrder();
  const [poOpen, setPoOpen] = useState(false);
  const [editingPo, setEditingPo] = useState<VendorOrder | null>(null);
  const [poVatRatePct, setPoVatRatePct] = useState<string>(() => getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
  const [poContractedValue, setPoContractedValue] = useState<string>("");
  const [poIsDescope, setPoIsDescope] = useState(false);
  const [poVendorId, setPoVendorId] = useState<string>("");
  const [poScDocType, setPoScDocType] = useState<string>("po");
  const [scPoLineItems,  setScPoLineItems]  = useState<LineItem[]>([]);
  const [scPoLiMode,    setScPoLiMode]     = useState<"free" | "budget">("free");
  const [scInvLineItems, setScInvLineItems] = useState<LineItem[]>([]);
  const [scInvLiMode,   setScInvLiMode]    = useState<"free" | "budget">("free");
  const companyPlan = useContext(CompanyPlanContext);
  const canAccess = (feature?: PlanFeatureKey) => !feature || hasFeature(companyPlan, feature);
  const lineItemDocs = (project.lineItemDocs as Record<string, boolean> | null) ?? {};
  const itemSourceMode = ((project.itemSourceMode as string | null) ?? 'free') as 'free' | 'boq' | 'budget';
  const scPoLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.scPO;
  const scInvLineItemsEnabled = hasFeature(companyPlan, 'lineItems') && !!lineItemDocs.scInvoice;
  const openNewPo = () => { setEditingPo(null); setPoVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate)); setPoContractedValue(""); setPoIsDescope(false); setPoVendorId(""); setPoScDocType("po"); setScPoLineItems([]); setScPoLiMode("free"); setPoOpen(true); };
  const openEditPo = (o: VendorOrder) => {
    setEditingPo(o);
    setPoVatRatePct(o.vatRate ? String((N(o.vatRate) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate));
    setPoContractedValue(o.contractedValue ?? "");
    setPoIsDescope(o.docType === 'descope');
    setPoVendorId(o.vendorId ?? "");
    const scPoItems = (o.lineItems as LineItem[] | null) ?? [];
    setScPoLineItems(scPoItems);
    setScPoLiMode(scPoItems.some(i => i.sourceType === "budget") ? "budget" : "free");
    setPoOpen(true);
  };
  const ensureProjectSc = async (virtualId: string): Promise<string> => {
    if (!virtualId.startsWith("master:")) return virtualId;
    const masterId = virtualId.replace("master:", "");
    const masterSc = scList.find((s: Subcontractor) => s.id === virtualId);
    if (!masterSc) return virtualId;
    const existing = scList.find((s: Subcontractor) => s.masterId === masterId && !s.id.startsWith("master:"));
    if (existing) return existing.id;
    const newId = `SC-${uid()}`;
    await createSc.mutateAsync({ data: {
      id: newId,
      projectId: project.id,
      companyId: project.companyId,
      name: masterSc.name,
      trade: masterSc.trade ?? "",
      masterId,
    }});
    queryClient.invalidateQueries({ queryKey: ["/api/subcontractors"] });
    return newId;
  };

  const handlePoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const dt = (fd.get("docType") as string) || "po";
    const rawVendorId = poVendorId;
    const selectedVendorId = rawVendorId.startsWith("master:") ? await ensureProjectSc(rawVendorId) : rawVendorId;
    const selectedVendor = scList.find((s: Subcontractor) => s.id === rawVendorId) ?? scList.find((s: Subcontractor) => s.id === selectedVendorId);
    const vatPct = N(poVatRatePct) / 100;
    const netVal = N(poContractedValue);
    if (dt === 'descope' && netVal >= 0) {
      toast({ title: "Invalid amount", description: "Descope documents must have a negative contracted value.", variant: "destructive" });
      return;
    }
    if (dt !== 'descope' && netVal < 0) {
      toast({ title: "Invalid amount", description: "Only Descope documents can have a negative contracted value.", variant: "destructive" });
      return;
    }
    const scPoLiEnabled = scPoLineItemsEnabled && scPoLineItems.length > 0;
    const effectiveNet = scPoLiEnabled ? lineItemsNetTotal(scPoLineItems) : netVal;
    const computedVatAmt = scPoLiEnabled ? lineItemsTaxTotal(scPoLineItems) : effectiveNet * vatPct;
    const effectiveVatRate = scPoLiEnabled ? 0 : vatPct;
    const data = {
      vendorId: selectedVendorId || null,
      vendorName: selectedVendor?.name ?? "",
      docType: dt as CreateVendorOrderBodyDocType,
      refNumber: (fd.get("refNumber") as string) || "",
      description: (fd.get("description") as string) || "",
      date: (fd.get("date") as string) || "",
      contractedValue: String(effectiveNet),
      vatRate: String(effectiveVatRate),
      vatAmount: String(computedVatAmt),
      supplierQtNumber: (fd.get("supplierQtNumber") as string) || null,
      budgetLineId: (() => { const v = fd.get("budgetLineId") as string; return v && v !== "none" ? v : null; })(),
      status: ((fd.get("status") as string) || "Draft") as CreateVendorOrderBodyStatus,
      ...(scPoLineItemsEnabled ? { lineItems: scPoLineItems } : {}),
    };
    try {
      if (editingPo) {
        await updateVO.mutateAsync({ id: editingPo.id, data });
      } else {
        await createVO.mutateAsync({ data: { id: `VO-${uid()}`, projectId: project.id, companyId: project.companyId, vendorType: 'sc' as const, ...data } });
      }
      queryClient.invalidateQueries({ queryKey: getListVendorOrdersQueryKey({ projectId: project.id }) });
      setPoOpen(false);
      setEditingPo(null);
      setScPoLineItems([]);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { error?: string }; message?: string };
      if (apiErr?.status === 409 || apiErr?.data?.error?.includes("already in use") || apiErr?.message?.includes("already in use")) {
        const msg = apiErr?.data?.error || "This reference number is already in use. Please choose a different one.";
        toast({ title: "Duplicate ref number", description: msg, variant: "destructive" });
        return;
      }
      throw err;
    }
  };
  const totalGross = invoices.reduce((s, i) => s + N(i.gross), 0);
  const totalPaid = invoices.reduce((s, inv) => {
    const { paid } = calcSCI(inv, payments);
    return s + paid;
  }, 0);

  const scAdvancePaid = payments.filter(p => p.tp === 'Advance').reduce((s, p) => s + N(p.amount), 0);
  const scAdvanceRecovered = calcAdvanceRecovery(invoices);
  const scAdvanceOutstanding = Math.max(0, scAdvancePaid - scAdvanceRecovered);
  const invoicePayments = payments.filter(p => p.tp !== 'Advance');
  const totalInvPayments = invoicePayments.reduce((s, p) => s + N(p.amount), 0);
  const totalScNet = invoices.reduce((s, inv) => s + calcSCI(inv, payments).net, 0);

  const getPoVatRate = (inv: { po?: string | null; [key: string]: unknown }): number => {
    const apiRate = (inv as { poVatRate?: string | null }).poVatRate;
    if (apiRate != null) return N(apiRate);
    if (!inv.po) return 0;
    const po = scOrders.find((o: VendorOrder) => o.refNumber === inv.po);
    return N(po?.vatRate ?? 0);
  };
  const formPoVatRate = selectedPoRefs.length > 0
    ? N(scOrders.find((o: VendorOrder) => o.refNumber === selectedPoRefs[0])?.vatRate ?? 0)
    : 0;
  const hasAnyVat = invoices.some(inv => N(inv.vatRate ?? 0) > 0 || getPoVatRate(inv) > 0);

  const openNew  = () => { setEditing(null); setSelectedSc(""); setSelectedPoRefs([]); setScBudgetItems([]); setScInvoiceType(""); setInvDeds([]); setInvCnItems([]); setInvRetItems([]); setScInvVatRatePct(getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate)); setScInvLineItems([]); setScInvLiMode("free"); setIsOpen(true); };
  const openEdit = (inv: ScInvoice) => {
    setEditing(inv);
    setSelectedSc(inv.scId || "");
    setSelectedPoRefs((inv.poIds as string[] | null | undefined) ?? (inv.po ? [inv.po] : []));
    setScBudgetItems((inv.budgetItems as string[] | null | undefined) ?? []);
    setScInvoiceType(inv.invoiceType || "");
    setScInvVatRatePct(inv.vatRate ? String((N(inv.vatRate) * 100).toFixed(4)) : (inv.po ? String((getPoVatRate(inv) * 100).toFixed(4)) : getDefaultTaxPct(taxTypes, companyTaxRate || projVatRate)));
    const scInvItems = (inv.lineItems as LineItem[] | null) ?? [];
    setScInvLineItems(scInvItems);
    setScInvLiMode(scInvItems.some(i => i.sourceType === "budget") ? "budget" : "free");
    setInvDeds((inv.deds as {desc:string;val:string;position?:string}[] | undefined || []).map(d => ({desc: d.desc, val: String(d.val), position: (d.position ?? 'after_vat') as 'before_vat'|'after_vat'})));
    const existingCnItems = (inv.cnItems as {ref:string;amt:number;taxPct?:number}[] | undefined);
    if (existingCnItems && existingCnItems.length > 0) {
      setInvCnItems(existingCnItems.map(c => ({ ref: c.ref, amt: String(c.amt), tax: String(N(c.taxPct ?? 0) * 100) })));
    } else if (inv.cnAmt && inv.cnAmt !== "0") {
      setInvCnItems([{ ref: inv.cnRef || '', amt: inv.cnAmt, tax: "0" }]);
    } else {
      setInvCnItems([]);
    }
    const existingRetItems = (inv.retItems as {type:string;pct:number}[] | undefined);
    if (existingRetItems && existingRetItems.length > 0) {
      setInvRetItems(existingRetItems.map(r => ({ type: r.type, pct: String((N(r.pct) * 100).toFixed(4)) })));
    } else if (N(inv.retPct) > 0) {
      setInvRetItems([{ type: 'Performance Retention', pct: String((N(inv.retPct) * 100).toFixed(4)) }]);
    } else {
      setInvRetItems([]);
    }
    setIsOpen(true);
  };

  const handlePmtSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPmtError(null);
    const fd = new FormData(e.currentTarget);
    const tp = fd.get("tp") as string || "Standard";
    const isAdvanceTp = tp === 'Advance';
    const fields = {
      amount: fd.get("amount") as string,
      date: fd.get("date") as string,
      tp,
      method: fd.get("method") as string,
      refNumber: (fd.get("refNumber") as string) || "",
    };
    try {
      if (editingPmt) {
        await updatePmt.mutateAsync({ id: editingPmt.id, data: fields });
      } else {
        const isReceiptOnly = project.advanceMode === 'receipt_only';
        const allowNoRef = isReceiptOnly && isAdvanceTp;
        const invRef = pmtInv ? pmtInv.id : scFreePmtInvRefs[0] || "";
        const invRefs = pmtInv ? [pmtInv.id] : scFreePmtInvRefs;
        const scNameVal = pmtInv ? (pmtInv.scName || "") : (invoices.find((i: ScInvoice) => i.id === invRef)?.scName || "");
        if (!invRef && !allowNoRef) return;
        await createPmt.mutateAsync({ data: {
          id: `SCP-${uid()}`, projectId: project.id, companyId: project.companyId,
          invRef: invRef || "", invRefs,
          scName: scNameVal,
          ...fields,
        }});
      }
    } catch (err: unknown) {
      const msg = (err as { data?: { error?: string } })?.data?.error ?? (err as Error)?.message ?? "Failed to save payment.";
      setPmtError(msg);
      toast({ title: "Overpayment not allowed", description: msg, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: getListScPaymentsQueryKey({ projectId: project.id }) });
    queryClient.invalidateQueries({ queryKey: getListScPaymentsQueryKey({ companyId: project.companyId }) });
    setPmtInv(null);
    setEditingPmt(null);
    setFreePmtMode(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!scInvoiceType) {
      toast({ title: "Invoice Type required", description: "Please select an invoice type before saving.", variant: "destructive" });
      return;
    }
    const fd = new FormData(e.currentTarget);
    const chosenSc = scList.find((s: Subcontractor) => s.id === selectedSc);
    const resolvedScId = selectedSc && selectedSc !== "__custom__" && selectedSc.startsWith("master:")
      ? await ensureProjectSc(selectedSc)
      : selectedSc;
    const scLiEnabled = scInvLineItemsEnabled && scInvLineItems.length > 0;
    const scInvVatPct = scLiEnabled ? 0 : N(scInvVatRatePct) / 100;
    const grossVal = scLiEnabled ? String(lineItemsNetTotal(scInvLineItems)) : (fd.get("gross") as string) || editing?.gross || "0";
    const data = {
      scId: resolvedScId || `SCI-${uid()}`,
      scName: chosenSc?.name || (fd.get("scNameFree") as string) || "",
      po: selectedPoRefs[0] || "",
      poIds: selectedPoRefs,
      budgetItems: scBudgetItems,
      desc: fd.get("desc") as string,
      date: fd.get("date") as string,
      gross: grossVal,
      refNumber: (fd.get("refNumber") as string) || "",
      retPct: "0",
      vatRate: String(scInvVatPct),
      advRecPct: String(N(String(fd.get("advRecPct") ?? "0")) / 100),
      invoiceType: scInvoiceType,
      cnItems: invCnItems.filter(c => c.amt).map(c => ({ ref: c.ref, amt: Number(c.amt), taxPct: N(c.tax) / 100 })) as CnItem[],
      retItems: invRetItems.filter(r => r.pct).map(r => ({ type: r.type, pct: N(r.pct) / 100 })),
      deds: invDeds.filter(d => d.desc && d.val).map((d, i) => ({ id: `d${i}`, desc: d.desc, tp: "Additional", val: Number(d.val), position: d.position })),
      ...(scInvLineItemsEnabled ? { lineItems: scInvLineItems } : {}),
    };
    if (editing) {
      await updateInv.mutateAsync({ id: editing.id, data });
    } else {
      const id = `SCI-${uid()}`;
      await createInv.mutateAsync({ data: { id, projectId: project.id, companyId: project.companyId, ...data, scId: data.scId || id } });
    }
    queryClient.invalidateQueries({ queryKey: getListScInvoicesQueryKey() });
    setIsOpen(false);
    setEditing(null);
    setInvDeds([]);
    setInvCnItems([]);
    setInvRetItems([]);
    setScInvoiceType("");
    setSelectedPoRefs([]);
    setScBudgetItems([]);
    setScInvLineItems([]);
  };

  return (
    <>
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Subcontractors</h2>
          <p className="text-muted-foreground text-sm mt-1">SC claims, retention and payments</p>
        </div>
        {subTab === 'inv' ? (
          <Button className="rounded-xl font-medium" onClick={openNew}><Plus className="w-4 h-4 mr-2"/>Log SC Invoice</Button>
        ) : subTab === 'pmt' ? (
          <Button className="rounded-xl font-medium" onClick={openFreePmt}><Plus className="w-4 h-4 mr-2"/>New Payment</Button>
        ) : subTab === 'po' ? (
          <Button onClick={openNewPo} className="rounded-xl font-medium"><Plus className="w-4 h-4 mr-2" /> {canAccess('voDescope') ? 'New PO / VO' : 'New PO'}</Button>
        ) : null}
        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) { setEditing(null); setSelectedPoRefs([]); setScBudgetItems([]); } }}>
          <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${scInvLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[800px]'}`}>
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit SC Invoice" : "Log SC Invoice"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <RefNumberInput
                  enabled={canAccess('docSequencing')}
                key={(editing?.id ?? "new") + "ref-sci"}
                docType="sc-invoice"
                companyId={project.companyId}
                projectId={project.id}
                editingValue={editing?.refNumber ?? ""}
              />
              {/* Row 1: Subcontractor | Invoice Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Subcontractor *</Label>
                  <>
                    <Select value={selectedSc || undefined} onValueChange={setSelectedSc}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select subcontractor…" /></SelectTrigger>
                      <SelectContent>
                        {scList.map((s: Subcontractor) => <SelectItem key={s.id} value={s.id}>{s.name}{s.trade ? ` — ${s.trade}` : ""}</SelectItem>)}
                        <SelectItem value="__custom__">— Enter manually —</SelectItem>
                      </SelectContent>
                    </Select>
                    {scList.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">No subcontractors found. You can enter a name manually below, or add subcontractors in Company Portal → Masters.</p>
                    )}
                    {editing && selectedSc && selectedSc !== "__custom__" && !scList.some((s: Subcontractor) => s.id === selectedSc) && (
                      <p className="text-xs text-amber-600 mt-1">Previously linked subcontractor is no longer available. Please re-select.</p>
                    )}
                  </>
                  {selectedSc === "__custom__" && (
                    <Input name="scNameFree" required placeholder="Enter subcontractor name" className="rounded-xl mt-2" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Invoice Type *</Label>
                  <Select value={scInvoiceType || undefined} onValueChange={setScInvoiceType}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Progress Invoice">Progress Invoice</SelectItem>
                      <SelectItem value="Final Invoice">Final Invoice</SelectItem>
                      {project.advanceMode !== 'receipt_only' && <SelectItem value="Progress Advance">Progress Advance</SelectItem>}
                      {project.advanceMode !== 'receipt_only' && <SelectItem value="Recovery Advance">Recovery Advance</SelectItem>}
                      <SelectItem value="Retention Release">Retention Release</SelectItem>
                    </SelectContent>
                  </Select>
                  {scInvoiceType && ({
                    'Progress Invoice':  'Billing for SC work completed to date. Retention withheld; advance recovery deducted.',
                    'Final Invoice':     'Final SC billing on scope completion. Settles remaining subcontract value.',
                    'Progress Advance':  'Advance to SC for work not yet done. Not deducted or recovered from future invoices.',
                    'Recovery Advance':  'Advance invoice. Deducted from future SC progress and final invoices.',
                    'Retention Release': 'Releases SC withheld retention on milestone or scope completion.',
                  } as Record<string,string>)[scInvoiceType] && (
                    <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-2.5 py-1.5 leading-snug">
                      {({
                        'Progress Invoice':  'Billing for SC work completed to date. Retention withheld; advance recovery deducted.',
                        'Final Invoice':     'Final SC billing on scope completion. Settles remaining subcontract value.',
                        'Progress Advance':  'Advance to SC for work not yet done. Not deducted or recovered from future invoices.',
                        'Recovery Advance':  'Advance invoice. Deducted from future SC progress and final invoices.',
                        'Retention Release': 'Releases SC withheld retention on milestone or scope completion.',
                      } as Record<string,string>)[scInvoiceType]}
                    </p>
                  )}
                </div>
              </div>
              {/* Row 2: Description */}
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input key={editing?.id + "d"} name="desc" required placeholder="e.g., Claim No. 3 — Concrete Works" className="rounded-xl" defaultValue={editing?.desc ?? ""} />
              </div>
              {/* Row 3: PO Reference(s) and Budget Items (conditional) */}
              <div className={`grid gap-4 ${scOrders.length > 0 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                {scOrders.length > 0 && (
                  <div className="space-y-2">
                    <Label>PO / VO Reference(s)</Label>
                    <MultiPicker
                      placeholder="Link PO(s)…"
                      options={scOrders.map((o: VendorOrder) => ({ id: o.refNumber, label: `${o.refNumber} — ${o.vendorName || o.description || 'N/A'}` }))}
                      selected={selectedPoRefs}
                      onChange={setSelectedPoRefs}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Linked Budget Items</Label>
                  <MultiPicker
                    placeholder={budgetLines.length === 0 ? "No budget items — add in Budget tab first" : "Link budget item(s)…"}
                    options={budgetLines.map((b: BudgetLine) => ({ id: b.id, label: `${b.wbs ? b.wbs + ' ' : ''}${b.desc ?? b.id}`.slice(0, 50) }))}
                    selected={scBudgetItems}
                    onChange={setScBudgetItems}
                  />
                </div>
              </div>
              {/* Row 4: Invoice Date */}
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input key={editing?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editing?.date ?? new Date().toISOString().slice(0,10)} />
              </div>
              {/* Row 4b: Gross Amount + Tax — or Line Items */}
              {scInvLineItemsEnabled ? (
                <LineItemsEditor
                  projectId={project.id}
                  value={scInvLineItems}
                  onChange={setScInvLineItems}
                  sourceMode={scInvLiMode}
                  onSourceModeChange={v => setScInvLiMode(v as "free" | "budget")}
                  availableModes={["free", "budget"]}
                  taxTypes={taxTypes}
                  taxLabel={taxLabel}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gross Amount (excl. {taxLabel}) *</Label>
                    <Input key={editing?.id + "g"} type="number" name="gross" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.gross ?? ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>{taxLabel} Rate</Label>
                    <TaxRatePicker value={scInvVatRatePct} onChange={setScInvVatRatePct} />
                  </div>
                </div>
              )}
              {/* Row 5: Advance Recovery % */}
              {(scInvoiceType === 'Recovery Advance' || (project.advanceMode !== 'receipt_only' && !['Progress Advance', 'Recovery Advance'].includes(scInvoiceType))) && (
                <div className="space-y-2">
                  <Label>Advance Recovery %</Label>
                  <Input key={editing?.id + "a"} type="number" name="advRecPct" min="0" max="100" step="0.1" className="rounded-xl" defaultValue={editing ? String(N(editing.advRecPct) * 100) : String(N(project.advRec || "0") * 100)} />
                </div>
              )}
              {/* Row 6: Retention Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Retention</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setInvRetItems(r => [...r, { type: 'Performance Retention', pct: String((N(project.scRet || "0.05") * 100).toFixed(4)) }])}>+ Add Retention</Button>
                </div>
                {invRetItems.length > 0 && (
                  <div className="grid gap-1 text-[10px] text-muted-foreground font-medium px-1" style={{gridTemplateColumns:'1fr 110px 32px'}}>
                    <span>Type</span><span>%</span><span/>
                  </div>
                )}
                {invRetItems.map((r, i) => (
                  <div key={i} className="grid gap-2 items-center" style={{gridTemplateColumns:'1fr 110px 32px'}}>
                    <select value={r.type} onChange={e => setInvRetItems(rs => rs.map((x,j) => j===i ? {...x,type:e.target.value} : x))} style={{fontSize:13,padding:'7px 10px',borderRadius:12,border:'1px solid #d1d5db',background:'#fff'}}>
                      <option>Performance Retention</option>
                      <option>Defects Liability</option>
                      <option>Contractual Retention</option>
                      <option>Other</option>
                    </select>
                    <Input type="number" placeholder="%" className="rounded-xl" min="0" max="100" step="0.01" value={r.pct} onChange={e => setInvRetItems(rs => rs.map((x,j) => j===i ? {...x,pct:e.target.value} : x))} />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setInvRetItems(rs => rs.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {invRetItems.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No retention — click "Add Retention" to add items</div>}
              </div>
              {/* Row 7: Additional Deductions (before Credit Notes) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Additional Deductions</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setInvDeds(d => [...d, {desc:"",val:"",position:"after_vat"}])}>+ Add Deduction</Button>
                </div>
                {invDeds.map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Description" className="rounded-xl flex-1" value={d.desc} onChange={e => setInvDeds(ds => ds.map((x,j) => j===i ? {...x,desc:e.target.value} : x))} />
                    <Input type="number" placeholder="Amount" className="rounded-xl w-28" min="0" step="0.01" value={d.val} onChange={e => setInvDeds(ds => ds.map((x,j) => j===i ? {...x,val:e.target.value} : x))} />
                    <select value={d.position} onChange={e => setInvDeds(ds => ds.map((x,j) => j===i ? {...x,position:e.target.value as 'before_vat'|'after_vat'} : x))} style={{fontSize:11,padding:'4px 6px',borderRadius:6,border:'1px solid #d1d5db',background:'#fff',flexShrink:0}}>
                      <option value="after_vat">After {taxLabel}</option>
                      <option value="before_vat">Before {taxLabel}</option>
                    </select>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => setInvDeds(ds => ds.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {invDeds.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No additional deductions</div>}
              </div>
              {/* Row 8: Credit Notes with tax (after Deductions) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Credit Notes</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs rounded-lg" onClick={() => setInvCnItems(d => [...d, {ref:"",amt:"",tax:scInvVatRatePct}])}>+ Add Credit Note</Button>
                </div>
                {invCnItems.length > 0 && (
                  <div className="grid grid-cols-[1fr_7rem_5rem_2rem] gap-2 pb-1">
                    <span className="text-xs text-muted-foreground">CN Reference</span>
                    <span className="text-xs text-muted-foreground">Amount (excl. tax)</span>
                    <span className="text-xs text-muted-foreground">{taxLabel} Rate</span>
                    <span />
                  </div>
                )}
                {invCnItems.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_7rem_5rem_2rem] gap-2 items-center">
                    <Input placeholder="CN Reference" className="rounded-xl" value={c.ref} onChange={e => setInvCnItems(cs => cs.map((x,j) => j===i ? {...x,ref:e.target.value} : x))} />
                    <Input type="number" placeholder="0.00" className="rounded-xl" min="0" step="0.01" value={c.amt} onChange={e => setInvCnItems(cs => cs.map((x,j) => j===i ? {...x,amt:e.target.value} : x))} />
                    <TaxRatePicker value={c.tax} onChange={v => setInvCnItems(cs => cs.map((x,j) => j===i ? {...x,tax:v} : x))} className="rounded-xl" />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => setInvCnItems(cs => cs.filter((_,j) => j!==i))}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                ))}
                {invCnItems.length === 0 && <div className="border border-dashed border-border rounded-xl py-3 text-center text-xs text-muted-foreground">No credit notes</div>}
              </div>
              {editing?.id && (
                <AttachmentPanel
                  entityType="sc_invoice"
                  entityId={editing.id}
                  companyId={project.companyId}
                  enabled={canAccess('attachments')}
                />
              )}
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createInv.isPending || updateInv.isPending}>
                {createInv.isPending || updateInv.isPending ? "Saving..." : editing ? "Save Changes" : "Save SC Invoice"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sub-tab bar */}
      <div style={{display:"flex",gap:2,background:"#f3f4f6",borderRadius:7,padding:3,overflowX:"auto",maxWidth:"100%"}}>
        {([['po','POs'],['inv','SC Invoices'],['pmt','SC Payments']] as const).map(([id,label])=>(
          <button key={id} onClick={()=>{ setSubTab(id); if(id!=='po'){setPoOpen(false);setEditingPo(null);} }} style={{padding:'5px 16px',borderRadius:5,border:'none',fontSize:12,fontWeight:500,background:subTab===id?'#1a2332':'transparent',color:subTab===id?'#fff':'#6b7280',cursor:'pointer',transition:'all .15s'}}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'po' && (
        <div className="space-y-4">
          <Dialog open={poOpen} onOpenChange={v => { setPoOpen(v); if (!v) setEditingPo(null); }}>
            <DialogContent className={`max-w-full max-h-[90vh] overflow-y-auto rounded-2xl ${scPoLineItemsEnabled ? 'sm:max-w-[1100px]' : 'sm:max-w-[700px]'}`}>
              <DialogHeader><DialogTitle className="font-display text-xl">{editingPo ? "Edit SC PO / VO" : canAccess('voDescope') ? "New SC PO / VO" : "New SC PO"}</DialogTitle></DialogHeader>
              <form onSubmit={handlePoSubmit} className="space-y-4 pt-2">
                {/* Row 1: Type + Ref Number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select name="docType" value={editingPo ? (editingPo.docType ?? "po") : poScDocType} onValueChange={v => { setPoIsDescope(v === 'descope'); setPoScDocType(v); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="po">Purchase Order</SelectItem>
                        {canAccess('voDescope') && <SelectItem value="vo">Variation Order</SelectItem>}
                        {canAccess('voDescope') && <SelectItem value="descope">Descope (Scope Reduction)</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <RefNumberInput
                  enabled={canAccess('docSequencing')}
                    key={(editingPo?.id ?? "new") + "-" + (editingPo ? (editingPo.docType ?? "po") : poScDocType)}
                    docType={(() => { const dt = editingPo ? (editingPo.docType ?? "po") : poScDocType; return dt === 'po' ? 'sc-po' : dt === 'vo' ? 'sc-vo' : dt === 'descope' ? 'sc-ds' : dt; })()}
                    companyId={project.companyId}
                    projectId={project.id}
                    editingValue={editingPo?.refNumber ?? undefined}
                  />
                </div>
                {/* Row 2: Subcontractor (full width) */}
                <div className="space-y-2">
                  <Label>Subcontractor</Label>
                  <Select value={poVendorId || undefined} onValueChange={setPoVendorId}>
                    <SelectTrigger><SelectValue placeholder="Select a subcontractor (optional)" /></SelectTrigger>
                    <SelectContent>
                      {scList.map((s: Subcontractor) => <SelectItem key={s.id} value={s.id}>{s.name}{s.trade ? ` — ${s.trade}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {scList.length === 0 && <p className="text-xs text-muted-foreground mt-1">No subcontractors found. Add them in Company Portal → Masters.</p>}
                </div>
                {/* Row 3: Description + SC QT Number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input name="description" placeholder="Brief description" className="rounded-xl" defaultValue={editingPo?.description ?? ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>SC Quotation Number</Label>
                    <Input name="supplierQtNumber" placeholder="SC's quotation reference" className="rounded-xl font-mono" defaultValue={editingPo?.supplierQtNumber ?? ""} />
                  </div>
                </div>
                {/* Row 4: Date */}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input name="date" type="date" className="rounded-xl" defaultValue={editingPo?.date ?? ""} />
                </div>
                {/* Row 4b: Net Value + VAT — or Line Items */}
                {scPoLineItemsEnabled ? (
                  <LineItemsEditor
                    projectId={project.id}
                    value={scPoLineItems}
                    onChange={setScPoLineItems}
                    sourceMode={scPoLiMode}
                    onSourceModeChange={v => setScPoLiMode(v as "free" | "budget")}
                    availableModes={["free", "budget"]}
                    taxTypes={taxTypes}
                    taxLabel={taxLabel}
                  />
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Net Value (excl. {taxLabel}){poIsDescope ? ' — neg.' : ''}</Label>
                        <Input type="number" step="0.01" className="rounded-xl" value={poContractedValue} onChange={e => setPoContractedValue(e.target.value)} placeholder={poIsDescope ? '-0.00' : '0.00'} />
                      </div>
                      <div className="space-y-2">
                        <Label>{taxLabel} Rate</Label>
                        <TaxRatePicker value={poVatRatePct} onChange={setPoVatRatePct} />
                      </div>
                    </div>
                    {(N(poContractedValue) !== 0 || N(poVatRatePct) > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm space-y-1">
                        <div className="flex justify-between text-muted-foreground"><span>Net (excl. {taxLabel})</span><span>{fc(N(poContractedValue))}</span></div>
                        <div className="flex justify-between text-blue-700"><span>{taxLabel} ({poVatRatePct}%)</span><span>{fc(N(poContractedValue) * N(poVatRatePct) / 100)}</span></div>
                        <div className="flex justify-between font-bold border-t border-blue-200 pt-1"><span>Gross Total (incl. {taxLabel})</span><span>{fc(N(poContractedValue) * (1 + N(poVatRatePct) / 100))}</span></div>
                      </div>
                    )}
                  </>
                )}
                {/* Row 5: Budget Line + Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget Line (optional)</Label>
                    <Select name="budgetLineId" defaultValue={editingPo?.budgetLineId ?? "none"}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {budgetLines.map(bl => <SelectItem key={bl.id} value={bl.id}>{bl.cat} — {bl.desc}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingPo?.status ?? "Draft"}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Draft">Draft</SelectItem>
                        <SelectItem value="Issued">Issued</SelectItem>
                        <SelectItem value="PartialDelivery">Partial Delivery</SelectItem>
                        <SelectItem value="Fulfilled">Fulfilled</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 rounded-xl">{editingPo ? "Save Changes" : "Create PO / VO"}</Button>
              </form>
            </DialogContent>
          </Dialog>
          {(() => {
            const committed = scOrders.reduce((s: number, o: VendorOrder) => s + N(o.contractedValue), 0);
            const invoiced = invoices.reduce((s, i) => s + N(i.gross), 0);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Committed (PO Value)</div>
                  <div className="text-2xl font-bold">{fc(committed)}</div>
                </div>
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Invoiced</div>
                  <div className="text-2xl font-bold text-blue-600">{fc(invoiced)}</div>
                </div>
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Remaining Commitment</div>
                  <div className="text-2xl font-bold text-amber-600">{fc(committed - invoiced)}</div>
                </div>
              </div>
            );
          })()}
          <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">Ref Number</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Subcontractor</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Supplier QT No</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="text-right font-semibold">Net Value</TableHead>
                  <TableHead className="text-right font-semibold text-blue-700">{taxLabel}</TableHead>
                  <TableHead className="text-right font-semibold">Gross (incl. {taxLabel})</TableHead>
                  <TableHead className="text-right font-semibold">Invoiced</TableHead>
                  <TableHead className="text-right font-semibold">Balance</TableHead>
                  <TableHead className="text-center font-semibold">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scOrders.length === 0 && (
                  <TableRow><TableCell colSpan={13} className="text-center py-12 text-muted-foreground">No purchase orders yet. Click "New PO / VO" above.</TableCell></TableRow>
                )}
                {scOrders.map((ord: VendorOrder) => {
                  const invTotal = invoices.filter(i => i.po === ord.refNumber).reduce((s, i) => s + N(i.gross), 0);
                  const ordVatAmt = N(ord.vatAmount ?? "0");
                  const ordGrossInclVat = N(ord.contractedValue) + ordVatAmt;
                  const balance = ordGrossInclVat - invTotal;
                  const stBg = ord.status === 'Issued' ? 'bg-blue-100 text-blue-700' : ord.status === 'Fulfilled' ? 'bg-emerald-100 text-emerald-700' : ord.status === 'PartialDelivery' ? 'bg-amber-100 text-amber-700' : ord.status === 'Cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-muted text-muted-foreground';
                  return (
                    <TableRow key={ord.id} className="group hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium font-mono text-sm">{ord.refNumber}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{ord.docType === 'po' ? 'PO' : ord.docType === 'descope' ? 'Descope' : 'VO'}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{ord.vendorName || '—'}</TableCell>
                      <TableCell className="max-w-[140px] truncate text-sm" title={ord.description}>{ord.description || '—'}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{ord.supplierQtNumber || '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{ord.date || '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(ord.contractedValue)}</TableCell>
                      <TableCell className="text-right text-blue-600">{ordVatAmt > 0 ? fc(ordVatAmt) : '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{fc(ordGrossInclVat)}</TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">{fc(invTotal)}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-600">{fc(balance)}</TableCell>
                      <TableCell className="text-center"><Badge className={`${stBg} border-none text-xs`}>{ord.status === 'PartialDelivery' ? 'Partial Delivery' : ord.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _sc = scList.find((x: { id: string; trade?: string }) => x.id === ord.scId); setPrintState({ doc: { ...ord as unknown as Record<string, unknown>, partySubtitle: _sc?.trade ?? "" }, type: ord.docType === 'po' ? 'sc-po' : ord.docType === 'vo' ? 'sc-vo' : 'sc-descope' }); }}>
                            <Printer className="w-3.5 h-3.5" />
                          </Button>}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditPo(ord)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600" onClick={async () => {
                            if (await confirm({ title: 'Delete Order', message: 'Delete this order? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) {
                              try {
                                await delVO.mutateAsync({ id: ord.id });
                                queryClient.invalidateQueries({ queryKey: getListVendorOrdersQueryKey({ projectId: project.id }) });
                              } catch (err: unknown) {
                                const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
                                toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
                              }
                            }
                          }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {subTab === 'inv' && <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard title="Total Gross" value={fc(totalGross)} icon={Pickaxe} />
        <KpiCard title="Total Net Payable" value={fc(totalScNet)} icon={DollarSign} />
        <KpiCard title="Invoice Payments" value={fc(totalInvPayments)} icon={Wallet} trend="up" />
        <KpiCard title="Advance Outstanding" value={fc(scAdvanceOutstanding)} icon={Shield} trend={scAdvanceOutstanding > 0 ? "down" : "up"} />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>SC Inv #</TableHead>
              <TableHead>Subcontractor</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Retention</TableHead>
              <TableHead className="text-right">Adv. Rec.</TableHead>
              <TableHead className="text-right">Net Payable</TableHead>
              {hasAnyVat && <TableHead className="text-right">{taxLabel}</TableHead>}
              {hasAnyVat && <TableHead className="text-right">Total (incl. {taxLabel})</TableHead>}
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={hasAnyVat ? 15 : 13} className="text-center py-12 text-muted-foreground">No subcontractor invoices. Log one above.</TableCell></TableRow>
            )}
            {invoices.map(inv => {
              const invStoredVatRate = N(inv.vatRate ?? 0);
              const scInvVatDisplay = invStoredVatRate || getPoVatRate(inv);
              const { ret, adv, net, vat, totalPayable, paid, bal } = calcSCI(inv, payments, scInvVatDisplay);
              const st = stBadge(bal, paid);
              return (
                <TableRow key={inv.id} className="group">
                  <TableCell className="font-medium">{inv.id}</TableCell>
                  <TableCell>{inv.scName}</TableCell>
                  <TableCell className="max-w-[140px] truncate">{inv.desc || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{inv.invoiceType || "—"}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">{inv.date || '—'}</TableCell>
                  <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                  <TableCell className="text-right text-amber-700">{ret > 0 ? fc(ret) : '—'}</TableCell>
                  <TableCell className="text-right text-orange-600">{N(inv.advRecPct) > 0 ? fc(adv) : '—'}</TableCell>
                  <TableCell className="text-right font-semibold">{fc(net)}</TableCell>
                  {hasAnyVat && <TableCell className="text-right text-blue-700">{scInvVatDisplay > 0 ? fc(vat) : '—'}</TableCell>}
                  {hasAnyVat && <TableCell className="text-right font-semibold">{scInvVatDisplay > 0 ? fc(totalPayable) : fc(net)}</TableCell>}
                  <TableCell className="text-right text-emerald-600 font-medium">{fc(paid)}</TableCell>
                  <TableCell className="text-right font-bold">{fc(bal)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${st.bg} ${st.cl} border-none`}>{st.lb}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Record Payment" onClick={() => { setPmtInv(inv); setPmtError(null); setFreePmtTp("Standard"); }}>
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      {canAccess('printExport') && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-blue-600" title="Print / Save PDF" onClick={() => { const _sc = scList.find((x: { id: string; trade?: string }) => x.id === inv.scId); setPrintState({ doc: { ...inv as unknown as Record<string, unknown>, partySubtitle: _sc?.trade ?? "" }, type: 'sc-invoice' }); }}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(inv)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete SC Invoice', message: 'Delete this SC invoice? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { try { await delInv.mutateAsync({ id: inv.id }); queryClient.invalidateQueries({ queryKey: getListScInvoicesQueryKey() }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed. This invoice may have payments linked to it."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      </>}

      {/* Record / Edit SC Payment Dialog */}
      <Dialog open={pmtDialogOpen} onOpenChange={open => { if (!open) { setPmtInv(null); setEditingPmt(null); setFreePmtMode(false); } }}>
        <DialogContent className="max-w-full sm:max-w-[440px] rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-lg">{editingPmt ? "Edit SC Payment" : pmtInv ? `Record Payment — ${pmtInv.id}` : "New SC Payment"}</DialogTitle></DialogHeader>
          <form onSubmit={handlePmtSubmit} className="space-y-3 pt-2">
            {pmtError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{pmtError}</div>
            )}
            <RefNumberInput
                  enabled={canAccess('docSequencing')}
              key={(editingPmt?.id ?? "new") + "ref-scp"}
              docType="sc-payment"
              companyId={project.companyId}
              projectId={project.id}
              editingValue={editingPmt?.refNumber ?? ""}
            />
            {freePmtMode && !editingPmt && !(project.advanceMode === 'receipt_only' && freePmtTp === 'Advance') && (
              <div className="space-y-1.5">
                <Label>SC Invoice(s) *</Label>
                <MultiPicker
                  placeholder="Select SC invoice(s)…"
                  options={invoices.map((i: ScInvoice) => ({ id: i.id, label: `${i.id} — ${i.scName} — ${(i.desc || '').slice(0, 30)}` }))}
                  selected={scFreePmtInvRefs}
                  onChange={setScFreePmtInvRefs}
                />
                {scFreePmtInvRefs.length === 0 && <p className="text-xs text-muted-foreground">Select at least one invoice</p>}
              </div>
            )}
            {freePmtMode && !editingPmt && project.advanceMode === 'receipt_only' && freePmtTp === 'Advance' && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
                Advance payment — no invoice reference required in Receipt Only mode.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount *</Label>
                <Input key={editingPmt?.id + "amt"} type="number" name="amount" required min="0" step="0.01" placeholder="0.00" className="rounded-xl" defaultValue={editingPmt?.amount ?? ""} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input key={editingPmt?.id + "dt"} type="date" name="date" className="rounded-xl" defaultValue={editingPmt?.date ?? new Date().toISOString().slice(0,10)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select key={(editingPmt?.id ?? "new") + "-tp"} name="tp" value={freePmtTp} onValueChange={setFreePmtTp}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="Partial Payment">Partial Payment</SelectItem>
                    <SelectItem value="Retention Release">Retention Release</SelectItem>
                    <SelectItem value="Advance">Advance</SelectItem>
                    <SelectItem value="Final">Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select key={(editingPmt?.id ?? "new") + "-mth"} name="method" defaultValue={editingPmt?.method ?? "Bank Transfer"}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select method…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl" disabled={createPmt.isPending || updatePmt.isPending}>
              {(createPmt.isPending || updatePmt.isPending) ? "Saving..." : editingPmt ? "Save Changes" : "Record Payment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payments sub-tab */}
      {subTab === 'pmt' && (
        <div className="space-y-5">
          {/* SC Advance Ledger */}
          {scAdvancePaid > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-orange-800 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" /> SC Advance Ledger
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="bg-white rounded-xl p-3 border border-orange-100">
                  <div className="text-xs text-muted-foreground mb-1">Total Advance Paid</div>
                  <div className="font-bold text-orange-700">{fc(scAdvancePaid)}</div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-orange-100">
                  <div className="text-xs text-muted-foreground mb-1">Recovered via Invoices</div>
                  <div className="font-bold text-emerald-700">{fc(scAdvanceRecovered)}</div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-orange-100">
                  <div className="text-xs text-muted-foreground mb-1">Outstanding Balance</div>
                  <div className={`font-bold ${scAdvanceOutstanding > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fc(scAdvanceOutstanding)}</div>
                </div>
              </div>
              <p className="text-xs text-orange-600 mt-3">Advance payments are separate from invoice payments. Recovery is computed from invoice advance recovery % × gross amount.</p>
            </div>
          )}
          <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-border/30 bg-muted/20 flex items-center justify-between">
              <span className="text-sm font-semibold">All SC Payments</span>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>Advance Payments: <span className="font-semibold text-orange-600">{fc(scAdvancePaid)}</span></span>
                <span>Invoice Payments: <span className="font-semibold text-emerald-600">{fc(totalInvPayments)}</span></span>
              </div>
            </div>
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow><TableHead>Pmt #</TableHead><TableHead>SC Invoice</TableHead><TableHead>Subcontractor</TableHead><TableHead>Type</TableHead><TableHead>Method</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No payments recorded yet. Click "New Payment" above.</TableCell></TableRow>}
                {payments.map(p => (
                  <TableRow key={p.id} className={`group ${p.tp === 'Advance' ? 'bg-orange-50/30' : ''}`}>
                    <TableCell className="font-medium">{p.id}</TableCell>
                    <TableCell className="font-mono text-xs">{p.invRef}</TableCell>
                    <TableCell>{p.scName || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${p.tp === 'Advance' ? 'border-orange-300 text-orange-700 bg-orange-50' : ''}`}>{p.tp || 'Standard'}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{p.method || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{p.date || '—'}</TableCell>
                    <TableCell className={`text-right font-semibold ${p.tp === 'Advance' ? 'text-orange-600' : 'text-rose-600'}`}>{fc(p.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEditPmt(p)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600"
                          onClick={async () => { if(await confirm({ title: 'Delete Payment', message: 'Delete this payment? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { try { await delPmt.mutateAsync({ id: p.id }); queryClient.invalidateQueries({ queryKey: getListScPaymentsQueryKey({ projectId: project.id }) }); queryClient.invalidateQueries({ queryKey: getListScPaymentsQueryKey({ companyId: project.companyId }) }); } catch (err: unknown) { const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed."; toast({ title: "Cannot Delete", description: msg, variant: "destructive" }); } }}}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
    {printState && (
      <DocPrintView
        open
        onClose={() => setPrintState(null)}
        printDocType={printState.type}
        doc={printState.doc}
        company={company}
        project={project}
        currency={cur}
      />
    )}
    </>
  );
}

type UnifiedCFRow = {
  id: string;
  date: string;
  type: 'Inflow' | 'Outflow';
  source: string;
  category: string;
  description: string;
  amount: number;
  ref: string;
  isManual: boolean;
  manualId?: string;
};

type UnifiedCFRowWithRunning = UnifiedCFRow & { running: number };

function buildUnifiedCashFlow(
  clientReceipts: ClientReceipt[],
  scPayments: ScPayment[],
  supplierPayments: SupplierPayment[],
  payroll: PayrollEntry[],
  cashFlow: CashFlowEntry[],
): UnifiedCFRowWithRunning[] {
  const rows: UnifiedCFRow[] = [];

  const ADVANCE_RECEIPT_TYPES_CF = ['Advance mobilisation', 'Advance'];
  for (const r of clientReceipts) {
    const isAdvance = ADVANCE_RECEIPT_TYPES_CF.includes(r.tp ?? '');
    rows.push({
      id: `cr-${r.id}`,
      date: r.date || '',
      type: 'Inflow',
      source: isAdvance ? 'Advance Receipt' : 'Client Receipt',
      category: isAdvance ? 'Advance (Deferred)' : 'Client Receipt',
      description: r.desc || r.invRef || '',
      amount: N(r.amount),
      ref: r.ref || r.invRef || '',
      isManual: false,
    });
  }

  for (const p of scPayments) {
    const isAdvance = p.tp === 'Advance';
    rows.push({
      id: `scp-${p.id}`,
      date: p.date || '',
      type: 'Outflow',
      source: isAdvance ? 'SC Advance' : 'SC Payment',
      category: isAdvance ? 'SC Advance (Deferred)' : 'Subcontractor',
      description: p.scName ? `${p.scName} — ${p.invRef}` : p.invRef,
      amount: N(p.amount),
      ref: p.ref || p.invRef || '',
      isManual: false,
    });
  }

  for (const p of supplierPayments) {
    rows.push({
      id: `sup-${p.id}`,
      date: p.date || '',
      type: 'Outflow',
      source: 'Supplier Payment',
      category: 'Supplier',
      description: p.suppName ? `${p.suppName} — ${p.invRef}` : p.invRef,
      amount: N(p.amount),
      ref: p.ref || p.invRef || '',
      isManual: false,
    });
  }

  for (const p of payroll) {
    const net = N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc);
    rows.push({
      id: `pr-${p.id}`,
      date: p.date || '',
      type: 'Outflow',
      source: 'Payroll',
      category: 'Payroll',
      description: p.name + (p.role ? ` (${p.role})` : '') + (p.mon ? ` — ${p.mon}` : ''),
      amount: net,
      ref: '',
      isManual: false,
    });
  }

  for (const cf of cashFlow) {
    const isExpense = cf.type === 'Expense';
    const isManual = !isExpense;
    rows.push({
      id: `cf-${cf.id}`,
      date: cf.date || '',
      type: isExpense ? 'Outflow' : (cf.type === 'Inflow' ? 'Inflow' : 'Outflow'),
      source: isExpense ? 'Expense' : 'Manual',
      category: cf.cat || '',
      description: cf.desc || '',
      amount: N(cf.amount),
      ref: cf.ref || '',
      isManual,
      manualId: isManual ? cf.id : undefined,
    });
  }

  rows.sort((a, b) => (a.date || '').localeCompare(b.date || '') || a.id.localeCompare(b.id));

  let running = 0;
  return rows.map(r => {
    running += r.type === 'Inflow' ? r.amount : -r.amount;
    return { ...r, running };
  });
}

function CashFlowTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: cashFlow = [] } = useListCashFlow({ projectId: project.id });
  const { data: clientReceipts = [] } = useListClientReceipts({ projectId: project.id });
  const { data: scPayments = [] } = useListScPayments({ projectId: project.id });
  const { data: supplierPayments = [] } = useListSupplierPayments({ projectId: project.id });
  const { data: payroll = [] } = useListPayroll({ projectId: project.id });
  const createCF = useCreateCashFlowEntry();
  const delCF = useDeleteCashFlowEntry();
  const [isOpen, setIsOpen] = useState(false);

  const unified = buildUnifiedCashFlow(clientReceipts, scPayments, supplierPayments, payroll, cashFlow);
  const totalInflows = unified.filter(r => r.type === 'Inflow').reduce((s, r) => s + r.amount, 0);
  const totalOutflows = unified.filter(r => r.type === 'Outflow').reduce((s, r) => s + r.amount, 0);
  const netCash = totalInflows - totalOutflows;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await createCF.mutateAsync({
      data: {
        id: `CF-${uid()}`,
        projectId: project.id,
        companyId: project.companyId,
        type: fd.get("type") as string,
        cat: fd.get("cat") as string,
        desc: fd.get("desc") as string,
        amount: fd.get("amount") as string,
        date: fd.get("date") as string,
      }
    });
    queryClient.invalidateQueries({ queryKey: getListCashFlowQueryKey() });
    setIsOpen(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Cash Flow Ledger</h2>
          <p className="text-muted-foreground text-sm mt-1">Unified view of all financial transactions</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl font-medium shadow-lg shadow-primary/20"><Plus className="w-4 h-4 mr-2"/> Add Manual Entry</Button>
          </DialogTrigger>
          <DialogContent className="max-w-full sm:max-w-[500px] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">New Manual Cash Flow Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select name="type" defaultValue="Outflow">
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inflow">Inflow</SelectItem>
                      <SelectItem value="Outflow">Outflow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input name="cat" placeholder="e.g. Overhead" className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input name="desc" required className="rounded-xl" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input type="number" name="amount" required className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" name="date" required className="rounded-xl" defaultValue={new Date().toISOString().slice(0,10)} />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createCF.isPending}>
                {createCF.isPending ? "Saving..." : "Save Entry"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-card rounded-2xl border border-border/50 p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Inflows</p>
          <p className="text-2xl font-bold text-emerald-600">{fc(totalInflows)}</p>
        </div>
        <div className="bg-card rounded-2xl border border-border/50 p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Outflows</p>
          <p className="text-2xl font-bold text-rose-600">{fc(totalOutflows)}</p>
        </div>
        <div className="bg-card rounded-2xl border border-border/50 p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Net Cash Flow</p>
          <p className={`text-2xl font-bold ${netCash >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fc(netCash)}</p>
        </div>
      </div>

      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Category / Name</TableHead>
              <TableHead>Description / Ref</TableHead>
              <TableHead className="text-right">Inflow</TableHead>
              <TableHead className="text-right">Outflow</TableHead>
              <TableHead className="text-right">Running Balance</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {unified.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No entries found.</TableCell></TableRow>
            )}
            {unified.map(row => (
              <TableRow key={row.id} className="group">
                <TableCell className="text-sm">{row.date}</TableCell>
                <TableCell>
                  <Badge className={
                    row.source === 'Client Receipt' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none' :
                    row.source === 'Manual' ? 'bg-sky-100 text-sky-700 hover:bg-sky-100 border-none' :
                    row.source === 'Payroll' ? 'bg-violet-100 text-violet-700 hover:bg-violet-100 border-none' :
                    'bg-rose-100 text-rose-700 hover:bg-rose-100 border-none'
                  }>{row.source}</Badge>
                </TableCell>
                <TableCell className="text-sm">{row.category}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={row.description + (row.ref ? ` · ${row.ref}` : '')}>
                  {row.description}{row.ref ? <span className="text-xs ml-1 opacity-60">· {row.ref}</span> : null}
                </TableCell>
                <TableCell className="text-right text-emerald-600 font-medium">
                  {row.type === 'Inflow' ? fc(row.amount) : '—'}
                </TableCell>
                <TableCell className="text-right text-rose-600 font-medium">
                  {row.type === 'Outflow' ? fc(row.amount) : '—'}
                </TableCell>
                <TableCell className={`text-right font-bold ${row.running >= 0 ? 'text-foreground' : 'text-rose-600'}`}>
                  {fc(row.running)}
                </TableCell>
                <TableCell className="text-right">
                  {row.isManual && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity" onClick={async () => {
                      if(await confirm({ title: 'Delete Cash Flow Entry', message: 'Delete this manual entry? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) {
                        await delCF.mutateAsync({ id: row.manualId! });
                        queryClient.invalidateQueries({ queryKey: getListCashFlowQueryKey() });
                      }
                    }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BudgetTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: lines = [] } = useListBudget({ projectId: project.id });
  const createLine = useCreateBudgetLine();
  const updateLine = useUpdateBudgetLine();
  const delLine = useDeleteBudgetLine();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<BudgetLine | null>(null);
  const totalBgt = lines.reduce((s, l) => s + N(l.appBgt), 0);
  const totalActl = lines.reduce((s, l) => s + N(l.actl), 0);
  const totalCmtd = lines.reduce((s, l) => s + N(l.cmtd), 0);
  const variance = totalBgt - totalActl - totalCmtd;

  const openNew  = () => { setEditing(null); setIsOpen(true); };
  const openEdit = (l: BudgetLine) => { setEditing(l); setIsOpen(true); };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      wbs: fd.get("wbs") as string,
      desc: fd.get("desc") as string,
      cat: fd.get("cat") as string,
      appBgt: fd.get("appBgt") as string,
      actl: (fd.get("actl") as string) || "0",
      cmtd: (fd.get("cmtd") as string) || "0",
    };
    if (editing) {
      await updateLine.mutateAsync({ id: editing.id, data });
    } else {
      await createLine.mutateAsync({ data: { id: `BL-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    queryClient.invalidateQueries({ queryKey: getListBudgetQueryKey() });
    setIsOpen(false);
    setEditing(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Budget Control</h2>
          <p className="text-muted-foreground text-sm mt-1">Approved budget vs. actual & committed costs</p>
        </div>
        <Button className="rounded-xl font-medium shadow-lg shadow-primary/20" onClick={openNew}>
          <Plus className="w-4 h-4 mr-2"/> Add Budget Line
        </Button>
        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="max-w-full sm:max-w-[500px] rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Budget Line" : "Add Budget Line"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>WBS Code</Label>
                  <Input key={editing?.id + "w"} name="wbs" placeholder="e.g., 1.1.2" className="rounded-xl" defaultValue={editing?.wbs ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select key={editing?.id + "cat"} name="cat" defaultValue={editing?.cat ?? "Civil"}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Civil","Structural","Mechanical","Electrical","Plumbing","Architecture","Finishing","Landscaping","Safety & HSE","Provisional Sums","Preliminaries","Other"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input key={editing?.id + "d"} name="desc" required placeholder="e.g., Foundation Concrete" className="rounded-xl" defaultValue={editing?.desc ?? ""} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Approved Budget *</Label>
                  <Input key={editing?.id + "b"} type="number" name="appBgt" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.appBgt ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Actual Cost</Label>
                  <Input key={editing?.id + "a"} type="number" name="actl" min="0" step="0.01" className="rounded-xl" defaultValue={editing?.actl ?? "0"} />
                </div>
                <div className="space-y-2">
                  <Label>Committed</Label>
                  <Input key={editing?.id + "cm"} type="number" name="cmtd" min="0" step="0.01" className="rounded-xl" defaultValue={editing?.cmtd ?? "0"} />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createLine.isPending || updateLine.isPending}>
                {createLine.isPending || updateLine.isPending ? "Saving..." : editing ? "Save Changes" : "Add Line"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard title="Approved Budget" value={fc(totalBgt)} icon={BookOpen} />
        <KpiCard title="Actual Cost" value={fc(totalActl)} icon={DollarSign} trend={totalActl > totalBgt ? "down" : "up"} />
        <KpiCard title="Committed" value={fc(totalCmtd)} icon={Briefcase} />
        <KpiCard title="Variance" value={fc(variance)} icon={Wallet} trend={variance >= 0 ? "up" : "down"} />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>WBS</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Approved Budget</TableHead>
              <TableHead className="text-right">Actual Cost</TableHead>
              <TableHead className="text-right">Committed</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No budget lines. Add one above.</TableCell></TableRow>}
            {lines.map(l => {
              const v = N(l.appBgt) - N(l.actl) - N(l.cmtd);
              return (
                <TableRow key={l.id} className="group">
                  <TableCell className="font-mono text-xs">{l.wbs || "—"}</TableCell>
                  <TableCell>{l.desc}</TableCell>
                  <TableCell>{l.cat || "—"}</TableCell>
                  <TableCell className="text-right">{fc(l.appBgt)}</TableCell>
                  <TableCell className="text-right">{fc(l.actl)}</TableCell>
                  <TableCell className="text-right">{fc(l.cmtd)}</TableCell>
                  <TableCell className={`text-right font-bold ${v >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fc(v)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(l)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        onClick={async () => { if(await confirm({ title: 'Delete Budget Line', message: 'Delete this budget line? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { await delLine.mutateAsync({ id: l.id }); queryClient.invalidateQueries({ queryKey: getListBudgetQueryKey() }); }}}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BOQTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: items = [] } = useListBoq({ projectId: project.id });
  const createItem = useCreateBoqItem();
  const updateItem = useUpdateBoqItem();
  const delItem = useDeleteBoqItem();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<BoqItem | null>(null);
  const totalContractAmt = items.reduce((s, i) => s + N(i.qty) * N(i.rate), 0);
  const totalActualCost = items.reduce((s, i) => s + N(i.actCost), 0);

  const openNew  = () => { setEditing(null); setIsOpen(true); };
  const openEdit = (i: BoqItem) => { setEditing(i); setIsOpen(true); };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      sec: fd.get("sec") as string,
      itm: fd.get("itm") as string,
      unit: fd.get("unit") as string,
      qty: fd.get("qty") as string,
      rate: fd.get("rate") as string,
      actQty: (fd.get("actQty") as string) || "0",
      actCost: (fd.get("actCost") as string) || "0",
    };
    if (editing) {
      await updateItem.mutateAsync({ id: editing.id, data });
    } else {
      await createItem.mutateAsync({ data: { id: `BOQ-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    queryClient.invalidateQueries({ queryKey: getListBoqQueryKey() });
    setIsOpen(false);
    setEditing(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Bill of Quantities</h2>
          <p className="text-muted-foreground text-sm mt-1">Contract quantities vs. actual progress</p>
        </div>
        <Button className="rounded-xl font-medium shadow-lg shadow-primary/20" onClick={openNew}>
          <Plus className="w-4 h-4 mr-2"/> Add BOQ Item
        </Button>
        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="max-w-full sm:max-w-[540px] rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit BOQ Item" : "Add BOQ Item"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Section</Label>
                  <Input key={editing?.id + "s"} name="sec" placeholder="e.g., A — Earthworks" className="rounded-xl" defaultValue={editing?.sec ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input key={editing?.id + "u"} name="unit" placeholder="m³ / m² / No." className="rounded-xl" defaultValue={editing?.unit ?? ""} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Item Description *</Label>
                <Input key={editing?.id + "i"} name="itm" required placeholder="e.g., Excavation in rock" className="rounded-xl" defaultValue={editing?.itm ?? ""} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input key={editing?.id + "q"} type="number" name="qty" required min="0" step="any" className="rounded-xl" defaultValue={editing?.qty ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Unit Rate *</Label>
                  <Input key={editing?.id + "r"} type="number" name="rate" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.rate ?? ""} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Actual Qty to Date</Label>
                  <Input key={editing?.id + "aq"} type="number" name="actQty" min="0" step="any" className="rounded-xl" defaultValue={editing?.actQty ?? "0"} />
                </div>
                <div className="space-y-2">
                  <Label>Actual Cost to Date</Label>
                  <Input key={editing?.id + "ac"} type="number" name="actCost" min="0" step="0.01" className="rounded-xl" defaultValue={editing?.actCost ?? "0"} />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createItem.isPending || updateItem.isPending}>
                {createItem.isPending || updateItem.isPending ? "Saving..." : editing ? "Save Changes" : "Add Item"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <KpiCard title="Contract Value (BOQ)" value={fc(totalContractAmt)} icon={ListChecks} />
        <KpiCard title="Actual Cost to Date" value={fc(totalActualCost)} icon={DollarSign} />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Section</TableHead>
              <TableHead>Item Description</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Actual Qty</TableHead>
              <TableHead className="text-right">Actual Cost</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No BOQ items. Add one above.</TableCell></TableRow>}
            {items.map(i => (
              <TableRow key={i.id} className="group">
                <TableCell className="text-xs text-muted-foreground">{i.sec || "—"}</TableCell>
                <TableCell>{i.itm}</TableCell>
                <TableCell>{i.unit || "—"}</TableCell>
                <TableCell className="text-right">{N(i.qty).toLocaleString()}</TableCell>
                <TableCell className="text-right">{fc(i.rate)}</TableCell>
                <TableCell className="text-right font-medium">{fc(N(i.qty) * N(i.rate))}</TableCell>
                <TableCell className="text-right">{N(i.actQty).toLocaleString()}</TableCell>
                <TableCell className="text-right">{fc(i.actCost)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(i)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                      onClick={async () => { if(await confirm({ title: 'Delete BOQ Item', message: 'Delete this BOQ item? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { await delItem.mutateAsync({ id: i.id }); queryClient.invalidateQueries({ queryKey: getListBoqQueryKey() }); }}}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RetentionTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const { data: clientInvoices = [] } = useListClientInvoices({ projectId: project.id });
  const { data: clientReceipts = [] } = useListClientReceipts({ projectId: project.id });
  const { data: scInvoices = [] } = useListScInvoices({ projectId: project.id });
  const { data: scPayments = [] } = useListScPayments({ projectId: project.id });
  const clientRetTotal = clientInvoices.reduce((s, i) => s + sumRetItems(i.retItems as RetItem[] | undefined, i.gross, i.retPct), 0);
  const clientRetReleased = clientReceipts.filter(r => r.tp === 'Retention Release').reduce((s, r) => s + N(r.amount), 0);
  const scRetTotal = scInvoices.reduce((s, i) => s + sumRetItems(i.retItems as RetItem[] | undefined, i.gross, i.retPct), 0);
  const scRetReleased = scPayments.filter(p => p.tp === 'Retention Release').reduce((s, p) => s + N(p.amount), 0);
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h2 className="text-2xl font-display font-bold">Retention Tracking</h2>
        <p className="text-muted-foreground text-sm mt-1">Client receivable retention and SC payable retention</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-600"/>Client Retention (Receivable)</h3>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">Total Retained</span><span className="font-bold text-amber-600">{fc(clientRetTotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">Released</span><span className="font-medium text-emerald-600">{fc(clientRetReleased)}</span></div>
            <div className="flex justify-between border-t border-border/50 pt-3"><span className="text-sm font-semibold">Outstanding</span><span className="font-bold text-lg">{fc(clientRetTotal - clientRetReleased)}</span></div>
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-violet-600"/>SC Retention (Payable)</h3>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">Total Withheld</span><span className="font-bold text-amber-600">{fc(scRetTotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">Released</span><span className="font-medium text-emerald-600">{fc(scRetReleased)}</span></div>
            <div className="flex justify-between border-t border-border/50 pt-3"><span className="text-sm font-semibold">Outstanding</span><span className="font-bold text-lg">{fc(scRetTotal - scRetReleased)}</span></div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-2xl border border-border/50 p-6">
        <h3 className="font-semibold mb-4">Client Invoice Retention Breakdown</h3>
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Ret %</TableHead>
              <TableHead className="text-right">Retention Held</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clientInvoices.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No client invoices.</TableCell></TableRow>}
            {clientInvoices.map(inv => (
              <TableRow key={inv.id}>
                <TableCell className="font-medium">{inv.id}</TableCell>
                <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                <TableCell className="text-right">{sumRetItems(inv.retItems as RetItem[] | undefined, 100, inv.retPct).toFixed(1)}%</TableCell>
                <TableCell className="text-right font-semibold text-amber-600">{fc(sumRetItems(inv.retItems as RetItem[] | undefined, inv.gross, inv.retPct))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PromissoryTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: notes = [] } = useListPromissoryNotes({ projectId: project.id });
  const createNote = useCreatePromissoryNote();
  const updateNote = useUpdatePromissoryNote();
  const delNote = useDeletePromissoryNote();
  const { data: customers = [] } = useListCustomers({ companyId: project.companyId });
  const { data: scList = [] } = useListSubcontractors({ projectId: project.id });
  const { data: suppList = [] } = useListSuppliers({ projectId: project.id });
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<PromissoryNote | null>(null);
  const [pnPartyType, setPnPartyType] = useState("Other");
  const [pnPartyId, setPnPartyId] = useState("");
  const [pnPartyName, setPnPartyName] = useState("");
  const [pnPopoverOpen, setPnPopoverOpen] = useState(false);
  const totalAmt = notes.reduce((s, n) => s + N(n.amount), 0);
  const pending = notes.filter(n => n.sts !== 'Settled').reduce((s, n) => s + N(n.amount), 0);
  const settled = notes.filter(n => n.sts === 'Settled').reduce((s, n) => s + N(n.amount), 0);

  const openNew  = () => {
    setEditing(null);
    setPnPartyType("Other");
    setPnPartyId("");
    setPnPartyName("");
    setIsOpen(true);
  };
  const openEdit = (n: PromissoryNote) => {
    setEditing(n);
    setPnPartyType(n.partyType || "Other");
    setPnPartyId(n.partyId || "");
    setPnPartyName(n.party || "");
    setIsOpen(true);
  };

  const handlePartyTypeChange = (val: string) => {
    setPnPartyType(val);
    setPnPartyId("");
    setPnPartyName("");
  };

  const handlePartySelect = (id: string, name: string) => {
    setPnPartyId(id);
    setPnPartyName(name);
  };

  const partyOptions: { id: string; name: string }[] = pnPartyType === "Client"
    ? customers.map((c: Customer) => ({ id: c.id, name: c.name }))
    : pnPartyType === "Subcontractor"
    ? scList.map((c: Subcontractor) => ({ id: c.id, name: c.name }))
    : pnPartyType === "Supplier"
    ? suppList.map((c: Supplier) => ({ id: c.id, name: c.name }))
    : [];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      noteNo: fd.get("noteNo") as string,
      tp: fd.get("tp") as string,
      dir: (fd.get("dir") as string) || "Received",
      partyType: pnPartyType,
      partyId: pnPartyId || null,
      party: pnPartyName,
      bank: fd.get("bank") as string,
      amount: fd.get("amount") as string,
      issDate: fd.get("issDate") as string,
      dueDate: fd.get("dueDate") as string,
      sts: (fd.get("sts") as string) || "Pending",
      nt: fd.get("nt") as string,
    };
    if (editing) {
      await updateNote.mutateAsync({ id: editing.id, data });
    } else {
      await createNote.mutateAsync({ data: { id: `PN-${uid()}`, projectId: project.id, companyId: project.companyId, ...data } });
    }
    queryClient.invalidateQueries({ queryKey: getListPromissoryNotesQueryKey() });
    setIsOpen(false);
    setEditing(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-bold">Promissory Notes</h2>
          <p className="text-muted-foreground text-sm mt-1">PDC and promissory note register</p>
        </div>
        <Button className="rounded-xl font-medium shadow-lg shadow-primary/20" onClick={openNew}>
          <Plus className="w-4 h-4 mr-2"/> Log Note
        </Button>
        <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="max-w-full sm:max-w-[540px] rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Note / PDC" : "Log Promissory Note / PDC"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note / Cheque No. *</Label>
                  <Input key={editing?.id + "nn"} name="noteNo" required placeholder="PN-001" className="rounded-xl" defaultValue={editing?.noteNo ?? ""} />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select name="tp" defaultValue={editing?.tp ?? "PDC"} key={editing?.id + "tp"}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PDC">PDC (Post-Dated Cheque)</SelectItem>
                      <SelectItem value="Promissory">Promissory Note</SelectItem>
                      <SelectItem value="Bank Guarantee">Bank Guarantee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Direction</Label>
                <Select name="dir" defaultValue={editing?.dir ?? "Received"} key={editing?.id + "dir"}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Received">Received (from client / third party)</SelectItem>
                    <SelectItem value="Issued">Issued (to supplier / subcontractor)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Party Type</Label>
                  <Select value={pnPartyType} onValueChange={handlePartyTypeChange}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Client">Client</SelectItem>
                      <SelectItem value="Subcontractor">Subcontractor</SelectItem>
                      <SelectItem value="Supplier">Supplier</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank</Label>
                  <Input key={editing?.id + "b"} name="bank" placeholder="e.g., HSBC" className="rounded-xl" defaultValue={editing?.bank ?? ""} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Party Name</Label>
                {pnPartyType !== "Other" ? (
                  <Popover open={pnPopoverOpen} onOpenChange={setPnPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" role="combobox" className="w-full rounded-xl justify-between font-normal h-10">
                        <span className={pnPartyId ? "text-foreground" : "text-muted-foreground"}>
                          {pnPartyId ? pnPartyName : `Select ${pnPartyType}…`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0 rounded-xl" align="start">
                      <Command>
                        <CommandInput placeholder={`Search ${pnPartyType.toLowerCase()}…`} />
                        <CommandList>
                          <CommandEmpty>No {pnPartyType.toLowerCase()} found.</CommandEmpty>
                          <CommandGroup>
                            {partyOptions.map(o => (
                              <CommandItem key={o.id} value={o.name} onSelect={() => {
                                handlePartySelect(o.id, o.name);
                                setPnPopoverOpen(false);
                              }}>
                                <Check className={`mr-2 h-4 w-4 ${pnPartyId === o.id ? "opacity-100" : "opacity-0"}`} />
                                {o.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Input value={pnPartyName} onChange={e => setPnPartyName(e.target.value)} placeholder="Enter party name" className="rounded-xl" />
                )}
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input key={editing?.id + "a"} type="number" name="amount" required min="0" step="0.01" className="rounded-xl" defaultValue={editing?.amount ?? ""} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issue Date</Label>
                  <Input key={editing?.id + "id"} type="date" name="issDate" className="rounded-xl" defaultValue={editing?.issDate ?? new Date().toISOString().slice(0,10)} />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input key={editing?.id + "dd"} type="date" name="dueDate" className="rounded-xl" defaultValue={editing?.dueDate ?? ""} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select name="sts" defaultValue={editing?.sts ?? "Pending"} key={editing?.id + "sts"}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Presented">Presented</SelectItem>
                      <SelectItem value="Settled">Settled</SelectItem>
                      <SelectItem value="Overdue">Overdue</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input key={editing?.id + "nt"} name="nt" placeholder="Optional remarks" className="rounded-xl" defaultValue={editing?.nt ?? ""} />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl mt-4" disabled={createNote.isPending || updateNote.isPending}>
                {createNote.isPending || updateNote.isPending ? "Saving..." : editing ? "Save Changes" : "Log Note"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <KpiCard title="Total Notes" value={fc(totalAmt)} icon={Landmark} />
        <KpiCard title="Pending" value={fc(pending)} icon={Wallet} trend="down" />
        <KpiCard title="Settled" value={fc(settled)} icon={Wallet} trend="up" />
      </div>
      <div className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead>Note No.</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>Party</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notes.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No promissory notes. Log one above.</TableCell></TableRow>}
            {notes.map((n: PromissoryNote) => (
              <TableRow key={n.id} className="group">
                <TableCell className="font-medium">{n.noteNo}</TableCell>
                <TableCell>{n.tp || "—"}</TableCell>
                <TableCell>
                  <Badge className={n.dir === 'Issued' ? 'bg-rose-100 text-rose-700 border-none' : 'bg-emerald-100 text-emerald-700 border-none'}>{n.dir || 'Received'}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{n.party || "—"}</span>
                    <Badge className={
                      n.partyType === 'Client' ? 'bg-blue-100 text-blue-700 border-none w-fit text-xs' :
                      n.partyType === 'Subcontractor' ? 'bg-violet-100 text-violet-700 border-none w-fit text-xs' :
                      n.partyType === 'Supplier' ? 'bg-amber-100 text-amber-700 border-none w-fit text-xs' :
                      'bg-gray-100 text-gray-600 border-none w-fit text-xs'
                    }>{n.partyType || 'Other'}</Badge>
                  </div>
                </TableCell>
                <TableCell>{n.bank || "—"}</TableCell>
                <TableCell className="text-right font-bold">{fc(n.amount)}</TableCell>
                <TableCell>{n.issDate || "—"}</TableCell>
                <TableCell>{n.dueDate || "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge className={n.sts === 'Settled' ? 'bg-emerald-100 text-emerald-700 border-none' : n.sts === 'Overdue' ? 'bg-rose-100 text-rose-700 border-none' : 'bg-amber-100 text-amber-700 border-none'}>{n.sts || 'Pending'}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(n)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                      onClick={async () => { if(await confirm({ title: 'Delete Note', message: 'Delete this promissory note? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) { await delNote.mutateAsync({ id: n.id }); queryClient.invalidateQueries({ queryKey: getListPromissoryNotesQueryKey() }); }}}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const REPORT_TABS = [
  { id: 'cfo',     label: 'CFO View' },
  { id: 'summary', label: 'Financial Summary' },
  { id: 'ar',      label: 'Accounts Receivable' },
  { id: 'ap',      label: 'Accounts Payable' },
  { id: 'cf',      label: 'Cash Flow Statement' },
  { id: 'pnotes',  label: 'Promissory Notes' },
];

function ReportsTab({ project }: { project: Project }) {
  const cur = useContext(CurContext); const fc = (v: Numeric) => fcBase(v, cur);
  const { taxLabel } = useContext(CompanyTaxContext);
  const [activeReport, setActiveReport] = useState('cfo');
  const { data: clientInvoices = [] } = useListClientInvoices({ projectId: project.id });
  const { data: clientReceipts = [] } = useListClientReceipts({ projectId: project.id });
  const { data: scInvoices = [] } = useListScInvoices({ projectId: project.id });
  const { data: scPayments = [] } = useListScPayments({ projectId: project.id });
  const { data: supplierInvoices = [] } = useListSupplierInvoices({ projectId: project.id });
  const { data: supplierPayments = [] } = useListSupplierPayments({ projectId: project.id });
  const { data: payroll = [] } = useListPayroll({ projectId: project.id });
  const { data: budget = [] } = useListBudget({ projectId: project.id });
  const { data: notes = [] } = useListPromissoryNotes({ projectId: project.id });

  const totInv = clientInvoices.reduce((s, i) => s + N(i.gross), 0);
  const { progressTotal: totProgressColl, advanceTotal: totAdvanceReceived, retentionReleaseTotal: totRetReleased } = separateAdvances(clientReceipts);
  const totColl = totProgressColl + totRetReleased;
  const totARBal = clientInvoices.reduce((s, i) => s + Math.max(0, calcCI(i, clientReceipts).bal), 0);
  const totRet = clientInvoices.reduce((s, i) => s + sumRetItems(i.retItems as RetItem[] | undefined, i.gross, i.retPct), 0);
  const totCN = clientInvoices.reduce((s, i) => s + sumCnItems(i.cnItems as CnItem[], i.cnAmt), 0);
  const totRetReceivable = totRet - totRetReleased;
  const totEarnedRevenue = totInv - totRet - totCN;
  const totSCClaim = scInvoices.reduce((s, i) => s + N(i.gross), 0);
  const totSCNet = scInvoices.reduce((s, i) => s + calcSCI(i, scPayments).net, 0);
  const totSCPaid = scPayments.filter(p => p.tp !== 'Advance').reduce((s, p) => s + N(p.amount), 0);
  const totSCAdvancePaid = scPayments.filter(p => p.tp === 'Advance').reduce((s, p) => s + N(p.amount), 0);
  const totSCAdvanceRecovered = calcAdvanceRecovery(scInvoices);
  const totSCBal = scInvoices.reduce((s, i) => s + Math.max(0, calcSCI(i, scPayments).bal), 0);
  const totAdvanceRecoveredClient = calcClientAdvanceRecovery(totAdvanceReceived, project.ctrVal, totInv);
  const totAdvanceOutstanding = Math.max(0, totAdvanceReceived - totAdvanceRecoveredClient);
  const totSupInv = supplierInvoices.reduce((s, i) => s + N(i.gross) * (1 + N(String(project.vatRate ?? 0))), 0);
  const totSupPaid = supplierPayments.reduce((s, p) => s + N(p.amount), 0);
  const totPayroll = payroll.reduce((s, p) => s + N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc), 0);
  const totBgt = budget.reduce((s, b) => s + N(b.appBgt), 0);
  const totActual = budget.reduce((s, b) => s + N(b.actl), 0);
  // CF statement: derived from real linked transactions only — no manual entries
  const unifiedCF = buildUnifiedCashFlow(clientReceipts, scPayments, supplierPayments, payroll, []);
  const totCFIn = unifiedCF.filter(r => r.type === 'Inflow').reduce((s, r) => s + r.amount, 0);
  const totCFOut = unifiedCF.filter(r => r.type === 'Outflow').reduce((s, r) => s + r.amount, 0);
  const grossMargin = N(String(project.ctrVal)) - totActual;
  const marginPct = N(String(project.ctrVal)) ? (grossMargin / N(String(project.ctrVal)) * 100).toFixed(1) : '0.0';

  // ── CFO View computed values ──────────────────────────────────────────────
  const now = Date.now();
  const overdueCI = clientInvoices.map(inv => {
    const { bal } = calcCI(inv, clientReceipts);
    const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
    return { ...inv, bal, days };
  }).filter(i => i.bal > 0);

  const ctrVal = N(String(project.ctrVal ?? 0));
  const billingRate  = ctrVal > 0 ? (totInv / ctrVal) * 100 : 0;
  const collectionRate = totInv > 0 ? (totProgressColl / totInv) * 100 : 0;
  const totAPBal = totSCBal + Math.max(0, totSupInv - totSupPaid);
  const budgetUtil = totBgt > 0 ? (totActual / totBgt) * 100 : 0;
  const totalCostsPaid = totSCPaid + totSupPaid + totPayroll;
  const cfoMarginPct = totInv > 0 ? ((totInv - (totSCClaim + totSupInv + totPayroll)) / totInv) * 100 : 0;
  const dso = (() => {
    const weighted = overdueCI.reduce((s, i) => s + i.bal * i.days, 0);
    const totalBal = overdueCI.reduce((s, i) => s + i.bal, 0);
    return totalBal > 0 ? Math.round(weighted / totalBal) : 0;
  })();
  const maxArDays = overdueCI.length > 0 ? Math.max(...overdueCI.map(i => i.days)) : 0;
  const netTaxPayable = (() => {
    const collected = clientInvoices.reduce((s, i) => s + N(i.gross) * N((i as Record<string, unknown>).vatRate as string ?? 0), 0);
    const paidSC   = scInvoices.reduce((s, i) => s + N(i.gross) * N((i as Record<string, unknown>).vatRate as string ?? 0), 0);
    const paidSupp = supplierInvoices.reduce((s, i) => s + N(i.gross) * N((i as Record<string, unknown>).vatRate as string ?? 0), 0);
    return collected - paidSC - paidSupp;
  })();

  const cfoTrendData = useMemo(() => {
    const today = new Date();
    const slots: Record<string, { month: string; invoiced: number; collected: number; costs: number }> = {};
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      slots[key] = { month: key, invoiced: 0, collected: 0, costs: 0 };
    }
    for (const inv of clientInvoices) {
      if (!inv.date) continue;
      const key = inv.date.slice(0, 7);
      if (slots[key]) slots[key].invoiced += N(inv.gross);
    }
    for (const r of clientReceipts) {
      if (!r.date || r.tp === 'Advance mobilisation' || r.tp === 'Advance') continue;
      const key = r.date.slice(0, 7);
      if (slots[key]) slots[key].collected += N(r.amount);
    }
    for (const p of [...scPayments, ...supplierPayments]) {
      if (!p.date) continue;
      const key = p.date.slice(0, 7);
      if (slots[key]) slots[key].costs += N(p.amount);
    }
    for (const p of payroll) {
      if (!p.date) continue;
      const key = p.date.slice(0, 7);
      if (slots[key]) {
        const net = N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc);
        slots[key].costs += net;
      }
    }
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return Object.values(slots).sort((a, b) => a.month.localeCompare(b.month)).map(m => {
      const [y, mo] = m.month.split("-");
      return { ...m, month: `${months[parseInt(mo) - 1]} ${y.slice(2)}` };
    });
  }, [clientInvoices, clientReceipts, scPayments, supplierPayments, payroll]);

  const Row = ({ label, value, bold, accent, indent }: { label: string; value: string; bold?: boolean; accent?: string; indent?: boolean }) => (
    <div className={`flex justify-between py-2 border-b border-border/30 ${indent ? 'pl-4' : ''}`}>
      <span className={`text-sm ${bold ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : ''}`} style={accent ? { color: accent } : {}}>{value}</span>
    </div>
  );
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-8">
      <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 pb-2 border-b-2 border-border/50">{title}</h3>
      {children}
    </div>
  );

  const overdueSCI = scInvoices.map(inv => {
    const { bal } = calcSCI(inv, scPayments);
    const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
    return { ...inv, bal, days };
  }).filter(i => i.bal > 0);

  const overdueSupp = supplierInvoices.map(inv => {
    const vatAmt = N(inv.gross) * N(String(project.vatRate ?? 0));
    const paid = supplierPayments.filter(p => p.invRef === inv.id).reduce((s, p) => s + N(p.amount), 0);
    const bal = N(inv.gross) + vatAmt - paid;
    const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
    return { ...inv, bal, vatAmt, days };
  }).filter(i => i.bal > 0);


  const printReport = () => window.print();

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-display font-bold">Financial Reports</h2>
          <p className="text-muted-foreground text-sm mt-1">{project.name} · {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        <Button variant="outline" onClick={printReport} className="rounded-xl no-print">
          <Printer className="w-4 h-4 mr-2" /> Print / Export
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border/50 no-print overflow-x-auto no-scrollbar">
        {REPORT_TABS.map(t => (
          <button key={t.id} onClick={() => setActiveReport(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap shrink-0 ${activeReport === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeReport === 'cfo' && (() => {
        const netCF = totCFIn - totCFOut;
        const billingPct = Math.min(100, billingRate);
        const collPct   = Math.min(100, collectionRate);
        const budgetPct = Math.min(100, budgetUtil);
        const totalDirectCosts = totSCClaim + totSupInv + totPayroll;
        const netProfit = totInv - totalDirectCosts;
        const retOutstanding = totRet - totRetReleased;
        const arAgingData = [
          { bucket: '0–30 d',  amt: overdueCI.filter(i => i.days <= 30).reduce((s,i)=>s+i.bal,0), fill:'#10b981' },
          { bucket: '31–60 d', amt: overdueCI.filter(i => i.days > 30 && i.days <= 60).reduce((s,i)=>s+i.bal,0), fill:'#f59e0b' },
          { bucket: '61–90 d', amt: overdueCI.filter(i => i.days > 60 && i.days <= 90).reduce((s,i)=>s+i.bal,0), fill:'#f97316' },
          { bucket: '90+ d',   amt: overdueCI.filter(i => i.days > 90).reduce((s,i)=>s+i.bal,0), fill:'#ef4444' },
        ];
        const costPieData = [
          { name: 'SC Claims',      value: totSCClaim, fill: '#3b82f6' },
          { name: 'Supplier Inv.',  value: totSupInv,  fill: '#8b5cf6' },
          { name: 'Payroll',        value: totPayroll, fill: '#f97316' },
        ].filter(d => d.value > 0);
        const costTotal = totSCClaim + totSupInv + totPayroll;
        const healthScore = (() => {
          let s = 100;
          if (cfoMarginPct < 10) s -= 30; else if (cfoMarginPct < 20) s -= 15;
          if (maxArDays > 90) s -= 25; else if (maxArDays > 60) s -= 15; else if (maxArDays > 30) s -= 5;
          if (budgetUtil > 100) s -= 25; else if (budgetUtil > 90) s -= 15; else if (budgetUtil > 80) s -= 5;
          if (netCF < 0) s -= 10;
          return Math.max(0, Math.min(100, s));
        })();
        const healthLabel = healthScore >= 80 ? 'Healthy' : healthScore >= 60 ? 'Watch' : 'At Risk';
        const healthColor = healthScore >= 80 ? 'text-emerald-600' : healthScore >= 60 ? 'text-amber-600' : 'text-rose-600';
        const healthBg    = healthScore >= 80 ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' : healthScore >= 60 ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800';
        const tickFmt = (v: number) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v/1_000).toFixed(0)}K` : String(v);
        const LedgerRow = ({ label, value, bold, color, indent }: { label: string; value: string; bold?: boolean; color?: string; indent?: boolean }) => (
          <div className={`flex justify-between py-1.5 border-b border-border/20 ${indent ? 'pl-4' : ''}`}>
            <span className={`text-sm ${bold ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            <span className={`text-sm font-mono ${bold ? 'font-bold' : ''} ${color ?? ''}`}>{value}</span>
          </div>
        );
        const riskAlerts = [
          maxArDays > 60   && { type:'rose',   title:'AR Overdue',      body:`Oldest receivable is ${maxArDays} days — exceeds 60-day threshold` },
          cfoMarginPct < 10 && { type:'amber', title:'Low Gross Margin', body:`Gross margin ${cfoMarginPct.toFixed(1)}% is below the 10% floor` },
          budgetUtil > 90   && { type:'amber', title:'Budget Critical',  body:`Budget utilisation at ${budgetUtil.toFixed(1)}% — nearing the limit` },
          netCF < 0         && { type:'rose',   title:'Negative Cash Flow', body:`Net outflows exceed inflows by ${fc(Math.abs(netCF))}` },
        ].filter(Boolean) as { type: string; title: string; body: string }[];
        return (
          <div className="space-y-6">
            {/* ── Executive Summary Band ─────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Contract Progress */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-200 mb-3">Contract Progress</p>
                <p className="text-3xl font-bold">{billingRate.toFixed(1)}%</p>
                <p className="text-sm text-blue-200 mt-0.5">Billed · {fc(totInv)} of {fc(ctrVal)}</p>
                <div className="mt-3 h-2 bg-blue-500/40 rounded-full overflow-hidden">
                  <div className="h-full bg-white/80 rounded-full transition-all" style={{ width: `${billingPct}%` }} />
                </div>
                <div className="mt-2 flex justify-between text-xs text-blue-200">
                  <span>Collected {collectionRate.toFixed(1)}%</span>
                  <span>Remaining {fc(Math.max(0, ctrVal - totInv))}</span>
                </div>
              </div>
              {/* Financial Health */}
              <div className={`rounded-2xl p-5 border-2 ${healthBg}`}>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Financial Health</p>
                <div className="flex items-end gap-3">
                  <p className={`text-5xl font-black ${healthColor}`}>{healthScore}</p>
                  <div className="mb-1">
                    <p className={`text-lg font-bold ${healthColor}`}>{healthLabel}</p>
                    <p className="text-xs text-muted-foreground">out of 100</p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-border/40 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${healthScore >= 80 ? 'bg-emerald-500' : healthScore >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${healthScore}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">Composite of margin, AR aging, budget & cash</p>
              </div>
              {/* Cash Position */}
              <div className={`rounded-2xl p-5 border ${netCF >= 0 ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800'}`}>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Net Cash Flow</p>
                <p className={`text-3xl font-bold ${netCF >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{netCF >= 0 ? '+' : ''}{fc(netCF)}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{netCF >= 0 ? 'Positive' : 'Negative'} position</p>
                <div className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cash In (collected)</span>
                    <span className="font-semibold text-emerald-600">{fc(totCFIn)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cash Out (paid)</span>
                    <span className="font-semibold text-rose-600">{fc(totCFOut)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 12 KPI Cards ──────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Billing Rate',      value: `${billingRate.toFixed(1)}%`,    sub: `${fc(totInv)} invoiced`,    pct: billingPct,  threshold: [75,50], higher: true },
                { label: 'Collection Rate',   value: `${collectionRate.toFixed(1)}%`, sub: `${fc(totProgressColl)} rcvd`, pct: collPct,   threshold: [80,60], higher: true },
                { label: 'Gross Margin',      value: `${cfoMarginPct.toFixed(1)}%`,   sub: fc(netProfit) + ' net',       pct: Math.min(100, cfoMarginPct * 2), threshold: [40,20], higher: true },
                { label: 'DSO',               value: `${dso}d`,                       sub: `${overdueCI.length} open inv`, pct: Math.min(100, dso / 1.2),     threshold: [25,50], higher: false },
                { label: 'Budget Util.',      value: `${budgetUtil.toFixed(1)}%`,     sub: `${fc(totActual)} actual`,    pct: budgetPct,   threshold: [40,80], higher: false },
                { label: 'Retention',         value: fc(retOutstanding),              sub: `${fc(totRet)} held`,         pct: 0,           threshold: [], higher: true },
              ].map(k => {
                const color = k.threshold.length === 2
                  ? (k.higher ? (k.pct >= k.threshold[0] ? 'text-emerald-600' : k.pct >= k.threshold[1] ? 'text-amber-600' : 'text-rose-600')
                               : (k.pct <= k.threshold[0] ? 'text-emerald-600' : k.pct <= k.threshold[1] ? 'text-amber-600' : 'text-rose-600'))
                  : 'text-foreground';
                const barColor = color === 'text-emerald-600' ? 'bg-emerald-500' : color === 'text-amber-600' ? 'bg-amber-500' : 'bg-rose-500';
                return (
                  <div key={k.label} className="bg-card rounded-xl border border-border/50 p-4 shadow-sm">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{k.label}</p>
                    <p className={`text-xl font-bold ${color}`}>{k.value}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{k.sub}</p>
                    {k.pct > 0 && (
                      <div className="mt-2 h-1 bg-border/40 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${k.pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Additional KPI Row ────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'AR Balance',       value: fc(totARBal),        sub: maxArDays > 0 ? `Oldest: ${maxArDays}d` : 'All clear',       color: totARBal > 0 ? 'text-rose-600' : 'text-emerald-600' },
                { label: 'AP Balance',       value: fc(totAPBal),        sub: `SC ${fc(totSCBal)} + Supp ${fc(Math.max(0,totSupInv-totSupPaid))}`, color: totAPBal > 0 ? 'text-amber-600' : 'text-emerald-600' },
                { label: 'Net Tax Payable',  value: fc(netTaxPayable),   sub: netTaxPayable > 0 ? 'Liability' : 'Credit position',          color: netTaxPayable > 0 ? 'text-amber-600' : 'text-emerald-600' },
                { label: 'Advance Recv.',    value: fc(totAdvanceReceived), sub: `Recovered: ${fc(totAdvanceRecoveredClient)}`,              color: 'text-foreground' },
              ].map(k => (
                <div key={k.label} className="bg-card rounded-xl border border-border/50 p-4 shadow-sm">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{k.label}</p>
                  <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{k.sub}</p>
                </div>
              ))}
            </div>

            {/* ── Charts Row ────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Trend Chart (3/5) */}
              <div className="lg:col-span-3 bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="text-sm font-semibold mb-0.5">6-Month Financial Trend</h3>
                <p className="text-xs text-muted-foreground mb-4">Invoiced, collected & costs paid per month</p>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={cfoTrendData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={tickFmt} />
                    <Tooltip formatter={(v: number, name: string) => [fc(v), name]} contentStyle={{ borderRadius: '10px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="invoiced"  name="Invoiced"     fill="#3b82f6" radius={[4,4,0,0]} />
                    <Bar dataKey="costs"     name="Costs Paid"   fill="#f97316" radius={[4,4,0,0]} />
                    <Line type="monotone" dataKey="collected" name="Collected" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {/* AR Aging (2/5) */}
              <div className="lg:col-span-2 bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="text-sm font-semibold mb-0.5">AR Aging Breakdown</h3>
                <p className="text-xs text-muted-foreground mb-4">Outstanding receivables by age bucket</p>
                {overdueCI.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-2">
                      <span className="text-emerald-600 text-xl font-bold">✓</span>
                    </div>
                    <p className="text-sm font-medium">All AR Cleared</p>
                    <p className="text-xs mt-1">No outstanding receivables</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={arAgingData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={tickFmt} />
                      <Tooltip formatter={(v: number) => [fc(v), 'Outstanding']} contentStyle={{ borderRadius: '10px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }} />
                      <Bar dataKey="amt" name="Outstanding" radius={[4,4,0,0]}>
                        {arAgingData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {arAgingData.map(b => b.amt > 0 && (
                    <div key={b.bucket} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: b.fill }} />
                      <span className="text-[10px] text-muted-foreground truncate">{b.bucket}: {fc(b.amt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Cost Breakdown + Ledger ───────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Cost Mix Donut (2/5) */}
              <div className="lg:col-span-2 bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="text-sm font-semibold mb-0.5">Cost Composition</h3>
                <p className="text-xs text-muted-foreground mb-2">Breakdown of all direct project costs</p>
                {costTotal === 0 ? (
                  <div className="flex items-center justify-center h-[160px] text-muted-foreground text-sm">No cost data</div>
                ) : (
                  <div className="flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={costPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                          {costPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => [fc(v), '']} contentStyle={{ borderRadius: '10px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="w-full space-y-2 mt-1">
                      {costPieData.map(d => (
                        <div key={d.name} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.fill }} />
                            <span className="text-muted-foreground">{d.name}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-semibold">{fc(d.value)}</span>
                            <span className="text-muted-foreground ml-1">({costTotal > 0 ? ((d.value/costTotal)*100).toFixed(0) : 0}%)</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-xs border-t border-border/40 pt-2 font-bold">
                        <span>Total</span><span>{fc(costTotal)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Financial Ledger (3/5, 2 cols inside) */}
              <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Revenue side */}
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 pb-2 border-b border-border/50">Revenue & Collections</h3>
                  <LedgerRow label="Contract Value"       value={fc(ctrVal)}             bold />
                  <LedgerRow label="Gross Invoiced"       value={fc(totInv)}             />
                  <LedgerRow label="Retention Held"       value={`(${fc(totRet)})`}      color="text-amber-600" />
                  <LedgerRow label="Credit Notes"         value={`(${fc(totCN)})`}       color="text-muted-foreground" />
                  <LedgerRow label="Earned Revenue"       value={fc(totEarnedRevenue)}   bold color="text-emerald-600" />
                  <LedgerRow label="Progress Collected"   value={fc(totProgressColl)}    />
                  <LedgerRow label="Advance Received"     value={fc(totAdvanceReceived)} indent />
                  <LedgerRow label="Retention Released"   value={fc(totRetReleased)}     indent />
                  <LedgerRow label="AR Outstanding"       value={fc(totARBal)}           color={totARBal > 0 ? 'text-rose-600' : 'text-emerald-600'} bold />
                  <LedgerRow label="Retention Receivable" value={fc(retOutstanding)}     color="text-amber-600" />
                </div>
                {/* Cost side */}
                <div className="bg-card rounded-2xl border border-border/50 p-5">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 pb-2 border-b border-border/50">Costs & Profitability</h3>
                  <LedgerRow label="SC Claims (Gross)"      value={fc(totSCClaim)}    />
                  <LedgerRow label="  SC Paid"              value={fc(totSCPaid)}     indent />
                  <LedgerRow label="  SC Outstanding"       value={fc(totSCBal)}      indent color={totSCBal > 0 ? 'text-rose-600' : ''} />
                  <LedgerRow label="Supplier Inv. (w/ Tax)" value={fc(totSupInv)}     />
                  <LedgerRow label="  Supplier Paid"        value={fc(totSupPaid)}    indent />
                  <LedgerRow label="  Supp. Outstanding"    value={fc(Math.max(0,totSupInv-totSupPaid))} indent color={totSupInv-totSupPaid > 0 ? 'text-rose-600' : ''} />
                  <LedgerRow label="Payroll Costs"          value={fc(totPayroll)}    />
                  <LedgerRow label="Total Direct Costs"     value={fc(totalDirectCosts)} bold />
                  <LedgerRow label="Gross Profit"           value={fc(netProfit)}     bold color={netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
                  <LedgerRow label="Margin %"               value={`${cfoMarginPct.toFixed(1)}%`} color={cfoMarginPct >= 20 ? 'text-emerald-600' : cfoMarginPct >= 10 ? 'text-amber-600' : 'text-rose-600'} />
                  <LedgerRow label="Budget Approved"        value={fc(totBgt)}        />
                  <LedgerRow label="Actual vs Budget"       value={`${budgetUtil.toFixed(1)}%`} color={budgetUtil > 95 ? 'text-rose-600' : budgetUtil > 80 ? 'text-amber-600' : 'text-emerald-600'} />
                  <LedgerRow label="Net Tax Payable"        value={fc(netTaxPayable)} color={netTaxPayable > 0 ? 'text-amber-600' : 'text-emerald-600'} />
                </div>
              </div>
            </div>

            {/* ── Overdue Tables ────────────────────────────────────── */}
            {(overdueCI.length > 0 || overdueSCI.length > 0 || overdueSupp.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {overdueCI.length > 0 && (
                  <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
                    <div className="px-5 py-3 border-b border-border/50 bg-rose-50/50 dark:bg-rose-950/20">
                      <h3 className="text-sm font-semibold text-rose-700 dark:text-rose-400">Overdue Receivables (AR)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-border/30">
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Invoice</th>
                          <th className="text-right px-4 py-2 font-medium text-muted-foreground">Balance</th>
                          <th className="text-right px-4 py-2 font-medium text-muted-foreground">Age</th>
                        </tr></thead>
                        <tbody>
                          {overdueCI.sort((a,b) => b.days - a.days).slice(0, 8).map(inv => (
                            <tr key={inv.id} className="border-b border-border/20 hover:bg-muted/30">
                              <td className="px-4 py-2 font-medium">{(inv as {ref?: string}).ref || inv.id.slice(0,8)}</td>
                              <td className={`px-4 py-2 text-right font-semibold ${inv.days > 60 ? 'text-rose-600' : 'text-amber-600'}`}>{fc(inv.bal)}</td>
                              <td className={`px-4 py-2 text-right font-bold ${inv.days > 90 ? 'text-rose-600' : inv.days > 60 ? 'text-orange-600' : 'text-amber-600'}`}>{inv.days}d</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {(overdueSCI.length > 0 || overdueSupp.length > 0) && (
                  <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
                    <div className="px-5 py-3 border-b border-border/50 bg-amber-50/50 dark:bg-amber-950/20">
                      <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">Outstanding Payables (AP)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-border/30">
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Party</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Type</th>
                          <th className="text-right px-4 py-2 font-medium text-muted-foreground">Balance</th>
                          <th className="text-right px-4 py-2 font-medium text-muted-foreground">Age</th>
                        </tr></thead>
                        <tbody>
                          {[...overdueSCI.map(i => ({ id: i.id, party: (i as {scName?: string}).scName || 'SC', type: 'SC', bal: i.bal, days: i.days })),
                            ...overdueSupp.map(i => ({ id: i.id, party: (i as {supplierName?: string}).supplierName || 'Supplier', type: 'Supplier', bal: i.bal, days: i.days }))]
                            .sort((a,b) => b.days - a.days).slice(0, 8).map(row => (
                            <tr key={row.id} className="border-b border-border/20 hover:bg-muted/30">
                              <td className="px-4 py-2 font-medium truncate max-w-[120px]">{row.party}</td>
                              <td className="px-4 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${row.type === 'SC' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400'}`}>{row.type}</span></td>
                              <td className="px-4 py-2 text-right font-semibold text-amber-600">{fc(row.bal)}</td>
                              <td className={`px-4 py-2 text-right font-bold ${row.days > 60 ? 'text-rose-600' : 'text-amber-600'}`}>{row.days}d</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Risk Alerts ───────────────────────────────────────── */}
            {riskAlerts.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/50 p-5">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 flex items-center justify-center text-xs font-bold shrink-0">!</span>
                  {riskAlerts.length} Risk Alert{riskAlerts.length > 1 ? 's' : ''} Detected
                </h3>
                <div className="space-y-2">
                  {riskAlerts.map((a, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${a.type === 'rose' ? 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800' : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'}`}>
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.type === 'rose' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                      <div>
                        <p className={`text-sm font-semibold ${a.type === 'rose' ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400'}`}>{a.title}</p>
                        <p className={`text-xs mt-0.5 ${a.type === 'rose' ? 'text-rose-600 dark:text-rose-500' : 'text-amber-600 dark:text-amber-500'}`}>{a.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {activeReport === 'summary' && (
        <div className="bg-card rounded-2xl border border-border/50 p-8 max-w-3xl">
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-border/50">
            <div>
              <h2 className="text-xl font-bold mb-1">{project.name}</h2>
              <p className="text-sm text-muted-foreground">Client: {project.client || '—'} · Owner: {project.owner || '—'} · Manager: {project.manager || '—'}</p>
              <p className="text-sm text-muted-foreground">Period: {project.stDt || '—'} to {project.endDt || '—'}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Report generated</p>
              <p className="text-sm font-medium">{new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>
          <Section title="Contract & Revenue">
            <Row label="Contract Value" value={fc(project.ctrVal)} bold />
            <Row label="Approved Budget" value={fc(project.budget)} />
            <Row label="Total Gross Invoiced" value={fc(totInv)} />
            <Row label="Less: Retention Held" value={`−${fc(totRet)}`} indent accent="#92400e" />
            {totCN > 0 && <Row label="Less: Credit Notes (incl. tax)" value={`−${fc(totCN)}`} indent accent="#991b1b" />}
            <Row label="Earned Revenue (Net Invoiced)" value={fc(totEarnedRevenue)} bold accent="#166534" />
          </Section>
          <Section title="Revenue Recognition">
            <Row label="Total Gross Invoiced" value={fc(totInv)} />
            <Row label="Less: Retention Held" value={`−${fc(totRet)}`} indent accent="#92400e" />
            {totCN > 0 && <Row label="Less: Credit Notes (incl. tax)" value={`−${fc(totCN)}`} indent accent="#991b1b" />}
            <Row label="Earned Revenue (Net Invoiced)" value={fc(totEarnedRevenue)} bold accent="#166534" />
            <Row label="Progress Billing Collected" value={fc(totProgressColl)} accent="#166534" />
            <Row label="Retention Released" value={fc(totRetReleased)} />
            <Row label="Advance Received" value={fc(totAdvanceReceived)} accent="#c2410c" />
            <Row label="  Less: Advance Recovered (proportional to billing)" value={`−${fc(totAdvanceRecoveredClient)}`} accent="#166534" indent />
            <Row label="Advance Outstanding (Deferred Liability)" value={fc(totAdvanceOutstanding)} accent="#c2410c" />
            <Row label="Outstanding AR Balance" value={fc(totARBal)} accent={totARBal > 0 ? '#991b1b' : '#166534'} />
            <Row label="Retention Receivable (Balance Sheet)" value={fc(totRetReceivable)} accent="#92400e" />
            <Row label="Net Cash Inflow from Operations" value={fc(totProgressColl + totRetReleased)} bold accent="#166534" />
          </Section>
          <Section title="Costs & Expenditure">
            <Row label="Total SC Claims (Gross)" value={fc(totSCClaim)} />
            <Row label="SC Net Payable (after retention/advance rec.)" value={fc(totSCNet)} indent />
            <Row label="SC Invoice Payments Made" value={fc(totSCPaid)} accent="#166534" indent />
            <Row label="SC Advance Paid (Ledger)" value={fc(totSCAdvancePaid)} accent="#c2410c" indent />
            <Row label="SC Advance Recovered via Invoices" value={fc(totSCAdvanceRecovered)} indent />
            <Row label="Outstanding SC Payable" value={fc(totSCBal)} accent={totSCBal > 0 ? '#991b1b' : '#166534'} indent />
            <Row label="Supplier Invoices (incl. VAT)" value={fc(totSupInv)} />
            <Row label="Supplier Payments Made" value={fc(totSupPaid)} accent="#166534" indent />
            <Row label="Payroll Costs" value={fc(totPayroll)} />
          </Section>
          <Section title="Budget vs Actual">
            <Row label="Approved Budget" value={fc(totBgt)} />
            <Row label="Actual Spend" value={fc(totActual)} accent={totActual > totBgt ? '#991b1b' : '#166534'} />
            <Row label="Budget Remaining" value={fc(totBgt - totActual)} accent={totBgt - totActual < 0 ? '#991b1b' : '#166534'} bold />
            <Row label="Gross Margin" value={`${fc(grossMargin)} (${marginPct}%)`} bold accent={grossMargin >= 0 ? '#166534' : '#991b1b'} />
          </Section>
          <Section title="Cash Flow Summary">
            <Row label="Total Cash Inflows (incl. Advances)" value={fc(totCFIn)} accent="#166534" />
            <Row label="  of which: Advance Received (Deferred)" value={fc(totAdvanceReceived)} indent accent="#c2410c" />
            <Row label="  of which: Progress + Retention Released" value={fc(totProgressColl + totRetReleased)} indent accent="#166534" />
            <Row label="Total Cash Outflows (incl. SC Advances)" value={fc(totCFOut)} accent="#991b1b" />
            <Row label="Net Cash Flow" value={fc(totCFIn - totCFOut)} bold accent={totCFIn - totCFOut >= 0 ? '#166534' : '#991b1b'} />
          </Section>
        </div>
      )}

      {activeReport === 'ar' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Gross Invoiced</p>
              <p className="text-2xl font-bold">{fc(totInv)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Less: Credit Notes</p>
              <p className="text-2xl font-bold text-rose-600">{totCN > 0 ? `−${fc(totCN)}` : fc(0)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Earned Revenue</p>
              <p className="text-2xl font-bold text-emerald-600">{fc(totEarnedRevenue)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Advance Received (Deferred)</p>
              <p className="text-2xl font-bold text-orange-600">{fc(totAdvanceReceived)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Outstanding Balance</p>
              <p className={`text-2xl font-bold ${totARBal > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fc(totARBal)}</p>
            </div>
          </div>
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Retention</TableHead>
                  <TableHead className="text-right">Credit Notes</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Age (days)</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientInvoices.length === 0 && <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground">No client invoices.</TableCell></TableRow>}
                {clientInvoices.map(inv => {
                  const { ret, net, coll, bal } = calcCI(inv, clientReceipts);
                  const cnAmt = sumCnItems(inv.cnItems as CnItem[], inv.cnAmt);
                  const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
                  const st = stBadge(bal, coll);
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.id}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{inv.invoiceType || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">{inv.desc}</TableCell>
                      <TableCell className="text-sm">{inv.date}</TableCell>
                      <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                      <TableCell className="text-right text-amber-600">{ret > 0 ? fc(ret) : '—'}</TableCell>
                      <TableCell className="text-right text-rose-600">{cnAmt > 0 ? `−${fc(cnAmt)}` : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{fc(net)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{fc(coll)}</TableCell>
                      <TableCell className={`text-right font-bold ${bal > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fc(Math.max(0, bal))}</TableCell>
                      <TableCell className={`text-center text-sm ${days > 90 ? 'text-rose-600 font-bold' : days > 60 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>{days}d</TableCell>
                      <TableCell className="text-center"><Badge style={{ background: st.bg, color: st.cl, border: 'none' }}>{st.lb}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {overdueCI.length > 0 && (
            <div className="bg-rose-50 dark:bg-rose-950/20 rounded-2xl border border-rose-200 dark:border-rose-800 p-5">
              <h4 className="text-sm font-semibold text-rose-700 dark:text-rose-400 mb-3">⚠ Overdue Receivables ({overdueCI.length})</h4>
              <div className="space-y-2">
                {overdueCI.sort((a, b) => b.days - a.days).map(inv => (
                  <div key={inv.id} className="flex justify-between text-sm">
                    <span className="text-rose-800 dark:text-rose-300">{inv.id} — {inv.desc?.slice(0, 40)}</span>
                    <span className="font-semibold text-rose-700 dark:text-rose-400">{fc(inv.bal)} · {inv.days}d</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeReport === 'ap' && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            <div className="p-4 border-b border-border/50 font-semibold text-sm">Subcontractor Payables</div>
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>SC Invoice</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Retention</TableHead>
                  <TableHead className="text-right">Credit Notes</TableHead>
                  <TableHead className="text-right">Net Payable</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Age</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scInvoices.length === 0 && <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">No SC invoices.</TableCell></TableRow>}
                {scInvoices.map(inv => {
                  const { ret, net, paid, bal } = calcSCI(inv, scPayments);
                  const scCnAmt = sumCnItems(inv.cnItems as CnItem[], inv.cnAmt);
                  const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
                  const st = stBadge(bal, paid);
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.id}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{inv.invoiceType || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[140px] truncate">{inv.desc}</TableCell>
                      <TableCell className="text-sm">{inv.date}</TableCell>
                      <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                      <TableCell className="text-right text-amber-600">{ret > 0 ? fc(ret) : '—'}</TableCell>
                      <TableCell className="text-right text-rose-600">{scCnAmt > 0 ? `−${fc(scCnAmt)}` : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{fc(net)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{fc(paid)}</TableCell>
                      <TableCell className={`text-right font-bold ${bal > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fc(Math.max(0, bal))}</TableCell>
                      <TableCell className={`text-center text-sm ${days > 45 ? 'text-rose-600 font-bold' : days > 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>{days}d</TableCell>
                      <TableCell className="text-center"><Badge style={{ background: st.bg, color: st.cl, border: 'none' }}>{st.lb}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            <div className="p-4 border-b border-border/50 font-semibold text-sm">Supplier Payables</div>
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Supplier Invoice</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">{taxLabel}</TableHead>
                  <TableHead className="text-right">Credit Notes</TableHead>
                  <TableHead className="text-right">Net Payable</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Age</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {supplierInvoices.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No supplier invoices.</TableCell></TableRow>}
                {supplierInvoices.map(inv => {
                  const siVat = N(inv.vatRate ?? 0);
                  const { va: siVa, net, paid, bal } = calcSI(inv, supplierPayments, siVat);
                  const siCnAmt = sumCnItems(inv.cnItems as CnItem[], inv.cnAmt);
                  const days = inv.date ? Math.floor((now - new Date(inv.date).getTime()) / 86400000) : 0;
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.id}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{inv.invoiceType || '—'}</TableCell>
                      <TableCell className="text-sm">{inv.suppName}</TableCell>
                      <TableCell className="text-sm">{inv.date}</TableCell>
                      <TableCell className="text-right">{fc(inv.gross)}</TableCell>
                      <TableCell className="text-right text-amber-600">{siVa > 0 ? fc(siVa) : '—'}</TableCell>
                      <TableCell className="text-right text-rose-600">{siCnAmt > 0 ? `−${fc(siCnAmt)}` : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{fc(net)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{fc(paid)}</TableCell>
                      <TableCell className={`text-right font-bold ${bal > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fc(Math.max(0, bal))}</TableCell>
                      <TableCell className={`text-center text-sm ${days > 45 ? 'text-rose-600 font-bold' : days > 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>{days}d</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeReport === 'cf' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Inflows</p>
              <p className="text-2xl font-bold text-emerald-600">{fc(totCFIn)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Outflows</p>
              <p className="text-2xl font-bold text-rose-600">{fc(totCFOut)}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Net Cash Flow</p>
              <p className={`text-2xl font-bold ${totCFIn - totCFOut >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fc(totCFIn - totCFOut)}</p>
            </div>
          </div>
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-right">Inflow</TableHead>
                  <TableHead className="text-right">Outflow</TableHead>
                  <TableHead className="text-right">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unifiedCF.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No cash flow entries.</TableCell></TableRow>}
                {unifiedCF.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">{e.date}</TableCell>
                    <TableCell>
                      <Badge style={{ background: e.type === 'Inflow' ? '#dcfce7' : '#fee2e2', color: e.type === 'Inflow' ? '#166534' : '#991b1b', border: 'none' }}>{e.source}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{e.category}</TableCell>
                    <TableCell className="text-sm">{e.description}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.ref}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">{e.type === 'Inflow' ? fc(e.amount) : '—'}</TableCell>
                    <TableCell className="text-right text-rose-600 font-medium">{e.type === 'Outflow' ? fc(e.amount) : '—'}</TableCell>
                    <TableCell className={`text-right font-bold ${e.running >= 0 ? 'text-foreground' : 'text-rose-600'}`}>{fc(e.running)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeReport === 'pnotes' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Notes</p>
              <p className="text-2xl font-bold">{notes.length}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Received (from Client)</p>
              <p className="text-2xl font-bold text-emerald-600">{fc(notes.filter(n => n.dir === 'Received').reduce((s, n) => s + N(n.amount), 0))}</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Issued (to Parties)</p>
              <p className="text-2xl font-bold text-amber-600">{fc(notes.filter(n => n.dir === 'Issued').reduce((s, n) => s + N(n.amount), 0))}</p>
            </div>
          </div>
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Note #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Party</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Days to Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No promissory notes.</TableCell></TableRow>}
                {notes.map((n: PromissoryNote) => {
                  const daysLeft = n.dueDate ? Math.ceil((new Date(n.dueDate).getTime() - now) / 86400000) : null;
                  const stsColors: Record<string, { bg: string; cl: string }> = {
                    Active: { bg: '#dbeafe', cl: '#1d4ed8' },
                    Paid: { bg: '#dcfce7', cl: '#166534' },
                    Settled: { bg: '#dcfce7', cl: '#166534' },
                    Matured: { bg: '#fef3c7', cl: '#92400e' },
                    Overdue: { bg: '#fee2e2', cl: '#991b1b' },
                    Dishonoured: { bg: '#fee2e2', cl: '#991b1b' },
                    Endorsed: { bg: '#f3f4f6', cl: '#6b7280' },
                  };
                  const stColor = stsColors[n.sts || 'Active'] || { bg: '#f3f4f6', cl: '#6b7280' };
                  return (
                    <TableRow key={n.id}>
                      <TableCell className="font-medium">{n.noteNo}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm">{n.tp || '—'}</span>
                          <Badge style={{ background: n.dir === 'Received' ? '#dcfce7' : '#fee2e2', color: n.dir === 'Received' ? '#166534' : '#991b1b', border: 'none', width: 'fit-content' }}>{n.dir || 'Received'}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">{n.party || '—'}</span>
                          <Badge className={
                            n.partyType === 'Client' ? 'bg-blue-100 text-blue-700 border-none w-fit text-xs' :
                            n.partyType === 'Subcontractor' ? 'bg-violet-100 text-violet-700 border-none w-fit text-xs' :
                            n.partyType === 'Supplier' ? 'bg-amber-100 text-amber-700 border-none w-fit text-xs' :
                            'bg-gray-100 text-gray-600 border-none w-fit text-xs'
                          }>{n.partyType || 'Other'}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{n.bank || '—'}</TableCell>
                      <TableCell className="text-right font-bold">{fc(n.amount)}</TableCell>
                      <TableCell className="text-sm">{n.issDate || '—'}</TableCell>
                      <TableCell className="text-sm">{n.dueDate || '—'}</TableCell>
                      <TableCell className="text-center"><Badge style={{ background: stColor.bg, color: stColor.cl, border: 'none' }}>{n.sts || 'Active'}</Badge></TableCell>
                      <TableCell className={`text-right text-sm font-medium ${daysLeft !== null && daysLeft < 30 ? 'text-rose-600 font-bold' : daysLeft !== null && daysLeft < 60 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {daysLeft !== null ? `${daysLeft}d` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
